/**
 * Typed browser client for the ServicesAI platform API — shared by portal and
 * console (doc 01 §1 packages/api-client). Hand-written against the v1
 * surface; SSE parsing included.
 */

export type Lang = 'en' | 'ar';
export interface Localized {
  en: string;
  ar: string;
}

/**
 * The identity providers a registered embed can open a popup against.
 *
 * `moei_sso` is the ministry's own eServices session, which a deployment on the
 * ministry estate should prefer: the popup is first-party to that site, so a
 * citizen already signed in there is returned without seeing a login page.
 * `uae_pass` is the fallback for an embed hosted anywhere else.
 */
export type EmbedSignInProvider = 'uae_pass' | 'moei_sso';

export type JourneyActionStatus =
  | 'pending'
  | 'completed'
  | 'processed'
  | 'cancelled'
  | 'expired'
  | 'failed';

export interface JourneyActionContext {
  kind: 'authentication' | 'payment';
  status: JourneyActionStatus;
  channel: 'webchat' | 'whatsapp';
  lang: Lang;
  expiresAt: string;
  /** Verified recently enough to reuse it for this request, not ever verified. */
  alreadyVerified: boolean;
  /** What the account already holds, so the mock knows what it has to collect. */
  identity: { hasEmiratesId: boolean };
  service: { slug: string | null; name: Localized } | null;
  /** The Ministry-issued reference, or null until MLS has issued one. */
  application: { reference: string | null; state: string | null } | null;
  payment: {
    amountFils: number;
    currency: string;
    lines: { label: Localized; amountFils: number }[];
    /**
     * Whether the sandbox provider will take this capture. The test-card panel
     * and the bypass button are shown on this alone — on a real gateway they
     * would offer a citizen a number to type and a way to skip paying.
     */
    sandbox?: boolean;
  } | null;
  /** Set only when live UAE Pass is configured; the portal redirects to it. */
  uaePassAuthorizeUrl: string | null;
  returnUrl: string;
  whatsappUrl: string;
}

export interface JourneyActionCompletion {
  ok: true;
  status: 'completed' | 'already_completed';
  returnUrl: string;
  whatsappUrl: string;
}

export interface ServiceGraphNode {
  id: string;
  kind: string;
  label: Localized;
  group?: string;
  detail?: Record<string, unknown>;
}

export interface ServiceGraphEdge {
  id: string;
  source: string;
  target: string;
  kind?: 'default' | 'conditional' | 'error' | 'async';
  label?: Localized;
}

export interface ServiceGraphProjection {
  slug: string;
  generatedAt: string;
  graphs: Record<'journey' | 'data' | 'integrations' | 'review', {
    nodes: ServiceGraphNode[];
    edges: ServiceGraphEdge[];
  }>;
  summary: {
    steps: number;
    fields: number;
    fieldGroups: number;
    documents: number;
    eligibilityRules: number;
    reviewStages: number;
    integrations: number;
    sensitiveFields: number;
  };
}

export interface SpecDiffEntry {
  path: string;
  kind: 'added' | 'removed' | 'changed';
  before?: unknown;
  after?: unknown;
}

export interface ServiceIntegrationHealth {
  slug: string;
  target: string;
  total: number;
  successful: number;
  successRate: number | null;
  states: { state: string; count: number; last_event_at: string | null }[];
}

// --- reply grammar (mirror of platform/src/engine/types.ts Widget) ----------

export type Widget =
  | {
      kind: 'choice';
      fieldId: string;
      /** `number` is the 1..N position shown beside the option, when numbered. */
      options: { value: string; label: string; number?: number; description?: string }[];
      /**
       * A heading, where the list is long enough to be drawn as a card. Absent
       * on an older platform, and on a short list drawn as chips rather than a
       * card, where the question above it is the heading.
       */
      title?: string;
    }
  /**
   * Several of the citizen's own records at once — three boats to renew.
   *
   * Answered one tap at a time even on a surface that could post a set, so both
   * channels use the same protocol and the engine holds the running selection
   * rather than the browser. `selected` is what has been chosen so far.
   */
  | {
      kind: 'multi_choice';
      fieldId: string;
      /** `group` heads a section, for a list too long to read as one run. */
      options: {
        value: string;
        label: string;
        number?: number;
        description?: string;
        group?: string;
      }[];
      selected: string[];
      doneValue: string;
      doneLabel: string;
      allValue?: string;
      allLabel?: string;
      /** A heading, where the list is long enough to be drawn as a card. */
      title?: string;
      /**
       * Present when the surface may hold the selection and post it in one go,
       * as `set:` followed by the chosen values joined by commas. Absent means
       * one tap per turn.
       */
      submitLabel?: string;
    }
  | {
      kind: 'boolean';
      fieldId?: string;
      action?: string;
      yesLabel: string;
      noLabel: string;
      /**
       * A file already on record that this yes/no is about — the document being
       * offered back for reuse, so the citizen can look at it before accepting.
       *
       * An id rather than a URL: the reply is persisted verbatim, so a
       * presigned URL here would be stored and would have expired by the time
       * the transcript was reloaded. Resolve it with `previewDocument`.
       *
       * Absent on an older platform, and absent on every boolean that is not
       * about a stored file.
       */
      preview?: { documentId: string; filename: string };
    }
  | { kind: 'prefill_card'; fieldId: string; label: string; value: string; yesLabel: string; noLabel: string }
  | {
      kind: 'text_prompt';
      fieldId: string;
      inputType: string;
      placeholder?: string;
      helpText?: string;
      min?: number;
      max?: number;
      /**
       * The latest day a `date` field accepts, `YYYY-MM-DD`.
       *
       * A hint like `min` and `max`: the server refuses a later date whatever
       * the control allows. It is sent so a picker need not offer a day that is
       * going to be refused a turn later, on a card the citizen has left.
       */
      maxDate?: string;
    }
  /**
   * A whole field group asked as one card, answered in one turn.
   *
   * Answered by posting `{ fields: { [fieldId]: value } }` to the messages
   * endpoint, not by the single-widget `{ widget }` payload — every value is
   * still validated and committed individually by the engine.
   */
  | {
      kind: 'field_group';
      groupId: string;
      title: string;
      hint?: string;
      fields: {
        fieldId: string;
        label: string;
        inputType: string;
        required: boolean;
        placeholder?: string;
        helpText?: string;
        min?: number;
        max?: number;
        /** The latest day a `date` field accepts, `YYYY-MM-DD`. See `text_prompt`. */
        maxDate?: string;
        options?: { value: string; label: string; number?: number }[];
        /**
         * Another box on this card holding the same name in the other script.
         *
         * Present on both halves of a pair. `script` is what this box holds, so
         * a surface filling the partner asks for the partner's own script.
         */
        transliteration?: { pairedWith: string; script: 'en' | 'ar' };
      }[];
      submitLabel: string;
    }
  | {
      kind: 'document_request';
      requirementId: string;
      name: string;
      acceptedKinds: string[];
      /**
       * The ministry accepts the application without this one. Absent on an
       * older platform, where the card said nothing either way.
       */
      optional?: boolean;
      attachedCount: number;
      minFiles: number;
      maxFiles: number;
    }
  | {
      kind: 'service_picker';
      services: {
        slug: string;
        name: string;
        shortName?: string;
        summary: string;
        maturity?: ServiceMaturity;
      }[];
    }
  | {
      kind: 'application_picker';
      title: string;
      applications: {
        reference: string;
        serviceName: string;
        serviceShortName?: string;
        state: string;
        stateLabel: string;
      }[];
    }
  | {
      kind: 'application_route';
      decisionId: string;
      current: { reference: string; serviceName: string; stateLabel: string };
      target: { serviceName: string; reference?: string };
      options: {
        value: 'replace' | 'finish_first' | 'save_switch' | 'cancel' | 'reselect';
        label: string;
        description: string;
      }[];
    }
  | { kind: 'eligibility_verdict'; verdict: 'eligible' | 'ineligible' | 'needs_review'; summary: string; reasons: string[] }
  | {
      kind: 'review_summary';
      reference: string;
      serviceSlug?: string;
      serviceName?: string;
      verified?: boolean;
      /**
       * `fieldId` is what makes a row correctable. Without it the summary is a
       * picture of the answers and the only way back to one is the two-step
       * "change something" picker; with it a row is the field, and tapping it
       * sends `__edit_field`. Optional because a row may display something that
       * is not a spec field.
       */
      fields: { label: string; value: string; fieldId?: string }[];
      sections?: {
        id: string;
        label: string;
        fields: { label: string; value: string; fieldId?: string }[];
      }[];
      documents: {
        requirementId: string;
        name: string;
        status: string;
        attachedCount: number;
        maxFiles: number;
      }[];
      fees: { label: string; amountFils: number }[];
      totalFils: number;
      /** The Ministry's undertaking, stated before the citizen commits. */
      decisionDays?: number;
    }
  | {
      kind: 'status_card';
      reference: string;
      /** Required to open the tracking page; a reference alone authorises nothing. */
      accessToken: string;
      /** Identifies the application to the shared status store and the refresh action. */
      applicationId?: string;
      serviceName?: string;
      /** Which service, so the card is drawn in its own livery rather than generically. */
      serviceSlug?: string;
      state: string;
      stateLabel: string;
      slaDueAt?: string;
      govReference?: string | null;
      governmentStatus?: string | null;
      /**
       * The Ministry's own remark, where it sent one. Separate from
       * `governmentStatus`, which is a label from a published list: the label
       * says which status, the note says what to do about it.
       */
      governmentNote?: string | null;
      /**
       * Filed, but the Ministry has not numbered it yet. Filing is
       * asynchronous, so at the moment this card is first drawn there is
       * normally no number to show, and an empty reference with nothing said
       * about it reads as a lost application.
       */
      filing?: 'queued' | 'failed';
      /** When the Ministry was last asked, so the card can say how old it is. */
      checkedAt?: string | null;
      /** Offer "check again". False where there is nothing to ask the Ministry. */
      refreshable?: boolean;
    }
  | {
      kind: 'payment_request';
      reference: string;
      lines: { label: string; amountFils: number }[];
      totalFils: number;
      testCardHint?: string;
      payLabel?: string;
      testCardLabel?: string;
      /**
       * The Ministry's own secure payment page. When present the fee is settled
       * at MOEI, so the card offers the link and takes no card number.
       */
      payUrl?: string;
    }
  | {
      kind: 'permit_card';
      reference: string;
      permitNumber: string;
      serviceName: string;
      validFrom: string;
      validUntil: string;
    }
  | {
      kind: 'handover_notice';
      /**
       * Control that brings the citizen back to the assistant without waiting
       * for anyone.
       *
       * Present only while the escalation is still unaccepted. Once a colleague
       * owns the thread the notice is a status line and nothing more: taking the
       * conversation back out from under someone who has started typing is worse
       * than the wait it would save.
       *
       * Absent on a replayed transcript recorded before this existed, which is
       * why it is optional rather than defaulted.
       */
      returnAction?: string;
      returnLabel?: string;
    }
  | {
      /**
       * The ministry holds no usable marine insurance for this citizen, and
       * clearing it means leaving the conversation. The insurers are MOEI's own
       * directory and no premium is ever quoted — insurance is a referral.
       */
      /**
       * How a named service opens: a picture, what it does, and what the
       * ministry already holds — settled before the first question.
       *
       * See `platform/src/engine/types.ts` for why `known` is never
       * speculative.
       */
      kind: 'service_opening';
      illustration: string;
      eyebrow: string;
      headline: string;
      body: string;
      facts: { icon: 'documents' | 'fee' | 'clock'; label: string }[];
      known: { label: string; value: string; state: 'confirmed' | 'absent' | 'checking' }[];
    }
  | {
      kind: 'insurance_required';
      reason: 'no_policy' | 'not_filed' | 'no_cover';
      headline: string;
      body: string;
      policyNumber?: string;
      providers: { name: string; emirate: string; url: string | null; phone: string | null }[];
      remaining: number;
      directoryUrl: string | null;
      recheckAction: string;
      recheckLabel: string;
      enterNumberAction?: string;
      enterNumberLabel?: string;
    }
  | {
      kind: 'channel_switch';
      target: 'whatsapp' | 'web';
      url: string;
      label: string;
    };

export interface EngineReply {
  text: string;
  agent?: string;
  widgets: Widget[];
}

/**
 * Where to look at one stored file, for as long as the link lasts.
 *
 * The URL is presigned and expires in minutes, so it is fetched when somebody
 * asks to see the document and never held. `mime` is what decides how to draw
 * it: an image can be shown in place, and anything else is a link.
 */
export interface DocumentView {
  url: string;
  filename: string;
  mime: string;
}

export interface OrchestrationActor {
  agent: string;
  note: string;
  status: 'coordinating' | 'working' | 'done';
}

/**
 * One ministry call the turn really made.
 *
 * Distinct from an actor above, and the distinction is load-bearing: an actor
 * is derived from the application's state and describes where the journey is,
 * while a step is the record that a call was made and answered. Render an actor
 * as "working on it"; render a step only as something that happened.
 *
 * Absent on a platform older than this field, so treat `steps ?? []`.
 */
export interface OrchestrationStep {
  /** Roster slug, so the same avatar the About page draws can be used. */
  agent: string;
  agentName: string;
  label: string;
  /** The authority that answered, when it is not MOEI's own register. */
  source: string | null;
  ok: boolean;
  durationMs: number;
}

export interface OrchestrationTrace {
  actors: OrchestrationActor[];
  collaboration: boolean;
  summary: string;
  steps?: OrchestrationStep[];
}

export interface CustomerAppContext {
  /** Ministry-issued reference only; null until one exists. */
  reference: string | null;
  serviceName: string;
  state: string;
  stateLabel: string;
  submittedAt: string | null;
  slaDueAt: string | null;
  decision: string | null;
}

export interface CustomerCrmNote {
  id: string;
  kind: 'comment' | 'call_note' | 'case_note' | 'follow_up';
  body: string;
  tags: string[];
  pinned: boolean;
  authorId: string;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerCrmTask {
  id: string;
  title: string;
  description: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  dueAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByName: string;
  assignedTo: string | null;
  assignedToName: string | null;
}

export interface CustomerCrmProfile {
  ownerId: string | null;
  ownerName: string | null;
  lifecycleStage: 'new' | 'active' | 'at_risk' | 'vip' | 'inactive';
  riskLevel: 'low' | 'standard' | 'elevated' | 'critical';
  tags: string[];
  nextFollowUpAt: string | null;
  updatedAt: string | null;
}

export interface CustomerDirectoryItem {
  id: string;
  name: string | null;
  phone: string | null;
  verificationTier: string;
  lang: Lang;
  applications: number;
  conversations: number;
  activeTickets: number;
  openTasks: number;
  openComplaints: number;
  notes: number;
  ownerName: string | null;
  lifecycleStage: CustomerCrmProfile['lifecycleStage'];
  riskLevel: CustomerCrmProfile['riskLevel'];
  tags: string[];
  nextFollowUpAt: string | null;
  lastActivityAt: string;
  satisfactionAverage: number | null;
  cxoneConversations: number;
  createdAt: string;
}

export interface CustomerCrmSummary {
  total: number;
  verified: number;
  activeTickets: number;
  openTasks: number;
  atRisk: number;
  vip: number;
  unassigned: number;
  followUpsDue: number;
  newLast30Days: number;
  conversationsLast30Days: number;
  satisfactionAverage: number | null;
  cxoneConversations: number;
}

export interface Customer360Record {
  id: string;
  name: { en?: string; ar?: string } | null;
  email: string | null;
  phone: string | null;
  emiratesId: string | null;
  preferredLang: Lang;
  verificationTier: string;
  createdAt: string;
  profile: Record<string, unknown>;
  identities: { id: string; kind: string; channel: string; proofed_at: string }[];
  documents: {
    id: string;
    kind: string;
    filename: string | null;
    status: string;
    extracted: Record<string, string>;
    confidence: number | null;
    issuedAt: string | null;
    expiresAt: string | null;
    createdAt: string;
  }[];
  consents: {
    id: string;
    scope: string;
    purpose: Localized;
    granted_via: string;
    granted_at: string;
    revoked_at: string | null;
  }[];
  applications: {
    reference: string;
    state: string;
    serviceSlug: string;
    serviceName: Localized;
    channel: string;
    lang: Lang;
    createdAt: string;
    updatedAt: string;
    submittedAt: string | null;
    closedAt: string | null;
    slaDueAt: string | null;
    govReference: string | null;
    decision: Record<string, unknown> | null;
    eventCount: number;
    assignment: {
      id: string;
      stage: string;
      status: string;
      dueAt: string | null;
      assigneeName: string | null;
    } | null;
  }[];
  conversations: {
    id: string;
    channel: string;
    lang: Lang;
    handoverState: string;
    applicationReference: string | null;
    messageCount: number;
    lastMessageAt: string | null;
    lastCitizenMessageAt: string | null;
    lastMessage: {
      direction: string;
      role: string;
      preview: string | null;
    } | null;
    cxone: {
      contactId: string | null;
      status: string;
      skillId: string | null;
      routingQueueName: string | null;
      assigneeName: string | null;
      queuePosition: number | null;
      handlingSurface: string | null;
      synchronizedAt: string | null;
    } | null;
    createdAt: string;
  }[];
  crm: CustomerCrmProfile;
  notes: CustomerCrmNote[];
  tasks: CustomerCrmTask[];
  complaints: {
    id: string;
    applicationReference: string | null;
    conversationId: string | null;
    serviceSlug: string | null;
    channel: string | null;
    body: string | null;
    category: string | null;
    severity: 'low' | 'medium' | 'high';
    status: 'open' | 'auto_resolved' | 'escalated' | 'closed';
    sentiment: string | null;
    responseDraft: Localized | null;
    autoReplied: boolean;
    createdAt: string;
    updatedAt: string;
  }[];
  satisfaction: {
    id: string;
    applicationReference: string | null;
    serviceSlug: string | null;
    channel: string | null;
    score: number;
    comment: string | null;
    createdAt: string;
  }[];
  payments: {
    id: string;
    applicationReference: string;
    serviceName: Localized;
    amountFils: number;
    currency: string;
    status: string;
    provider: string;
    cardLast4: string | null;
    cardBrand: string | null;
    providerReference: string | null;
    createdAt: string;
    capturedAt: string | null;
  }[];
  staffOptions: { id: string; displayName: string; roles: string[] }[];
  timeline: {
    at: string;
    type: string;
    channel?: string;
    detail: Record<string, unknown>;
  }[];
}

// --- agents -----------------------------------------------------------------

export interface AgentMetric {
  label: Localized;
  value: string;
  unit?: Localized;
}

export interface AgentDraftNoteRef {
  id: string;
  revision: number;
}

export interface AgentDraftContext {
  objective: string;
  rationale: string;
  expectedOutcome: string;
  constraints: string[];
  successCriteria: string[];
  ownerId: string | null;
  noteRefs: AgentDraftNoteRef[];
}

export interface RichTextMark {
  type: 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'link';
  attrs?: Record<string, string | number | boolean | null>;
}

export interface RichTextNode {
  type: 'paragraph' | 'heading' | 'bulletList' | 'orderedList' | 'listItem'
    | 'taskList' | 'taskItem' | 'blockquote' | 'codeBlock' | 'horizontalRule'
    | 'hardBreak' | 'text';
  attrs?: Record<string, string | number | boolean | null>;
  content?: RichTextNode[];
  text?: string;
  marks?: RichTextMark[];
}

export interface RichTextDocument {
  type: 'doc';
  content: RichTextNode[];
}

export interface AgentSummary {
  slug: string;
  name: Localized;
  title: Localized;
  blurb: Localized;
  responsibilities: Localized[];
  kind: 'journey' | 'back_office';
  initials: string;
  accent: string;
  runnable?: boolean;
  schedule?: string;
  enabled?: boolean;
  owner_id?: string | null;
  labels?: string[];
  current_config_version_id?: string | null;
  config_version?: number | null;
  prompt_hash?: string | null;
  integration_count?: number;
  schedule_count?: number;
  next_schedule_at?: string | null;
  pending_approvals?: number;
  lastRun?: {
    id: string;
    status: string;
    summary: Localized | null;
    metric: AgentMetric | null;
    started_at: string;
    finished_at: string | null;
  } | null;
}

export interface AgentIntegrationBindingConfig {
  id?: string;
  adapterKey: string;
  displayName: string;
  enabled: boolean;
  capabilities: string[];
  scopes: Record<string, unknown>;
  secretRef: string | null;
  config: Record<string, unknown>;
}

export interface AgentScheduleConfig {
  id?: string;
  name: string;
  purpose: 'agent_run' | 'notification' | 'digest' | 'escalation';
  enabled: boolean;
  cron: string;
  timezone: string;
  conditions: Record<string, unknown> & {
    lang?: 'en' | 'ar';
    reportAlert?: {
      mode: 'all' | 'any';
      rules: {
        bindingKey: string;
        operator: 'eq' | 'gt' | 'gte' | 'lt' | 'lte';
        threshold: number;
      }[];
    };
  };
  audience: { type: 'all' | 'role' | 'segment' | 'custom'; values: string[] };
  channel: 'email' | 'portal' | 'whatsapp_template' | 'whatsapp_session' | null;
  templateKey: string | null;
  quietHours: { enabled: boolean; start: string; end: string };
  cooldownMinutes: number;
  retryPolicy: { maxAttempts: number; backoffMinutes: number };
  reportDefinitionId?: string | null;
}

export interface AgentReportDataBinding {
  key: string;
  scope: 'tenant';
  source:
    | 'agent_runs'
    | 'applications'
    | 'complaints'
    | 'approvals'
    | 'schedules'
    | 'integrations'
    | 'request_events'
    | 'operations'
    | 'security_events'
    | 'governance_events';
  field: string;
  aggregation:
    | 'none'
    | 'count'
    | 'sum'
    | 'average'
    | 'minimum'
    | 'maximum'
    | 'latest'
    | 'p50'
    | 'p95'
    | 'p99';
  filters: {
    field: string;
    operator: 'eq' | 'neq' | 'in' | 'not_in' | 'gt' | 'gte' | 'lt' | 'lte' | 'exists';
    value?: string | number | boolean | null | (string | number | boolean)[];
  }[];
  windowMinutes?: number;
  limit: number;
}

export type AgentReportSection =
  | {
      id?: string;
      kind: 'metric';
      title?: Localized;
      bindingKey: string;
      label: Localized;
      format: 'number' | 'percentage' | 'currency' | 'duration' | 'text';
      comparisonBindingKey?: string;
    }
  | {
      id?: string;
      kind: 'narrative';
      title?: Localized;
      content: Localized;
      bindingKeys: string[];
    }
  | {
      id?: string;
      kind: 'table';
      title?: Localized;
      bindingKey: string;
      columns: {
        field: string;
        label: Localized;
        format: 'number' | 'percentage' | 'currency' | 'date' | 'datetime' | 'text';
      }[];
    }
  | {
      id?: string;
      kind: 'callout';
      title?: Localized;
      tone: 'info' | 'success' | 'warning' | 'critical';
      content: Localized;
      bindingKeys: string[];
    }
  | {
      id?: string;
      kind: 'cta';
      title?: Localized;
      label: Localized;
      href: string;
      bindingKeys: string[];
    };

export interface AgentReportDefinition {
  id?: string;
  key: string;
  identity: {
    title: Localized;
    description?: Localized;
  };
  goal: Localized;
  bindings: AgentReportDataBinding[];
  sections: AgentReportSection[];
  presentation: {
    layout: 'document' | 'dashboard' | 'brief';
    theme: 'default' | 'executive' | 'operational' | 'security' | 'governance' | 'critical';
    density: 'compact' | 'comfortable';
    showHeader: boolean;
    showGeneratedAt: boolean;
  };
  delivery: {
    enabled: boolean;
    channels: ('portal' | 'email')[];
    formats: ('html' | 'pdf' | 'csv')[];
    audience: { type: 'all' | 'role' | 'segment' | 'custom'; values: string[] };
  };
}

export interface AgentReportPreview {
  definition: AgentReportDefinition;
  dataMode: 'sample' | 'live_safe';
  generatedAt: string;
  resolved: Record<string, unknown>;
  content: {
    to: string;
    agentSlug: string;
    lang: Lang;
    subject: string;
    preheader: string;
    title: string;
    summary: string;
    metrics?: { label: string; value: string; detail?: string }[];
    sections?: { heading: string; body: string; badge?: string }[];
    cta?: { label: string; href: string };
    reference?: string;
    templateKey?: string;
    reportPresentation?: AgentReportDefinition['presentation'];
  };
  rendered: { subject: string; html: string; text: string };
  warnings: string[];
}

export interface AssuranceCommandCenter {
  slug: 'cyber-sentinel' | 'ai-governance' | 'tech-ops' | 'language-sentinel';
  generatedAt: string;
  latestRun: {
    summary: Localized;
    metric: AgentMetric | null;
    data: Record<string, unknown>;
    startedAt: string;
  } | null;
  reports: AgentReportPreview[];
  trend: { bucket: string; count: number }[];
  attention: {
    category: string;
    severity: string | null;
    status: string | null;
    channel: string | null;
    observed_at: string;
    count?: number;
  }[];
}

export type MetricsMeasurementStatus =
  | 'measured'
  | 'partial'
  | 'not_configured'
  | 'not_applicable';

export interface MetricsMeasurement {
  status: MetricsMeasurementStatus;
  sampleSize: number;
  reason: string | null;
}

export interface MetricsAggregateSection<T> {
  measurement: MetricsMeasurement;
  rows: T[];
}

export interface MetricsAssuranceScoreRow {
  agentSlug: AssuranceCommandCenter['slug'];
  metricKey: 'security_score' | 'governance_score' | 'availability' | 'detections_7d';
  metricValue: number | null;
  unit: 'score_out_of_100' | 'percent' | 'count';
  observedAt: string | null;
  measurementStatus: 'measured' | 'not_configured' | 'not_applicable';
}

export interface MetricsLatencyRow {
  channel: string;
  outcome: string;
  sampleCount: number;
  averageMs: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
}

export interface MetricsIncidentBucketRow {
  kind: string;
  severity: string;
  status: string;
  count: number;
}

export interface MetricsSecurityBucketRow {
  kind: string;
  severity: string;
  count: number;
}

export interface MetricsGovernanceBucketRow {
  kind: string;
  count: number;
}

export interface MetricsModerationBucketRow {
  action: string;
  language: string;
  count: number;
}

export interface MetricsIntegrationHealthRow {
  healthStatus: string;
  enabled: boolean;
  count: number;
  checkedCount: number;
  lastCheckedAt: string | null;
}

export interface MetricsReportDeliveryHealthRow {
  status: string;
  count: number;
  lastActivityAt: string | null;
}

export interface MetricsExport {
  contractVersion: '1.0';
  generatedAt: string;
  scope: 'tenant';
  sections: Record<string, unknown> & {
    assuranceScores: MetricsAggregateSection<MetricsAssuranceScoreRow>;
    requestLatency24h: MetricsAggregateSection<MetricsLatencyRow>;
    incidentBuckets30d: MetricsAggregateSection<MetricsIncidentBucketRow>;
    securityBuckets7d: MetricsAggregateSection<MetricsSecurityBucketRow>;
    governanceBuckets7d: MetricsAggregateSection<MetricsGovernanceBucketRow>;
    moderationBuckets7d: MetricsAggregateSection<MetricsModerationBucketRow>;
    integrationHealth: MetricsAggregateSection<MetricsIntegrationHealthRow>;
    reportDeliveryHealth30d: MetricsAggregateSection<MetricsReportDeliveryHealthRow> & {
      successRatePct: number | null;
      lastActivityAt: string | null;
    };
  };
}

export interface AgentReportTestIntent {
  intentId: string;
  token: string;
  maskedRecipient: string;
  expiresAt: string;
}

export interface AgentReportTestSendResult {
  intentId: string;
  provider: 'log' | 'ses';
  providerMessageId: string;
  acceptedAt: string;
}

export interface ComplaintsManagerPolicy {
  acceptedSources: ('webchat' | 'whatsapp' | 'voice' | 'survey' | 'staff')[];
  categories: string[];
  autoReplyKinds: ('complaint' | 'feedback' | 'suggestion' | 'praise')[];
  autoReplyCategories: string[];
  autoReplyMinConfidence: number;
  slaMinutes: { low: number; medium: number; high: number };
  escalationDestinations: ('staff_queue' | 'cxone')[];
  reportWindowDays: number;
}

export type ApiJsonPrimitive = string | number | boolean | null;
export type ApiJsonValue = ApiJsonPrimitive | ApiJsonValue[] | ApiJsonObject;
export interface ApiJsonObject {
  [key: string]: ApiJsonValue;
}

export type ComplaintKind = 'complaint' | 'feedback' | 'suggestion' | 'praise';
export type ComplaintSource = 'webchat' | 'whatsapp' | 'voice' | 'survey' | 'staff';
export type ComplaintCategory =
  | 'service_quality'
  | 'delay'
  | 'fees'
  | 'staff'
  | 'technical'
  | 'accessibility'
  | 'privacy'
  | 'safety'
  | 'other';
export type ComplaintSeverity = 'low' | 'medium' | 'high';
export type ComplaintStatus =
  | 'open'
  | 'triaging'
  | 'awaiting_review'
  | 'auto_resolved'
  | 'escalated'
  | 'closed';
export type ComplaintResponseStatus =
  | 'not_started'
  | 'drafted'
  | 'approved'
  | 'queued'
  | 'sending'
  | 'accepted'
  | 'sent'
  | 'failed'
  | 'not_required';
export type ComplaintRiskFlag = 'legal' | 'safety' | 'privacy' | 'fraud' | 'discrimination';

export interface ComplaintQueueItem {
  id: string;
  reference: string;
  customer_id: string | null;
  application_id: string | null;
  conversation_id: string | null;
  service_slug: string | null;
  channel: string | null;
  kind: ComplaintKind;
  source: ComplaintSource;
  source_key: string | null;
  category: ComplaintCategory | null;
  subcategory: string | null;
  severity: ComplaintSeverity;
  status: ComplaintStatus;
  sentiment: 'positive' | 'neutral' | 'negative' | null;
  urgency: 'normal' | 'urgent' | 'immediate' | null;
  risk_flags: ComplaintRiskFlag[];
  triage_summary: string | null;
  triage_confidence: number | string | null;
  assigned_to: string | null;
  assigned_at: string | null;
  sla_due_at: string | null;
  first_responded_at: string | null;
  resolved_at: string | null;
  response_status: ComplaintResponseStatus;
  response_channel: string | null;
  response_provider_id: string | null;
  escalation_ticket_id: string | null;
  auto_replied: boolean;
  revision: number;
  processing_started_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComplaintDetailItem extends ComplaintQueueItem {
  body: string | null;
  response_draft: Localized | null;
  handled_by_run: string | null;
}

export interface ComplaintEvent {
  id: string;
  event: string;
  actor_kind: 'staff' | 'engine' | 'system';
  actor_id: string | null;
  detail: ApiJsonObject;
  created_at: string;
}

export interface ComplaintListFilters {
  status?: ComplaintStatus;
  severity?: ComplaintSeverity;
  category?: ComplaintCategory;
  kind?: ComplaintKind;
  source?: ComplaintSource;
  assignedTo?: string;
  sla?: 'due_soon' | 'breached';
  limit?: number;
  offset?: number;
}

export interface ComplaintListResponse {
  items: ComplaintQueueItem[];
  limit: number;
  offset: number;
  total: number;
  summary: {
    total: number;
    active: number;
    breached: number;
    high: number;
    escalated: number;
  };
}

export interface ComplaintDetailResponse {
  complaint: ComplaintDetailItem;
  events: ComplaintEvent[];
}

export interface ComplaintMutationResponse {
  complaint: ComplaintQueueItem & {
    response_draft?: Localized | null;
  };
  queued?: boolean;
  deliveryBlocked?: boolean;
}

export type MysteryShopperChecklistDimension =
  | 'authentication'
  | 'findability'
  | 'navigation'
  | 'content_clarity'
  | 'form_usability'
  | 'validation'
  | 'accessibility'
  | 'mobile'
  | 'bilingual'
  | 'performance'
  | 'completion';

export interface MysteryShopperTargetConfig {
  id: string;
  name: string;
  serviceSlug: string | null;
  startUrl: string;
  loginUrl: string | null;
  environment: 'demo' | 'staging';
  allowedDomains: string[];
  allowedPaths: string[];
  authStrategy: 'none' | 'username_password' | 'preauthenticated_context';
  credentialRef: string | null;
  languages: ('en' | 'ar')[];
  viewports: { name: string; width: number; height: number }[];
  crawl: {
    maxPages: number;
    maxDepth: number;
    maxStepsPerPage: number;
    timeoutMs: number;
  };
  checklist: {
    id: string;
    label: Localized;
    dimension: MysteryShopperChecklistDimension;
    instruction: string;
    weight: number;
  }[];
  scenarios: {
    id: string;
    name: Localized;
    startPath: string;
    steps: string[];
    allowedActions: (
      | 'navigate'
      | 'fill_form'
      | 'upload_fixture'
      | 'sandbox_submit'
      | 'sandbox_payment'
    )[];
    fixture: Record<string, string>;
  }[];
  sandboxConfirmed: boolean;
}

export interface MysteryShopperConfig {
  targets: MysteryShopperTargetConfig[];
  defaultTargetId: string | null;
  maxConcurrentSessions: number;
  retentionDays: number;
}

export type MysteryShopJobStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'succeeded'
  | 'failed'
  | 'cancelled'
  | 'timed_out';

export interface MysteryShopTargetSummary {
  id: string;
  name: string;
  startUrl: string;
  environment: string;
  allowedDomains: string[];
  allowedPaths: string[];
  languages: string[];
  viewports: { name: string; width: number; height: number }[];
}

export interface MysteryShopDimensionScore {
  dimension: MysteryShopperChecklistDimension;
  score: number | null;
  earnedWeight: number;
  applicableWeight: number;
  configuredWeight: number;
  assessedItems: number;
  unassessedItems: number;
}

export interface MysteryShopChecklistScore {
  score: number | null;
  earnedWeight: number;
  applicableWeight: number;
  configuredWeight: number;
  assessedItems: number;
  unassessedItems: number;
  resultCounts: {
    pass: number;
    fail: number;
    warning: number;
    blocked: number;
    not_applicable: number;
  };
  dimensions: MysteryShopDimensionScore[];
  itemResults: { [checklistId: string]: MysteryShopFinding['result'] };
}

export interface MysteryShopBaselineComparison {
  scoreDelta: number | null;
  dimensions: {
    dimension: MysteryShopperChecklistDimension;
    currentScore: number | null;
    baselineScore: number | null;
    delta: number | null;
  }[];
  improvedChecklistIds: string[];
  regressedChecklistIds: string[];
  unchangedChecklistIds: string[];
}

export interface MysteryShopOutput {
  schemaVersion: 1;
  provider: 'fake' | 'browserbase';
  providerStatus: 'succeeded' | 'blocked' | 'timed_out';
  startedAt: string;
  finishedAt: string;
  summary: string;
  score: MysteryShopChecklistScore;
  counts: {
    pages: number;
    findings: number;
    good: number;
    bad: number;
    fixNext: number;
  };
  replay: {
    provider: 'fake' | 'browserbase';
    sessionId: string | null;
    available: boolean;
    pageCount: number;
  };
  baseline: {
    jobId: string;
    comparison: MysteryShopBaselineComparison;
  } | null;
}

export interface MysteryShopJob {
  id: string;
  targetId: string;
  targetName: string;
  target: MysteryShopTargetSummary;
  scenarioIds: string[];
  configVersionId: string | null;
  scheduleId: string | null;
  scheduleRunId: string | null;
  agentRunId: string | null;
  baselineJobId: string | null;
  trigger: 'manual' | 'scheduled' | 'event';
  triggeredBy: string | null;
  status: MysteryShopJobStatus;
  phase: string;
  attempt: number;
  maxAttempts: number;
  nextAttemptAt: string;
  heartbeatAt: string | null;
  cancelRequestedAt: string | null;
  pauseRequestedAt: string | null;
  pagesDiscovered: number;
  pagesVisited: number;
  checklistPassed: number;
  checklistTotal: number;
  score: number | null;
  output: MysteryShopOutput | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MysteryShopPage {
  id: string;
  pageId: string | null;
  url: string;
  normalizedUrl: string;
  title: string | null;
  depth: number;
  status: 'discovered' | 'visited' | 'skipped' | 'blocked' | 'failed';
  viewport: { name?: string; width?: number; height?: number };
  accessibilitySummary: {
    documentLanguage?: string | null;
    headingCount?: number;
    imageCount?: number;
    imagesMissingAlt?: number;
    formControlCount?: number;
    unlabeledFormControls?: number;
  };
  formsSummary: {
    action?: string | null;
    method?: string;
    inputCount?: number;
    requiredCount?: number;
    labels?: string[];
  }[];
  consoleErrors: string[];
  failedRequests: { url?: string; status?: number | null; type?: string }[];
  timings: {
    domContentLoadedMs?: number | null;
    loadMs?: number | null;
    responseMs?: number | null;
  };
  screenshotArtifactId: string | null;
  discoveredAt: string;
  visitedAt: string | null;
}

export interface MysteryShopFinding {
  id: string;
  pageId: string | null;
  checklistId: string | null;
  dimension: string;
  result: 'pass' | 'fail' | 'warning' | 'blocked' | 'not_applicable';
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  isPositive: boolean;
  title: string;
  detail: string;
  replayTimestampMs: number | null;
  screenshotArtifactId: string | null;
  elementLabel: string | null;
  elementLocator: string | null;
  boundingBox: { x?: number; y?: number; width?: number; height?: number } | null;
  evidenceHash: string | null;
  createdAt: string;
}

export interface MysteryShopEvent {
  id: string;
  kind:
    | 'phase'
    | 'action'
    | 'observation'
    | 'finding'
    | 'checklist'
    | 'blocked'
    | 'control'
    | 'error';
  phase: string;
  message: string;
  pageUrl: string | null;
  data: ApiJsonObject;
  occurredAt: string;
}

export interface MysteryShopArtifact {
  id: string;
  pageId: string | null;
  kind: 'screenshot' | 'dom_summary' | 'log_summary' | 'report';
  mime: string;
  sha256: string;
  byteSize: number;
  createdAt: string;
}

export interface MysteryShopDetail {
  job: MysteryShopJob;
  pages: MysteryShopPage[];
  findings: MysteryShopFinding[];
  events: MysteryShopEvent[];
  artifacts: MysteryShopArtifact[];
}

export interface MysteryShopLiveView {
  viewOnly: boolean;
  debuggerFullscreenUrl: string;
  debuggerUrl: string;
  wsUrl?: string;
  pages: {
    id: string;
    debuggerFullscreenUrl: string;
    debuggerUrl: string;
    title: string;
    url: string;
  }[];
}

export interface MysteryShopReplay {
  pageCount: number;
  pages: {
    pageId: string;
    startTimeMs: number;
    endTimeMs: number;
  }[];
}

export interface MysteryShopSessionLog {
  method: string;
  pageId: number;
  timestamp: number | null;
  requestUrl?: string;
  responseStatus?: number;
}

export interface MysteryShopEmailResult {
  provider: 'log' | 'ses';
  providerMessageId: string;
  acceptedAt: string;
}

export interface MysteryShopRunStatusEvent {
  status: MysteryShopJobStatus | 'deleted';
  phase?: string;
  updatedAt?: string;
}

export interface MysteryShopStreamEvent {
  id?: string;
  event: MysteryShopEvent['kind'] | 'status' | 'done';
  data: MysteryShopEvent | MysteryShopRunStatusEvent;
}

export interface AgentConfigSnapshot {
  enabled: boolean;
  profile: {
    title?: Localized;
    blurb?: Localized;
    responsibilities?: Localized[];
    owner?: string | null;
    labels: string[];
  };
  prompt: {
    guidance: Localized;
    systemInstructions: Localized[];
    responseStyle: {
      tone: 'formal' | 'professional' | 'warm' | 'concise' | 'detailed';
      minSentences: number;
      maxSentences: number;
    };
    variables: Record<string, string>;
  };
  runtime: { model: string; temperature: number; maxTokens: number; timeoutMs: number };
  guardrails: {
    requireGrounding: boolean;
    allowExternalLookup: boolean;
    redactPersonalData: boolean;
    humanApprovalForActions: boolean;
    blockedTopics: string[];
  };
  integrations: AgentIntegrationBindingConfig[];
  schedules: AgentScheduleConfig[];
  reports: AgentReportDefinition[];
  specialization?: {
    complaintsManager?: ComplaintsManagerPolicy;
    mysteryShopper?: MysteryShopperConfig;
  };
}

export interface AgentConfigVersion {
  id: string;
  profile_id: string;
  version: number;
  state: 'draft' | 'pending_approval' | 'published' | 'superseded' | 'rejected';
  config: AgentConfigSnapshot;
  draft_context: AgentDraftContext;
  prompt_hash: string;
  change_note: string;
  created_by: string;
  submitted_at: string | null;
  published_by: string | null;
  published_at: string | null;
  supersedes_id: string | null;
  lock_version: number;
  created_at: string;
  updated_at: string;
}

export interface AgentProfile {
  id: string;
  tenant_id: string;
  agent_slug: string;
  enabled: boolean;
  owner_id: string | null;
  labels: string[];
  current_config_version_id: string | null;
  lock_version: number;
  created_at: string;
  updated_at: string;
}

export interface AgentNote {
  id: string;
  profile_id: string;
  body: string;
  body_doc: RichTextDocument | null;
  body_format: 'plain' | 'rich_text_v1';
  format_version: number;
  labels: string[];
  revision: number;
  created_by: string;
  updated_by: string;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export type AgentNoteInput =
  | {
      body: string;
      bodyDoc?: null;
      bodyFormat?: 'plain';
      labels: string[];
    }
  | {
      body: string;
      bodyDoc: RichTextDocument;
      bodyFormat: 'rich_text_v1';
      labels: string[];
    };

export interface AgentResourceVerification {
  status: 'not_started' | 'pending' | 'passed' | 'failed';
  checks: {
    key: string;
    status: 'passed' | 'failed' | 'warning';
    message?: string;
    checkedAt?: string;
  }[];
  verifiedBy: string | null;
  verifiedAt: string | null;
  notes: string;
}

export interface AgentIntegrationResourceDraftPayload {
  name: string;
  desiredSystem?: string;
  purpose?: string;
  desiredCapabilities?: string[];
  dataNeeded?: string[];
  actionsNeeded?: string[];
  customConnectorDescription?: string;
  mapping?: AgentIntegrationBindingConfig;
}

export interface AgentAutomationResourceDraftPayload {
  name: string;
  trigger?: string;
  action?: string;
  outcome?: string;
  context?: string;
  mapping?: AgentScheduleConfig;
}

export interface AgentReportResourceDraftPayload {
  name: string;
  description?: string;
  goals?: string[];
  definition?: AgentReportDefinition;
}

interface AgentResourceDraftBase {
  id: string;
  tenantId: string;
  profileId: string;
  lifecycle: 'concept' | 'mapped' | 'verified' | 'promoted' | 'archived';
  revision: number;
  context: AgentDraftContext;
  verification: AgentResourceVerification;
  promotedConfigVersionId: string | null;
  promotedResourceId: string | null;
  promotedAt: string | null;
  promotedBy: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export type AgentResourceDraft = AgentResourceDraftBase & (
  | {
      kind: 'integration';
      payload: AgentIntegrationResourceDraftPayload;
    }
  | {
      kind: 'automation';
      payload: AgentAutomationResourceDraftPayload;
    }
  | {
      kind: 'report';
      payload: AgentReportResourceDraftPayload;
    }
);

export type AgentResourceDraftInput =
  | {
      kind: 'integration';
      context?: AgentDraftContext;
      payload: AgentIntegrationResourceDraftPayload;
    }
  | {
      kind: 'automation';
      context?: AgentDraftContext;
      payload: AgentAutomationResourceDraftPayload;
    }
  | {
      kind: 'report';
      context?: AgentDraftContext;
      payload: AgentReportResourceDraftPayload;
    };

export type AgentResourceDraftUpdateInput = AgentResourceDraftInput & {
  expectedRevision: number;
};

export interface AgentResourceDraftPromoteInput {
  expectedRevision: number;
  configVersionId?: string;
  expectedConfigLockVersion?: number;
  draftContext?: AgentDraftContext;
}

export type AgentAssistantTarget =
  | { kind: 'config_draft'; draftVersionId: string }
  | { kind: 'resource_draft'; resourceDraftId: string };

export interface AgentAssistantScope {
  paths: string[];
  noteRefs: AgentDraftNoteRef[];
}

export interface AgentAssistantSessionInput {
  target: AgentAssistantTarget;
  scope?: AgentAssistantScope;
}

export interface AgentAssistantSession {
  id: string;
  tenant_id: string;
  profile_id: string;
  target_kind: 'config_draft' | 'resource_draft';
  draft_version_id: string | null;
  resource_draft_id: string | null;
  scope: AgentAssistantScope;
  created_by: string;
  status: 'active' | 'closed';
  agent_slug: string;
  created_at: string;
  updated_at: string;
}

export interface AgentWorkspace {
  profile: AgentProfile;
  effectiveConfig: AgentConfigSnapshot;
  publishedVersion: AgentConfigVersion | null;
  versions: AgentConfigVersion[];
  notes: AgentNote[];
  resourceDrafts: AgentResourceDraft[];
  activity: Record<string, unknown>[];
  integrationHealth: {
    id: string;
    adapter_key: string;
    enabled: boolean;
    health_status: 'unknown' | 'healthy' | 'degraded' | 'failed';
    last_checked_at: string | null;
    last_error: string | null;
  }[];
  scheduleActivity: {
    id: string;
    schedule_id: string;
    name: string;
    purpose: string;
    due_at: string;
    status: string;
    attempt: number;
    output: Record<string, unknown> | null;
    error: string | null;
    started_at: string;
    finished_at: string | null;
  }[];
  approvals: Record<string, unknown>[];
  metrics: {
    runs: number;
    successRate: number | null;
    averageDurationMs: number | null;
    promptTokens: number;
    completionTokens: number;
  };
}

interface AgentAssistantProposalBase {
  id: string;
  sessionId: string;
  rationale: string;
  summary: string;
  patch: { op: 'add' | 'replace' | 'remove'; path: string; value?: unknown }[];
  impacts: string[];
  questions: string[];
  notes: { id: string; revision: number; influence: string }[];
  changes: { section: string; before: unknown; after: unknown }[];
  validation: {
    valid: boolean;
    issues: { path: string; message: string }[];
    warnings: { path: string; message: string }[];
    checklist: { path: string; complete: boolean; message: string }[];
  };
  model: string;
}

export type AgentAssistantProposal =
  | (AgentAssistantProposalBase & {
      target: { kind: 'config_draft'; draftVersionId: string };
      draftVersionId: string;
      resourceDraftId: null;
      preview: AgentConfigSnapshot;
    })
  | (AgentAssistantProposalBase & {
      target: {
        kind: 'resource_draft';
        resourceDraftId: string;
        resourceKind: AgentResourceDraft['kind'];
      };
      draftVersionId: null;
      resourceDraftId: string;
      preview: AgentResourceDraft;
    });

export interface AgentAssistantHistoryMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  note_revisions: { id: string; revision: number; sha256?: string; influence?: string }[];
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  created_at: string;
}

export type AgentAssistantSessionTarget =
  | {
      kind: 'config_draft';
      draftVersionId: string;
      document: { config: AgentConfigSnapshot; context: AgentDraftContext };
    }
  | {
      kind: 'resource_draft';
      resourceDraftId: string;
      resourceKind: AgentResourceDraft['kind'];
      document: AgentResourceDraft;
    };

export interface AgentAssistantSessionDetail {
  session: AgentAssistantSession;
  target: AgentAssistantSessionTarget;
  messages: AgentAssistantHistoryMessage[];
  proposals: Record<string, unknown>[];
  history: {
    messages: AgentAssistantHistoryMessage[];
    proposals: Record<string, unknown>[];
  };
}

export interface HousingAssistanceAmountView {
  assistanceType: 'grant' | 'loan' | 'not_eligible';
  maxAmountAed: number;
  monthlyIncomeAed: number | null;
  summary: Localized;
}

export interface HousingIcpChecklistItemView {
  id: string;
  label: Localized;
  ok: boolean;
  detail: Localized;
  value: string;
}

export interface HousingIcpEligibilityView {
  source: 'icp';
  items: HousingIcpChecklistItemView[];
  allOk: boolean;
  hardFail: boolean;
}

export interface ApplicationProjection {
  id: string;
  reference: string;
  serviceSlug: string;
  serviceName: Localized;
  state: string;
  lang: Lang;
  steps: { id: string; title: Localized; current: boolean; done: boolean }[];
  groups: {
    id: string;
    label: Localized;
    fields: {
      id: string;
      label: Localized;
      /**
       * Whose answer this is. `ministry` where a declared process step supplies
       * it, so a progress count must not treat an empty one as outstanding —
       * the citizen is not being asked for it. Absent on an older platform.
       */
      source?: 'citizen' | 'ministry';
      /**
       * Whether the conversation will ask for this, as the facts stand. A count
       * that includes an optional nobody will be asked for never reaches its
       * total. Absent on an older platform, where every field was counted.
       */
      required?: boolean;
      value: unknown;
    }[];
  }[];
  documents: {
    requirementId: string;
    name: Localized;
    status: 'pending' | 'attached' | 'skipped' | 'not_required';
    /**
     * The ministry accepts the application without this one. Absent on an
     * older platform, where an optional document was `pending` like any other
     * and a surface had no way to tell the citizen which papers it could do
     * without.
     */
    optional?: boolean;
    attachedCount: number;
    maxFiles: number;
    /** Why this document was asked for, when it is conditional. */
    because?: Localized | null;
    /** True when a ministry officer confirms the contents by hand. */
    humanVerify?: boolean;
  }[];
  eligibility: { verdict: string; summary?: Localized; reasons: Localized[] } | null;
  fees: { lines: { label: Localized; amountFils: number }[]; totalFils: number } | null;
  slaDueAt: string | null;
  submittedAt: string | null;
  decision: { outcome: string; reasons?: Localized[] } | null;
  govReference: string | null;
  governmentStatus: GovernmentStatus | null;
  /**
   * The ministry's own page for this application. `kind` is `application` once
   * the URL carries the ministry's number for the filing and `service` while it
   * only reaches the service — read it before choosing the wording, because
   * "view your application" pointing at a blank form is the worse mistake.
   * Absent on an older platform.
   */
  ministryPortal?: { url: string; kind: 'application' | 'service' } | null;
  housingAssistance?: HousingAssistanceAmountView | null;
  icpEligibility?: HousingIcpEligibilityView | null;
  housingCase?: {
    currentStageId: string;
    stages: Array<{
      id: string;
      label: Localized;
      status: 'pending' | 'in_progress' | 'completed';
      completedAt: string | null;
      notes: Localized | null;
    }>;
    research: {
      callCompleted?: boolean;
      visitCompleted?: boolean;
      deskCall?: boolean;
      socialVisit?: boolean;
      technicalVisit?: boolean;
      readyForCommittee?: boolean;
      socialStudyNotes: Localized | null;
      updatedAt: string;
    } | null;
    committee: {
      outcome: 'approved' | 'rejected';
      assistanceType: 'grant' | 'loan' | null;
      amountAed: number | null;
      reasons: Localized[];
      decidedAt: string;
    } | null;
    simulated: boolean;
    updatedAt: string;
    priority?: HousingPriorityView | null;
    /** The award the income bands imply; the committee still sets the figure. */
    suggestion?: HousingCommitteeSuggestion | null;
    reapplication?: {
      priorReference: string;
      priorOutcome: string;
    } | null;
  } | null;
  priorityScore?: number | null;
}

/** Committee starting point, replacing the hardcoded default the console used. */
export interface HousingCommitteeSuggestion {
  assistanceType: 'grant' | 'loan';
  amountAed: number;
  basis: Localized;
}

// --- public application status (status page) ---------------------------------

export type DocumentType = 'certificate' | 'tax_invoice' | 'payment_receipt' | 'decision_notice';

export interface StatusStep {
  id: string;
  title: Localized;
  description?: Localized;
  current: boolean;
  done: boolean;
}

export interface StatusFees {
  lines: { label: Localized; amountFils: number }[];
  totalFils: number;
}

export interface StatusPayment {
  amountFils: number;
  cardLast4: string | null;
  cardBrand: string | null;
  providerRef: string | null;
  capturedAt: string | null;
}

export interface StatusDocument {
  type: DocumentType;
  label: Localized;
  available: boolean;
}

export interface StatusTimelineEntry {
  event: string;
  state?: string;
  label?: Localized;
  /** Reviewer or ministry-facing explanation attached to a returned event. */
  note?: Localized;
  /**
   * What a `gov_status_changed` entry's ministry wording amounts to.
   *
   * `label` carries MOEI's own sentence, which is evidence and stays verbatim —
   * but "Application sent by customer" is a sentence a citizen reads as being
   * about something they did. A surface needs to say what it means without
   * paraphrasing it away, and it cannot derive that from the prose: the
   * published status lists live in `packages/spec` and the mapping that reads
   * them is server-side. So the platform sends the outcome it already computed
   * and the surface writes its own plain line above the ministry's words.
   *
   * Absent on every other event, and `unknown` for a status no published list
   * covers — in which case there is nothing honest to say and the ministry's
   * wording stands alone.
   */
  outcome?: MinistryStatusOutcome;
  at: string;
}

export interface GovernmentStatus {
  source: string;
  code: string | null;
  statusEn: string | null;
  statusAr: string | null;
  color: string | null;
  checkedAt: string;
  /**
   * A free-text remark the Ministry sent with the status, where it sent one.
   * `CheckApplicationStatus` delivers a label and no note, so usually absent.
   */
  note?: string | null;
  /** Whether the Ministry will still accept a change. Null where unpublished. */
  editable?: boolean | null;
}

/** How the Ministry's status maps onto something a surface can act on. */
export type MinistryStatusOutcome =
  | 'approved'
  | 'rejected'
  | 'deleted'
  | 'not_found'
  | 'needs_citizen_input'
  | 'payment_due'
  | 'payment_received'
  | 'in_progress'
  | 'unknown';

/**
 * An application tracked by the Ministry's number alone.
 *
 * Everything here came from MOEI. There is no platform application behind it —
 * no timeline, no documents, no fee, no decision — because a reference is
 * printed on correspondence and authorises none of that. The full case file is
 * `ApplicationStatus`, which needs the tracking link's token.
 */
export interface MinistryTrackedStatus {
  reference: string;
  outcome: MinistryStatusOutcome;
  /** What to show the citizen, in the Ministry's own words where it sent any. */
  statusLabel: Localized;
  /** Null when the Ministry holds no application under this number. */
  governmentStatus: GovernmentStatus | null;
}

/** Public-by-reference application status (doc 08 §2) — powers the status page. */
/**
 * One filed application, as a list or a card needs it.
 *
 * Deliberately lighter than `ApplicationStatus`: no timeline, no documents, no
 * step progress. Those are read for the one application a citizen has opened,
 * not for every row on their dashboard.
 */
export interface ApplicationStatusSummary {
  /**
   * The stable handle a client keys its cache on.
   *
   * Not the reference: the citizen-facing reference is the Ministry's, and it
   * does not exist until the Ministry issues one — which is exactly the window
   * a list most needs to be able to follow a row through.
   */
  id: string;
  /** Ministry-issued reference; null while the Ministry has not issued one. */
  reference: string | null;
  govReference: string | null;
  /** Authorises the detail read and the tracking link. */
  accessToken: string;
  serviceSlug: string;
  serviceName: Localized;
  serviceCategory: string;
  state: string;
  stateLabel: Localized;
  governmentStatus: GovernmentStatus | null;
  /** When the Ministry was last asked. Null when it never has been. */
  checkedAt: string | null;
  slaDueAt: string | null;
  submittedAt: string | null;
  /**
   * Last write to this application — a status landing, a payment, a ministry
   * check that changed something. Distinct from `checkedAt`, which is the last
   * time we *asked* and moves even when nothing did.
   */
  updatedAt: string;
  /**
   * Present only while a fee is genuinely outstanding.
   *
   * `amountFils` is zero where the Ministry prices the service itself and has
   * not stated the figure. Zero means "shown on the Ministry's page", never
   * "free"; render the link, not the number.
   */
  payment: { amountFils: number; link: string | null } | null;
  /** Money has reached the Ministry. Distinct from having no fee to pay. */
  paid: boolean;
  /**
   * Whether the Ministry can be asked about this one at all. A refresh control
   * offered where this is false can only ever do nothing.
   */
  checkable: boolean;
  /** Filed, but the Ministry has not issued its number yet. */
  filing: boolean;
}

/**
 * What MOEI itself publishes about the service an application is filed under.
 *
 * Only the ministry's own identifiers: the SmartServices card ids and the public
 * service page. Nothing about how the filing reaches MOEI, because none of that
 * is a fact a citizen can use at a counter.
 */
export interface MinistryServiceIdentifiers {
  /**
   * The SmartServices card id, which differs by language: MOEI numbers its
   * English and Arabic cards separately, so the right one to show depends on
   * what the citizen is reading.
   */
  smartServicesIds: { en: string; ar: string } | null;
  /** The bilingual moei.gov.ae service page. */
  portalUrl: Localized | null;
}

export interface ApplicationStatus {
  /** Ministry-issued reference; null while the Ministry has not issued one. */
  reference: string | null;
  /**
   * The handle a signed-in client addresses this application by.
   *
   * The one identifier that exists before the Ministry issues a number, which
   * is exactly the window a citizen most wants to watch. A uuid, so it is safe
   * in a URL where the platform's sequential locator is not.
   */
  applicationId: string;
  govReference: string | null;
  governmentStatus: GovernmentStatus | null;
  ministry: MinistryServiceIdentifiers;
  serviceName: Localized;
  state: string;
  stateLabel: Localized;
  slaDueAt: string | null;
  submittedAt: string | null;
  createdAt: string | null;
  holderName: string | null;
  permitNumber: string | null;
  issuedAt: string | null;
  validFrom: string | null;
  validUntil: string | null;
  fees: StatusFees | null;
  /** A receipt: what was captured, on which card, when. Null when nothing was. */
  payment: StatusPayment | null;
  /**
   * The fee still owed, and the Ministry's own page for it.
   *
   * Separate from `payment`, which is a receipt and therefore says nothing at
   * all while a fee is outstanding. `link` may be null: MLS creates the payment
   * record after filing and issues the checkout only once it advances the
   * application to Ready for payment.
   */
  outstandingPayment: { amountFils: number; link: string | null } | null;
  decision: { outcome: string; reasons?: Localized[] } | null;
  steps: StatusStep[];
  documents: StatusDocument[];
  timeline: StatusTimelineEntry[];
}

export type AiMaturity =
  | 'preparation_only'
  | 'form_audited'
  | 'transactional';

export type MlsMaturity =
  | 'not_applicable'
  | 'candidate'
  | 'matched'
  | 'live_ready';

export interface ServiceMaturity {
  ai: AiMaturity;
  execution: 'informational_handover' | 'transactional';
  mls: MlsMaturity;
  capabilities: {
    application: boolean;
    documents: boolean;
    fees: boolean;
    eligibility: boolean;
    sync: boolean;
  };
}

export interface CatalogEntry {
  slug: string;
  name: Localized;
  summary: Localized;
  category: string;
  categoryName: Localized;
  channels: { webchat: boolean; whatsapp: boolean; voice: boolean };
  feesFromFils: number | null;
  decisionDays: number | null;
  documentsCount: number;
  /** Who the service is for; drives the personalized "your services" view. */
  audience: ('citizen' | 'resident' | 'business' | 'government')[];
  /** true → guided AI chat flow; false → catalog entry migrated from moei.gov.ae. */
  aiEnabled: boolean;
  /** Localized official MOEI URL used when the platform cannot execute the application. */
  sourceUrl: Localized | null;
  /** Evidence and executable capability derived from the pinned spec. */
  maturity: ServiceMaturity | null;
}

/**
 * One service a typed sentence was understood to be asking for.
 *
 * Carries the slug and nothing describing the service: the caller already holds
 * the catalogue, and a second copy of a name here is a second copy that can
 * disagree with the first.
 */
export interface ServiceSearchMatch {
  slug: string;
  /** 0–1. Below roughly 0.6 these are candidates to choose between, not an answer. */
  confidence: number;
  /** One clause on why it fits, in the requested language, or null if withheld. */
  reason: string | null;
}

// ---------------------------------------------------------------------------
// Service bundles
// ---------------------------------------------------------------------------
//
// Hand-written against `packages/spec/src/bundleSpec.ts` rather than imported
// from it, because this package deliberately depends on nothing: it is the wire
// contract, and a wire contract that shares a type with the server stops being
// able to describe a server of a different vintage. Same reason `CatalogEntry`
// above is a copy of the projection rather than the projection itself.

export type BundleTheme = 'harbour' | 'majlis' | 'ledger' | 'dune';
export type BundleStatus = 'draft' | 'published' | 'retired';

export interface BundleImage {
  src: string;
  alt: Localized;
  responsive: boolean;
}

export interface BundleStat {
  id: string;
  value: string;
  label: Localized;
  caption?: Localized;
  /** Where the figure came from. Required by the schema; a claim without one is refused. */
  source: string;
}

export interface BundleReduction {
  id: string;
  label: Localized;
  from: number;
  to: number;
  unit: Localized;
  source: string;
}

export interface BundleBenefit {
  id: string;
  title: Localized;
  body: Localized;
  icon: string;
}

export interface BundlePartner {
  id: string;
  name: Localized;
  logo?: BundleImage;
  url?: string;
}

export interface BundleFaq {
  id: string;
  question: Localized;
  answer: Localized;
}

export interface BundleCta {
  label: Localized;
  href: string;
  authenticatedHref?: string;
  note?: Localized;
}

export type BundleSectionKind =
  | 'hero'
  | 'signature'
  | 'stats'
  | 'reduction'
  | 'story'
  | 'quay'
  | 'benefits'
  | 'workspace'
  | 'partners'
  | 'faq'
  | 'cta';

export interface BundleSection {
  id: string;
  kind: BundleSectionKind;
  variant: string;
  visible: boolean;
  eyebrow?: Localized;
  title?: Localized;
  body?: Localized;
  image?: BundleImage;
  cta?: BundleCta;
  options: Record<string, unknown>;
}

export interface BundleSpecLane {
  id: string;
  title: Localized;
  blurb?: Localized;
  services: string[];
  image?: BundleImage;
}

export interface BundleSpec {
  slug: string;
  name: Localized;
  tagline: Localized;
  summary: Localized;
  theme: BundleTheme;
  workspace: string | null;
  category?: string;
  hero: {
    eyebrow?: Localized;
    headline: Localized;
    body?: Localized;
    image?: BundleImage;
    cta?: BundleCta;
    secondaryCta?: BundleCta;
  };
  stats: BundleStat[];
  reductions: BundleReduction[];
  lanes: BundleSpecLane[];
  benefits: BundleBenefit[];
  partners: BundlePartner[];
  faq: BundleFaq[];
  sections: BundleSection[];
  card?: BundleImage;
  internalNote?: string;
}

/** A member service, already resolved against the published catalogue. */
export interface BundleServiceEntry {
  slug: string;
  name: Localized;
  summary: Localized;
  category: string;
  aiEnabled: boolean;
  /** true → the journey is proven end to end against the ministry. */
  liveReady: boolean;
  feesFromFils: number | null;
  decisionDays: number | null;
  documentsCount: number;
  sourceUrl: Localized | null;
}

export interface BundleLaneView {
  id: string;
  title: Localized;
  blurb: Localized | null;
  image: BundleImage | null;
  services: BundleServiceEntry[];
}

export interface PublicBundle {
  slug: string;
  status: BundleStatus;
  spec: BundleSpec;
  lanes: BundleLaneView[];
  services: BundleServiceEntry[];
  serviceCount: number;
  liveServiceCount: number;
  /** Slugs the spec names that the catalogue could not resolve. */
  unresolvedServices: string[];
  /** true when this is the draft, fetched through a console preview link. */
  preview?: boolean;
}

export interface BundleSummary {
  slug: string;
  name: Localized;
  status: BundleStatus;
  theme: string;
  sortOrder: number;
  serviceCount: number;
  sectionCount: number;
  hasDraftChanges: boolean;
  publishedAt: string | null;
  updatedAt: string;
}

export interface BundleMedia {
  id: string;
  bundleId: string;
  kind: string;
  contentType: string;
  byteSize: number;
  width: number | null;
  height: number | null;
  alt: Localized;
  createdAt: string;
}

export interface AdminBundleDetail {
  id: string;
  slug: string;
  status: BundleStatus;
  sortOrder: number;
  draftSpec: BundleSpec;
  publishedSpec: BundleSpec | null;
  media: BundleMedia[];
  unresolvedServices: string[];
  publishedAt: string | null;
  updatedAt: string;
}

/**
 * Why a service was surfaced, as a name rather than a number.
 *
 * `relevance` below already encodes this, but grouping a catalogue into
 * "because you own a boat" and "you have used these before" by testing
 * `relevance === 80` would make the platform's scoring scale a wire contract
 * and break the grouping silently the day it is retuned.
 */
export type ApplicabilityBasis =
  | 'in_progress'
  | 'ministry'
  | 'asset'
  | 'history'
  | 'audience'
  | 'unknown';

/** One catalog entry narrowed to a citizen, with why it was surfaced. */
export type ApplicableCatalogEntry = CatalogEntry & {
  reason: Localized | null;
  /** Higher is more relevant to this citizen. Absent on older platforms. */
  relevance?: number;
  basis?: ApplicabilityBasis;
  /** The asset the service acts on, when `basis` is `asset`. */
  asset?: string | null;
};

/**
 * How much of each thing the Ministry has on record for this citizen.
 *
 * A null is "we know they hold one, not how many" rather than none: only the
 * marine reads list what somebody owns, so a number for a vehicle or a permit
 * would be invented. Absent on older platforms.
 */
export interface CustomerHoldings {
  boats: number | null;
  licences: number | null;
  establishment: string | null;
  housingCaseNo: string | null;
}

export interface PublicServiceDocument {
  id: string;
  name: Localized;
  /** The government already holds this; the citizen is not asked to upload it. */
  electronicallyLinked: boolean;
  optional: boolean;
  /** Only asked for on some applications, depending on earlier answers. */
  conditional: boolean;
  acceptedKinds: string[];
  maxFiles: number | null;
}

export interface PublicServiceFeeLine {
  id: string;
  label: Localized;
  /** null when the amount is computed from the answers rather than fixed. */
  amountFils: number | null;
  variable: boolean;
  minimumFils: number | null;
  maximumFils: number | null;
  /** Charged only on some applications. */
  conditional: boolean;
}

/**
 * One published service, as a citizen may see it — the response of
 * `GET /v1/registry/services/{slug}`.
 *
 * Not the ServiceSpec, despite what the endpoint's summary used to say. The
 * spec carries the concierge persona, HITL thresholds, the sync target and the
 * audited source control behind every field; none of that crosses this
 * boundary. What is here is what the service card page draws.
 *
 * Catalog-only services (imported from moei.gov.ae, no spec published yet)
 * answer with the same keys and empty collections, so one renderer draws both.
 */
export interface PublicServiceDetail {
  slug: string;
  name: Localized;
  shortName: Localized | null;
  summary: Localized;
  description: Localized;
  category: string;
  categoryName: Localized;
  keywords: { en: string[]; ar: string[] };
  audience: ('citizen' | 'resident' | 'business' | 'government')[];
  channels: { webchat: boolean; whatsapp: boolean; voice: boolean };
  steps: { id: string; title: Localized; description?: Localized }[];
  /** Field groups as a citizen sees them: what we ask about, and how much. */
  questions: {
    id: string;
    label: Localized;
    count: number;
    /** The questions themselves, not just how many there are. */
    asks: {
      id: string;
      label: Localized;
      required: boolean;
      /** Offered from the citizen's own verified profile rather than asked. */
      known: boolean;
    }[];
  }[];
  documents: PublicServiceDocument[];
  eligibility: {
    rules: { id: string; reason: Localized; failVerdict: 'ineligible' | 'needs_review' }[];
    verdicts: { verdict: string; summary: Localized }[];
  } | null;
  fees: PublicServiceFeeLine[];
  feeExplanation: Localized | null;
  feeWaivers: { id: string; label: Localized }[];
  /** `ministry` means the fee is paid on the ministry's page, not here. */
  feeSettlement: 'platform' | 'ministry' | null;
  /** Stage names and turnarounds. The staff role each needs stays internal. */
  reviewStages: { id: string; name: Localized; tatHours: number }[];
  /** What has to happen, and what stops it. Never the reads that find out. */
  process: {
    summary: Localized;
    blocks: { id: string; when: Localized; reason: Localized; citizenMustDo: Localized }[];
  } | null;
  /** Verbatim copy from the ministry's own published card. */
  processText: Localized | null;
  termsText: Localized | null;
  feesText: Localized | null;
  documentsText: Localized | null;
  officialDocumentList: { en: string[]; ar: string[] } | null;
  /**
   * What the ministry publishes it costs, read out of its own fee prose.
   *
   * Separate from `fees`, which is what this platform computes and collects.
   * Most of the catalogue collects nothing here and is still not free — the
   * ministry prices 82 of the 118 services — so a page that reads only `fees`
   * tells almost everybody a government service costs nothing.
   *
   * `free: true` is a published price of zero. `null` is a card that publishes
   * no price, which the page has to say differently.
   */
  officialFeeSchedule: {
    lines: {
      label: Localized;
      amountFils: number;
      minimumFils: number | null;
      maximumFils: number | null;
      /** A band, a delay charge or an exception — never simply the price. */
      conditional: boolean;
    }[];
    free: boolean;
  } | null;
  /** The ministry's own numbered process, as steps rather than a paragraph. */
  officialProcessSteps: Localized[] | null;
  expectedTime: Localized | null;
  decisionDays: number | null;
  smartServicesId: string | null;
  lastVerifiedAt: string | null;
  aiEnabled: boolean;
  sourceUrl: Localized | null;
  maturity: ServiceMaturity | null;
}

/** The applicant's Sheikh Zayed Housing Programme case, as MOEI reports it. */
export interface HousingCaseView {
  applicationNo: string;
  requestStatus: string | null;
  statusCode: string | null;
  status: Localized | null;
  award: {
    assistance: string | null;
    aid: string | null;
    amount: number | null;
    purpose: string | null;
    bedRooms: number | null;
  } | null;
  /**
   * Qarar (قرار) projection from Manzali awards: typed grant/loan, amount,
   * and whether the citizen must open a bank implementation file.
   */
  qarar: {
    reference: string;
    assistanceType: 'grant' | 'loan' | null;
    assistanceRaw: string | null;
    aidRaw: string | null;
    amountAed: number | null;
    purpose: string | null;
    bedRooms: number | null;
    requestStatus: string | null;
    status: Localized | null;
    requiresBankFile: boolean;
    openFileServiceSlug: string | null;
    source: 'manzali';
  } | null;
}

export interface CatalogForCustomer {
  applicable: ApplicableCatalogEntry[];
  other: CatalogEntry[];
  categories: { slug: string; name: Localized; icon?: string }[];
  profile: { audiences: string[]; assets: string[]; holdings?: CustomerHoldings };
  /** Null when there is no case, or when the housing bundle was unreachable. */
  housingCase: HousingCaseView | null;
}

/**
 * Who the portal is currently acting as.
 *
 * Established by starting a conversation — the platform creates a real citizen
 * record and issues a token — rather than chosen from a list of seeded people.
 * `name` is null until the citizen tells us or UAE Pass does.
 */
export interface CitizenIdentity {
  id: string;
  name: Localized | null;
  verificationTier: string;
  lang: string;
  /**
   * Whether there is a UAE Pass session in this browser as well as ours.
   *
   * True only for a live UAE Pass sign-in on the website. Signing out revokes our
   * token; when this is set, the citizen should also be sent to UAE Pass to end
   * its session, or the next person on the device is signed straight back in as
   * the last. Absent on older platforms, so treat undefined as false.
   */
  uaePassSession?: boolean;
  /**
   * Whether this citizen is signed in, as opposed to merely recognised.
   *
   * The two have different lifetimes on purpose. Recognition lasts ninety days
   * and is why a returning visitor lands on their own dashboard; being signed in
   * lasts half an hour of inactivity and is what opens a new application or
   * reveals the identity documents the ministry holds. Absent on older
   * platforms, so treat undefined as false.
   */
  authenticated?: boolean;
  /** The lifetime rules and this session's deadlines, for the countdown. */
  session?: CitizenSessionPolicy;
}

/**
 * How long a citizen stays signed in, as the platform is configured.
 *
 * Served rather than compiled into each client. Two clients counting down
 * against numbers they hardcoded is two clients that disagree with the server
 * the first time a deployment changes one — and the disagreement surfaces as a
 * citizen being signed out without the warning they were promised.
 */
export interface CitizenSessionPolicy {
  idleMinutes: number;
  absoluteHours: number;
  /** Whether closing the tab ends the session. */
  endOnPageClose: boolean;
  /** Whether quitting the browser ends it. A separate question from the tab. */
  endOnBrowserClose: boolean;
  /** Minutes of notice before a lapse. Zero disables the warning. */
  warnBeforeMinutes: number;
  /** Rolling: moves forward on every authenticated request. */
  idleExpiresAt: string | null;
  /** Fixed at sign-in and never extended. */
  absoluteExpiresAt: string | null;
}

/** Which authority supplied a detail the platform holds. */
export type HeldIdentitySource = 'uae_pass' | 'icp' | 'conversation' | 'ministry';

export interface HeldIdentityField {
  /** The profile key, for ordering or testing without matching on a label. */
  key: string;
  label: Localized;
  value: Localized;
  /**
   * How the value should be read, not what it means.
   *
   * `text` is prose and takes the page's direction. `code` is a machine-format value —
   * a phone number, an email, a PO box — and is a left-to-right run in both languages:
   * `+971501000014` set in an Arabic paragraph renders with the plus at the wrong end.
   *
   * `date` values are ISO `YYYY-MM-DD` and safe to hand a date formatter — the server
   * downgrades anything it could not parse to `text` rather than letting a client
   * render "Invalid Date" where a citizen's date of birth should be.
   *
   * `masked` values arrive already reduced to their last four characters. The full
   * value is not in the response and cannot be recovered from it.
   */
  kind: 'text' | 'code' | 'date' | 'masked';
  at: string | null;
}

export interface HeldIdentityGroup {
  source: HeldIdentitySource;
  label: Localized;
  /** What this source is, in one line, for somebody who has not met the acronym. */
  detail: Localized;
  /** Where to go to have a detail corrected. Null when that is the ministry itself. */
  correction: Localized | null;
  fields: HeldIdentityField[];
}

/**
 * The identity details the platform holds, grouped by where each came from.
 *
 * Grouped by source rather than by category because the source is the actionable part:
 * the ministry cannot amend an ICP record, so a citizen who finds a wrong nationality
 * here needs to know it is ICP's to correct, not ours.
 */
export interface HeldIdentity {
  verificationTier: string;
  /** When the identity behind these details was last proved. */
  verifiedAt: string | null;
  groups: HeldIdentityGroup[];
  /**
   * How many held details this tier may not see.
   *
   * Everything here is something the platform looked up or was told earlier, which the
   * platform's own sensitivity rules reserve for a fully verified identity. The count is
   * what lets an unverified citizen be told what signing in would show, rather than
   * being shown an empty page that implies nothing is held.
   */
  withheld: number;
}

/** A seeded demo record the portal may sign in as until UAE Pass is live. */
export interface SampleAccount {
  key: string;
  label: Localized;
  /** What this record has open, so a tester can pick a useful one. */
  standing: Localized;
}

// --- Ministry CRM (Microsoft Dynamics 365 / Dataverse, read-only) -----------

export interface CrmEntitySummary {
  /** Catalog view key — the `entity` parameter for records/mirror queries. */
  key: string;
  /** Underlying Dataverse entity set (views may share one set). */
  set: string;
  logical: string;
  labelEn: string;
  labelAr: string;
  headline: boolean;
}

export interface CrmRecordDto {
  id: string;
  title: string | null;
  createdon: string | null;
  set: string;
  raw: Record<string, unknown>;
}

export interface CrmRecordComment {
  id: string;
  kind: 'note' | 'activity';
  activityType: string | null;
  subject: string | null;
  text: string | null;
  createdOn: string | null;
  modifiedOn: string | null;
  author: string | null;
  isDocument: boolean;
  fileName: string | null;
  mimeType: string | null;
}

/** The citizen behind a live CRM case: who they are on the platform, what they
 *  have open, and whether they are mid-conversation on WhatsApp. */
/** An integration deliberately running against a mock or a sandbox. */
export interface PendingIntegrationView {
  integration: string;
  mode: string;
  detail: string;
}

export interface PlatformReadiness {
  environment: string;
  /** Whether a citizen can sign in as a seeded demo persona. */
  sampleAccounts: { enabled: boolean; detail: string | null };
  /** Whether a citizen's submission actually reaches the ministry. */
  ministryWrites: {
    mlsMode: string;
    readOnly: boolean;
    filesForReal: boolean;
  };
  pendingIntegrations: PendingIntegrationView[];
  capabilities: {
    /** Whether the registry is switched on; off by default. */
    enabled: boolean;
    /** What these capabilities are reachable from — not the citizen journeys. */
    surface: string;
    generatedAt: string;
    packs: Array<{ backend: string; version: string }>;
    highestImpact: string;
    total: number;
    serving: number;
    draft: number;
    blocking: Array<{
      capability: string;
      title: string;
      backend: string;
      decisionImpact: string;
      missingControls: string[];
    }>;
  };
}

/** One journey as the catalogue advertises it. */
export interface JourneySummary {
  id: string;
  title: string;
  service: string;
  transports: string[];
  languages: Array<'en' | 'ar'>;
  /** Whether running it writes to a ministry. */
  mutating: boolean;
  owner: string;
  kind: 'scripted' | 'generative';
}

/* ------------------------------------------- developer documentation portal */

/**
 * The published developer surface.
 *
 * Mirrors `platform/src/docs/inventory.ts` *after* redaction: the inventory
 * carries an `internal` key on every operation describing how the call is
 * routed, and the projection removes it. There is deliberately no field for it
 * here — a client that cannot name it cannot render it.
 */
export interface DevPortalEndpoint {
  method: string;
  route: string;
  path: string;
  documented: boolean;
}

export interface DevPortalServiceSummary {
  slug: string;
  name: { en: string; ar: string };
  category: string;
  tier: 'live' | 'ai_enabled' | 'preparation_only';
  audience: string[];
  channels: { webchat: boolean; whatsapp: boolean; voice: boolean };
  maturity: Record<string, unknown>;
  counts: {
    steps: number;
    fields: number;
    requiredFields: number;
    conditionalFields: number;
    documents: number;
    conditionalDocuments: number;
    eligibilityRules: number;
    feeLines: number;
    reviewStages: number;
  };
  journeys: string[];
  hasImprovisingJourney: boolean;
  mls?: { flow?: string; appType?: number; controller?: string; liveReady: boolean };
}

export interface DevPortalCapability {
  id: string;
  title: string;
  description: string;
  backend: string;
  owner: string;
  lifecycle: string;
  decisionImpact: string;
  readOnly: boolean;
  requiresHumanApproval: boolean;
  failurePosture: string;
  mayEnterModelContext: boolean;
  allowedStates: string[];
  operations: string[];
  requiredControls: string[];
  missingControls: string[];
  grantedRoles: string[];
}

export interface DevPortalOperation {
  id: string;
  backend: string;
  title: string;
  description: string;
  controller: string;
  method: string;
  pathTemplate: string;
  kind: 'read' | 'write';
  lifecycle: string;
  owner: string;
  inputFields: string[];
  requiredInputFields: string[];
  readFields: string[];
  outcomes: string[];
  narrative?: string;
  evidence?: string;
  hasProbeInput: boolean;
  hasObservedShape: boolean;
}

export interface DevPortalTool {
  name: string;
  capability: string;
  description: string;
  inputSchema: { type: 'object'; properties: Record<string, unknown>; required?: string[] };
  annotations: {
    readOnlyHint: boolean;
    destructiveHint: boolean;
    idempotentHint: boolean;
    openWorldHint: boolean;
  };
  schemaHash: string;
  grantedRoles: string[];
  disclosure: 'eager' | 'deferred' | 'not_served';
}

export interface DeveloperSurfaceResponse {
  generatedAt: string;
  packs: Array<{ backend: string; version: string }>;
  endpoints: DevPortalEndpoint[];
  services: DevPortalServiceSummary[];
  capabilities: DevPortalCapability[];
  operations: DevPortalOperation[];
  mcpTools: DevPortalTool[];
  /**
   * What this deployment will let you run, as opposed to what it can describe.
   * The catalogue is identical everywhere; invocation is a configuration.
   */
  runtime: { invocationEnabled: boolean; writesEnabled: boolean; reason?: string };
}

export interface DevPortalJourney {
  id: string;
  title: string;
  transports: string[];
  languages: Array<'en' | 'ar'>;
  mutating: boolean;
  owner: string;
  hasScript: boolean;
  /** The improvising citizen, whose facts are the service's worked example. */
  persona?: {
    id: string;
    goal: string;
    temperament: string;
    facts: Record<string, string>;
    terminalStates: string[];
  };
}

/** What the ministry declares it wants, read from the contract the flow asserts against. */
export interface DevPortalMlsFieldContract {
  name: string;
  step: string;
  type: string;
  required: boolean;
  nullable: boolean;
  literal?: string;
  maxLength?: number;
  minLength?: number;
  note?: string;
}

export interface DevPortalFieldMapEntry {
  field: {
    id: string;
    label: { en: string; ar: string };
    type: string;
    group: string;
    required: 'always' | 'conditional' | 'never';
    validation?: Record<string, unknown>;
    optionCount?: number;
    piiClass: string;
  };
  /** Absent when this answer never reaches the ministry. */
  mls?: DevPortalMlsFieldContract;
  /** Only differences that change what a caller must do. */
  differences: string[];
}

export interface DevPortalFieldMap {
  slug: string;
  entries: DevPortalFieldMapEntry[];
  /** Asked of the citizen and never sent. */
  notSent: string[];
  /** Wanted by the ministry and never asked: profile, a ministry read, or a constant. */
  suppliedElsewhere: DevPortalMlsFieldContract[];
  steps: Array<{ step: string; title: string; fieldCount: number }>;
}

export interface DevPortalServiceResponse {
  slug: string;
  summary: DevPortalServiceSummary | undefined;
  fieldMap: DevPortalFieldMap;
  /** The full ServiceSpec: fields, validation, predicates, fees, SLA, sync. */
  spec: Record<string, unknown>;
  /** Four graphs — journey, data, integrations, review — from the same spec. */
  graph: {
    slug: string;
    generatedAt: string;
    graphs: Record<string, { nodes: unknown[]; edges: unknown[] }>;
    summary: Record<string, number>;
  };
  journeys: DevPortalJourney[];
  /** How to exercise the service over HTTP, as requests rather than as a button. */
  apiCalls: DevPortalApiCalls;
  developerNote: string | null;
}

/** One request in the sequence that exercises a service. */
export interface DevPortalApiCall {
  id: string;
  name: string;
  purpose: string;
  method: 'GET' | 'POST';
  path: string;
  headers: Record<string, string>;
  body?: unknown;
  form?: { field: string; value: string; type: 'file' | 'text' }[];
  captures?: { variable: string; from: string }[];
  expect: string;
}

export interface DevPortalApiCalls {
  slug: string;
  variables: { key: string; value: string; description: string }[];
  calls: DevPortalApiCall[];
  /** Answers only the developer can supply, with the reason there is no example. */
  supplyYourself: { fieldId: string; label: string; why: string }[];
  /** The same requests as curl, keyed by call id. */
  curl: Record<string, string>;
  /** A Postman v2.1 collection, ready to import. */
  postman: unknown;
}

export interface DevPortalCapabilityExample {
  capability: string;
  exampleInput: Record<string, unknown>;
  expectedOutcome: string;
  runnable?: boolean;
  notes?: string;
}

export interface DevPortalCapabilityResponse {
  capability: DevPortalCapability;
  operations: DevPortalOperation[];
  tool: DevPortalTool | null;
  example: DevPortalCapabilityExample | null;
}

export interface DevPortalOperationResponse {
  operation: DevPortalOperation;
  calledBy: string[];
  exampleInput: Record<string, unknown> | null;
  observedShape: {
    operation: string;
    observedAt: string;
    ok: boolean;
    status: number;
    shape: Array<{ pointer: string; type: string; count?: number; value?: unknown }>;
  } | null;
}

export interface DevPortalMcpResponse {
  connection: {
    endpoint: string;
    discovery: string;
    protocolVersion: string;
    audience: string;
    scope: string;
    authorizationServer: string | null;
    doorRoles: string[];
    devTokensAccepted: boolean;
  };
  roles: readonly string[];
  roleMapping: Record<string, string[]>;
  previewedRole: string | null;
  tools: DevPortalTool[];
}

/** One step of a traced invocation, as the portal renders it. */
export interface DevPortalTraceStep {
  operation: string;
  title: string;
  lifecycle: string;
  kind: 'read' | 'write';
  /** Method and path as sent. Not how the call physically travelled. */
  request: { method: string; path: string };
  status: number;
  durationMs: number;
  outcome: string;
  failure: boolean;
  fields: Record<string, unknown>;
  /** Declared and absent: what a default was substituted for. */
  missingFields: string[];
  /** Present and undeclared: the drift signal. */
  unknownFields: string[];
}

export interface DevPortalTraceResponse {
  capability?: string;
  correlationId?: string;
  outcome?: string;
  degraded?: boolean;
  halted?: boolean;
  fields?: Record<string, unknown>;
  blobs?: string[];
  durationMs?: number;
  trace?: DevPortalTraceStep[];
  /** Present instead of a trace when the call was refused or failed. */
  error?: { code: string; message: string };
}

export interface JourneyCatalogueResponse {
  /**
   * Whether a journey can run here at all.
   *
   * False in production, where sample personas are rightly disabled — the
   * console greys the button and shows `reason` rather than letting every run
   * fail with the same message.
   */
  runnable: boolean;
  reason?: string;
  journeys: JourneySummary[];
}

export interface JourneyReportView {
  journeyId: string;
  title: string;
  service: string;
  environment: string;
  requestedBy: string;
  startedAt: string;
  finishedAt: string;
  passed: boolean;
  results: Record<string, unknown>[];
  /** Where two transports disagreed — usually the finding worth reading. */
  divergences: Record<string, unknown>[];
  /** Set when the runner declined rather than ran. */
  refused?: string;
}

export interface JourneyRunResponse {
  report: JourneyReportView;
  markdown: string;
}

// ---------------------------------------------------------------------------
// Simulator
// ---------------------------------------------------------------------------

/** What a run may be started against. */
export interface SimulatorServiceSummary {
  slug: string;
  name: Localized;
  shortName: Localized | null;
  summary: Localized;
  /** The Ministry's own flow name, which every engine extension keys on. */
  flow: string;
  /** What the citizen must already hold for the service to be reachable. */
  precondition: 'none' | 'owned_boat' | 'held_licence' | 'training_record';
  charges: boolean;
}

export type SimulatorTemperament =
  | 'cooperative'
  | 'impatient'
  | 'confused'
  | 'changes-mind'
  | 'code-switching'
  | 'probing';

export interface SimulatorPersonaPreset {
  id: string;
  label: Localized;
  temperament: SimulatorTemperament;
  brief: Localized;
}

export interface SimulatorCatalogue {
  services: SimulatorServiceSummary[];
  personas: SimulatorPersonaPreset[];
  temperaments: SimulatorTemperament[];
}

/**
 * Three verdicts, not two.
 *
 * `warned` is the one that matters: "the register could not be reached" and
 * "you own no boat" look identical downstream and mean opposite things, and
 * refusing on the first would turn a minute of bridge trouble into a service
 * that appears broken.
 */
export interface SimulatorPreflight {
  verdict: 'ready' | 'warned' | 'refused';
  reason: Localized | null;
  remedy?: Localized | null;
  ownedBoats?: number;
  registerUnreachable?: boolean;
}

export type SimulatorRunStatus =
  | 'queued'
  | 'running'
  /** Filed, and paused on the Ministry rather than over. */
  | 'awaiting_gov'
  | 'finished'
  | 'refused'
  | 'cancelled'
  | 'error';

export interface SimulatorRun {
  id: string;
  /**
   * A display name and nothing more.
   *
   * The customer id and tenant are stripped at the boundary: this record is
   * readable by anyone, and a list pairing a name with a customer identifier is
   * a directory of who has used it.
   */
  customer_label: string | null;
  service_slug: string;
  lang: 'en' | 'ar';
  persona_brief: string;
  /**
   * Which facts the person held, never what they were.
   *
   * "They knew their name and an Emirates ID" is what explains a run — it is
   * the difference between a citizen who could answer and one who could not —
   * and the values would publish a government identity on an open surface.
   */
  persona: {
    goal?: string;
    temperament?: string;
    brief?: string;
    knewAbout?: string[];
    ownedIdentifiers?: number;
  };
  temperament: string;
  max_turns: number;
  status: SimulatorRunStatus;
  /** Why it ended, in the harness's own vocabulary. */
  ending: 'goal' | 'gave-up' | 'budget' | 'aborted' | 'unavailable' | 'not-filed' | null;
  /** The citizen's own words for stopping — the most useful line in the record. */
  ended_because: string | null;
  reached_goal: boolean;
  passed: boolean | null;
  final_state: string | null;
  application_reference: string | null;
  gov_reference: string | null;
  /**
   * Whether the run proved anything.
   *
   * `mls_mock` means it demonstrated that the mock agrees with itself.
   */
  integration_backend: string | null;
  /**
   * Whether the operator asked this run to file at the ministry for real.
   *
   * Off unless asked for. A simulated citizen taps Submit about a second after
   * the review card appears, so a run that files by default puts applications
   * in front of a clerk faster than anybody can read them.
   */
  files_for_real: boolean;
  invariant_violations: Array<{ invariant: string; detail: string; turn?: number }>;
  turn_count: number;
  leg_count: number;
  environment: string;
  started_by: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
  finished_at: string | null;
  /** Present on the list only. */
  comment_count?: number;
}

export interface SimulatorTurn {
  id: string;
  run_id: string;
  leg: number;
  seq: number;
  actor: 'citizen' | 'service' | 'system';
  kind: string;
  body: string | null;
  widgets: Widget[];
  asked_fields: string[];
  state: string | null;
  reasoning: string | null;
  latency_ms: number | null;
  created_at: string;
}

export interface SimulatorComment {
  id: string;
  run_id: string;
  turn_id: string | null;
  author_name: string;
  body: string;
  revision: number;
  created_at: string;
  updated_at: string;
}

export interface SimulatorRunDetail {
  run: SimulatorRun;
  turns: SimulatorTurn[];
  comments: SimulatorComment[];
}

export interface SimulatorStartInput {
  service: string;
  lang?: 'en' | 'ar';
  preset?: string;
  personaBrief?: string;
  temperament?: SimulatorTemperament;
  maxTurns?: number;
  /** File at the ministry rather than stopping at the review summary. Off by default. */
  filesForReal?: boolean;
}

/** Whether this run may cross the ministry boundary, and the sentence saying why. */
export interface SimulatorFilingPolicy {
  mayFile: boolean;
  because: Localized;
}

export interface SimulatorStartResponse {
  runId: string;
  verdict: SimulatorPreflight['verdict'];
  reason?: Localized | null;
  remedy?: Localized | null;
  filing?: SimulatorFilingPolicy;
}

export interface CrmCaseContext {
  ok: boolean;
  contact: { id: string; name: string | null } | null;
  match: {
    method: 'crm_link' | 'phone';
    /** The CRM field the match came from, so the agent can judge it. */
    matchedOn: string | null;
    customerId: string;
    name: string | null;
    phoneMasked: string | null;
    verificationTier: string;
  } | null;
  phonesFound: Array<{ masked: string; field: string; origin: 'case' | 'contact' }>;
  conversations: Array<{
    id: string;
    channel: string;
    handoverState: string;
    lang: string | null;
    messageCount: number;
    lastCitizenMessageAt: string | null;
    createdAt: string;
  }>;
  serviceRequests: Array<{
    reference: string;
    state: string;
    channel: string;
    serviceName: Record<string, string> | null;
    serviceSlug: string | null;
    submittedAt: string | null;
    slaDueAt: string | null;
    govStatus: string | null;
    createdAt: string;
  }>;
}

export interface CrmStatus {
  configured: boolean;
  mode: 'mock' | 'live';
  sso: {
    connected: boolean;
    email: string | null;
    connectedAt: string | null;
    method: 'console_sso' | 'direct' | null;
  };
  sharedReader: {
    connected: boolean;
    email: string | null;
    orgUrl: string | null;
    connectedAt: string | null;
    lastError: string | null;
  };
}

export interface CrmPerServiceStat {
  linkId: string;
  serviceId: string;
  serviceName: Localized | null;
  serviceSlug: string | null;
  crmEntity: string;
  odataFilter: string | null;
  labelEn: string | null;
  labelAr: string | null;
  count: number | null;
}

export interface CrmStats {
  ok: boolean;
  mode: 'mock' | 'live';
  entities: Array<{ key: string; set: string; labelEn: string; labelAr: string; count: number | null }>;
  perService: CrmPerServiceStat[];
  /** entitySet carries the view KEY (several views share one physical set). */
  snapshots: Array<{ entitySet: string; count: number; capturedAt: string; source: 'live' | 'mock' }>;
}

export interface CrmLink {
  id: string;
  serviceId: string;
  serviceName: Localized | null;
  serviceSlug: string | null;
  crmEntity: string;
  odataFilter: string | null;
  labelEn: string | null;
  labelAr: string | null;
}

export interface CrmCustomerPanel {
  ok: boolean;
  linked: boolean;
  contactId?: string;
  contact: Record<string, unknown> | null;
  contactError?: string | null;
  metadata?: Record<string, unknown>;
  cases: Array<{ set: string; records: CrmRecordDto[] }>;
}

export interface CrmMirrorEntityStatus {
  entitySet: string;
  mirrored: number;
  withResolution: number;
  translated: number;
  embedded: number;
  backfillDone: boolean;
  cursorModifiedOn: string | null;
  lastRunAt: string | null;
  lastError: string | null;
}

export interface CrmTranslationStats {
  translated: number;
  pending: number;
  available: boolean;
}

export interface CrmMirrorStatus {
  enabled: boolean;
  semanticEnabled: boolean;
  entities: CrmMirrorEntityStatus[];
  totalMirrored: number;
  totalEmbedded: number;
  translations: CrmTranslationStats;
}

export interface CrmCaseI18nTexts {
  title: string | null;
  body: string | null;
  resolution: string | null;
}

export interface CrmSimilarCase {
  id: string;
  entitySet: string;
  recordId: string;
  title: string | null;
  body: string | null;
  resolution: string | null;
  statusLabel: string | null;
  modifiedOn: string | null;
  score: number;
  semanticScore: number | null;
  lexicalScore: number | null;
  retrievalMethod: 'semantic' | 'hybrid' | 'lexical';
  caseType: string | null;
  categories: string[];
  matchReason: CrmBilingualText | null;
  i18n: { en: CrmCaseI18nTexts; ar: CrmCaseI18nTexts };
  sourceLang: 'en' | 'ar' | null;
  translationPending: boolean;
}

export interface CrmBilingualText {
  en: string;
  ar: string;
}

export interface CrmResolutionDraftMessage {
  kind: 'customer_reply' | 'internal_request';
  recipient: CrmBilingualText;
  subject: CrmBilingualText | null;
  body: CrmBilingualText;
}

export interface CrmResolutionSuggestion {
  source: 'llm' | 'deterministic';
  summary: CrmBilingualText;
  suggestedResolution: CrmBilingualText;
  confidence: 'low' | 'medium' | 'high';
  rationale: CrmBilingualText;
  /** Send-ready customer reply plus any distinct internal-team requests. */
  draftMessages: CrmResolutionDraftMessage[];
  /** 1-based positions into the accompanying similar-cases array. */
  references: number[];
}

export interface CxonePublicSettings {
  mode: 'mock' | 'live';
  region: string;
  issuer: string;
  authMethod: 'client_secret_post' | 'client_secret_basic';
  tenantId: string;
  businessUnitId: string;
  channelIntegrationId: string;
  brandId: string;
  webChannelId: string;
  whatsappChannelId: string;
  defaultSkillId: string;
  connectionStatus: 'disconnected' | 'validating' | 'live' | 'degraded';
  configured: boolean;
  lastCheckedAt: string | null;
  lastError: string | null;
  agentApp: {
    clientId: string;
    redirectUri: string;
    configured: boolean;
  };
  byoc: {
    integrationBoxUrl: string;
    authorizationUrl: string;
    addActionUrl: string;
    reconnectActionUrl: string;
    removeActionUrl: string;
    clientId: string;
    clientSecretConfigured: boolean;
  };
}

export interface CxoneHealth {
  ok: boolean;
  mode: 'mock' | 'live';
  status: 'disconnected' | 'validating' | 'live' | 'degraded';
  authenticated: boolean;
  latencyMs: number;
  region: string;
  discovered?: boolean;
  apiEndpoint?: string;
  tenantValidated?: boolean;
  capabilities?: {
    digital: boolean;
    routing: boolean;
    eventHub: boolean;
    realtime: boolean;
  };
  error?: string;
}

export interface CxoneAgentConfig {
  mode: 'mock' | 'live';
  connectionStatus: 'disconnected' | 'validating' | 'live' | 'degraded';
  issuer: string;
  region: string;
  tenantId: string;
  clientId: string;
  redirectUri: string;
  configured: boolean;
}

export interface CxoneAgentSessionLease {
  id: string;
  leaseToken: string;
  expiresAt: string;
}

export interface CxoneAgentEventInput {
  eventId: string;
  eventKind: string;
  contactId?: string;
  externalThreadId?: string;
  status?: string;
  skillId?: string;
  routingQueueId?: string;
  routingQueueName?: string;
  queuePosition?: number;
  estimatedWaitSeconds?: number;
  assignee?: { id?: string; name?: string; avatar?: string };
  disposition?: { id?: string; name?: string };
  occurredAt?: string;
  [key: string]: unknown;
}

export interface HandoverEvent {
  id: string;
  conversationId: string | null;
  kind: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

/** Who owns a conversation once the citizen asks for a person. */
export type HandoverProvider = 'native' | 'cxone';

/** A draft reply offered to an agent. Never sent without them pressing send. */
export interface HandoverSuggestion {
  id: string;
  tone: 'direct' | 'warm';
  toneLabel: Localized;
  text: string;
  lang: Lang;
  /** How long this draft took, so a slow model is visible rather than assumed. */
  latencyMs: number;
}

export interface HandoverApplicationField {
  id: string;
  label: Localized;
  prompt: Localized | null;
  helpText: Localized | null;
  placeholder: Localized | null;
  type: string;
  options: { value: string; label: Localized }[] | null;
  value: unknown;
  required: boolean;
  /** False for ministry-supplied answers: readable, never typed over. */
  editable: boolean;
  source: string | null;
}

export interface HandoverApplicationGroup {
  id: string;
  label: Localized;
  fields: HandoverApplicationField[];
}

export interface HandoverApplicationDocument {
  id: string;
  name: Localized;
  attachedCount: number;
  minFiles: number;
  satisfied: boolean;
  electronicallyLinked: boolean;
}

export interface HandoverApplicationView {
  applicationId: string;
  /** Record locator for staff lookups — never read out to a citizen. */
  reference: string;
  /** The only reference that works at a counter. Null until the ministry files. */
  govReference: string | null;
  serviceSlug: string;
  serviceName: Localized;
  state: string;
  lang: Lang;
  groups: HandoverApplicationGroup[];
  missing: {
    id: string;
    label: string;
    type: string;
    conditional: boolean;
    decidesEligibility: boolean;
  }[];
  documents: HandoverApplicationDocument[];
  totalFils: number | null;
  eligibilityVerdict: string | null;
  fileable: boolean;
  notYetPermitted: { what: string; because: string }[];
}

export interface CitizenHandoverEvent {
  id?: string;
  kind: string;
  conversationId?: string;
  state?: 'ai' | 'pending_human' | 'human';
  messageId?: string;
  role?: string;
  agentName?: string;
  status?: string;
  queuePosition?: number;
  estimatedWaitSec?: number;
  agentAvatar?: string | null;
  routingQueueName?: string | null;
  dispositionName?: string | null;
  connection?: 'live' | 'degraded';
  reader?: string;
  staffId?: string;
  at?: string;
}

/** A durable conversation in the persona's history rail. */
export interface ConversationSummary {
  conversationId: string;
  serviceSlug: string;
  serviceName: Localized;
  /**
   * What the thread is called — derived from the application, or whatever the
   * citizen renamed it to. Null until a turn produces one; fall back to the
   * service name.
   */
  title: string | null;
  /** True when a person named it, in which case nothing may overwrite it. */
  titleCustom: boolean;
  reference: string | null;
  state: string | null;
  lang: string;
  snippet: string | null;
  lastMessageAt: string;
}

// --- customer dashboard (portal) --------------------------------------------

export type DashboardSignalType =
  | 'document_expiring'
  | 'document_expired'
  | 'application_approved'
  | 'application_needs_input'
  | 'application_in_review'
  | 'application_draft'
  | 'payment_due';

export type DashboardSignalSeverity = 'info' | 'success' | 'warning' | 'danger';

export interface DashboardSignal {
  id: string;
  type: DashboardSignalType;
  severity: DashboardSignalSeverity;
  label: Localized;
  detail: Localized;
  slug?: string;
  /** Ministry-issued reference only; never the platform's MOEI-* locator. */
  reference?: string;
  /** Present whenever `reference` is, so the signal can link to tracking. */
  accessToken?: string;
  daysRemaining?: number;
  amountFils?: number;
}

export interface DashboardRecommendation {
  slug: string;
  name: Localized;
  reason: Localized;
}

/** One pleasure boat the Ministry holds for the signed-in citizen. */
export interface CitizenBoat {
  officialNumber: string;
  nameEn: string;
  nameAr: string;
  registrationExpiry: string | null;
  /** Negative once the registration has expired; null when none is published. */
  daysToExpiry: number | null;
  dueForRenewal: boolean;
}

/**
 * A page of the citizen's boats.
 *
 * `total` is what the Ministry holds and `matched` is what the search left, so
 * a fleet owner can be told both how many they own and how many answered their
 * query — neither of which `boats.length` says on its own.
 */
export interface CitizenBoatPage {
  total: number;
  matched: number;
  boats: CitizenBoat[];
}

// --- the marine asset console --------------------------------------------

/** Which sort of thing a card is about, as it appears in a URL. */
export type MarineAssetKind = 'boat' | 'licence';

/**
 * How sure we are the Ministry will accept this asset for this service.
 *
 * `unchecked` means nobody has asked yet, or that the service files under an
 * application type the Ministry has never been watched answering. It is not a
 * soft no: an unchecked action stays offered, because refusing a citizen a
 * service on the strength of silence is the worse mistake.
 */
export type ActionEligibility = 'unchecked' | 'eligible' | 'declined';

/**
 * Whether an action is on the card or behind the disclosure.
 *
 * Eight actions on one card is noise, and confirming eight per boat costs a
 * ministry call each. The primary ones load with the page; the rest confirm
 * when the citizen opens them.
 */
export type ActionTier = 'primary' | 'more';

export interface AssetAction {
  slug: string;
  name: Localized;
  /** The compact label for a button, where the service carries one. */
  shortName: Localized | null;
  flow: string;
  appType: number | null;
  tier: ActionTier;
  eligibility: ActionEligibility;
}

/** An application about this asset that the citizen can still act on or watch. */
export interface AssetInFlight {
  id: string;
  /** Ministry-issued reference only; null while this is still a draft. */
  reference: string | null;
  accessToken: string;
  state: string;
  serviceSlug: string;
  serviceName: Localized;
  slaDueAt: string | null;
  submittedAt: string | null;
  updatedAt: string;
}

/** What the citizen wrote about the asset themselves. Never Ministry fact. */
export interface AssetAnnotations {
  nickname: string | null;
  berth: string | null;
  note: string | null;
  photoDocumentId: string | null;
  /** Short-lived and presigned, so an `<img>` can load it without a token. */
  photoUrl: string | null;
  updatedAt: string | null;
}

export interface MarineAsset {
  kind: MarineAssetKind;
  /** The Ministry's key: an official number, or a licence number. */
  identifier: string;
  nameEn: string;
  nameAr: string;
  statusEn: string | null;
  statusAr: string | null;
  /** Struck off the register, so only the deletion services still apply. */
  struckOff: boolean;
  /** Days until the *insurance* lapses. MOEI publishes no registration expiry. */
  daysToInsuranceExpiry: number | null;
  insuranceStart: string | null;
  insuranceEnd: string | null;
  portEn: string | null;
  portAr: string | null;
  ownerType: number | null;
  vesselType: string | null;
  expiryDate: string | null;
  blocked: boolean;
  vsid: number | null;
  actions: AssetAction[];
  inFlight: AssetInFlight[];
  annotations: AssetAnnotations;
}

export interface MarineAssetsPage {
  boats: MarineAsset[];
  licences: MarineAsset[];
  boatTotal: number;
  boatMatched: number;
  /** True when no Ministry read got through, as opposed to owning nothing. */
  fleetUnreachable: boolean;
  /** Services that start something the citizen does not hold yet. */
  offers: AssetAction[];
}

export interface MarineAssetDetail extends MarineAsset {
  /** Everything the Ministry holds about this one boat, or null. */
  dossier: {
    vessel: Record<string, unknown>;
    owners: Array<Record<string, unknown>>;
    engines: Array<Record<string, unknown>>;
  } | null;
  history: AssetInFlight[];
}

/**
 * The one thing being asked of the citizen, as an instruction.
 *
 * Null when nothing is: an application in review is news, not an action.
 */
export interface DashboardNextAction {
  signalId: string;
  verb: Localized;
  detail: Localized;
  severity: DashboardSignalSeverity;
  /** Null when nothing the platform publishes can act on this signal. */
  href: string | null;
  reference?: string;
  amountFils?: number;
}

/**
 * Which parts of the dashboard this citizen needs, already in order.
 *
 * The server decides because it is the only side that can know cheaply — the
 * portal would have to load every section to find out which were worth
 * drawing, and would reshuffle the page as each one landed.
 */
export type KnownDashboardSectionId =
  | 'attention'
  | 'boats'
  | 'housing'
  | 'requests'
  | 'explore';

/**
 * What the wire actually carries.
 *
 * The platform and the portal deploy separately, so a platform that has learnt
 * to compute a new domain — vehicles, credentials — will send it to portals
 * that have never heard of it. Widened for the same reason results crossing
 * the bridge tolerate unknown keys: the sender is often not the receiver's
 * build. The portal skips an id it has no component for; it must not choke on
 * one. The union above stays as the list of ids a current build understands,
 * and keeps autocomplete working.
 */
export type DashboardSectionId = KnownDashboardSectionId | (string & {});

/** Both names for the same thing; `Space` is the accurate one. */
export type KnownSpaceId = KnownDashboardSectionId;
export type SpaceId = DashboardSectionId;

export interface DashboardSectionEntry {
  id: DashboardSectionId;
  /** Whether the citizen holds anything here. Absent sections are not drawn. */
  present: boolean;
  /** What the rail chip shows. */
  count: number;
  /** How many of those need the citizen to act. Drives the ordering. */
  urgent: number;
  /**
   * Enough to draw a real card with no bespoke component.
   *
   * The portal has a component per known id and can skip the rest, because it
   * redeploys tomorrow. The mobile application cannot: a Space it has never
   * heard of must still render as something a citizen can tap, or a live case
   * is invisible until they update the app.
   */
  label: Localized;
  icon: string;
  accent: string;
  href: string;
}

/** The accurate name for the same entry. */
export type SpaceEntry = DashboardSectionEntry;

/** Personalized customer overview shown on the portal dashboard. */
export interface DashboardOverview {
  customer: {
    id: string;
    name: Localized | null;
    verificationTier: string;
    lang: Lang;
  };
  nextAction: DashboardNextAction | null;
  sections: DashboardSectionEntry[];
  /** The same list under the name the mobile client uses. */
  spaces?: SpaceEntry[];
  signals: DashboardSignal[];
  recommendations: DashboardRecommendation[];
}

// --- Manzili housing bundle -------------------------------------------------

export type HousingPhase =
  | 'document_validation'
  | 'bank_matching'
  | 'financing_contract'
  | 'contractor_matching'
  | 'exterior_design'
  | 'consultant_review'
  | 'interior_design'
  | 'furniture'
  | 'complete';

export type HousingSkippablePhase = Exclude<
  HousingPhase,
  'document_validation' | 'complete'
>;

export type HousingChapterId = 'apply' | 'plan' | 'visualize' | 'finish';
export type HousingVisualIntent = 'whole_home' | 'room' | 'renovation';

/*
 * No `HousingIntakeField` / `HousingIntakeState` here any more.
 *
 * They described the guided web form's projection of a housing application —
 * grouped fields with per-field save state, a completed count, a document
 * chapter. The form is gone; the conversation's own `ApplicationProjection`
 * describes the same application, and the Manzali space adds
 * `ManzaliIntakeProgress` for the two numbers a dashboard needs.
 */

export interface HousingStepEvidence {
  id: string;
  phase: HousingSkippablePhase;
  documentId: string;
  filename: string | null;
  mime: string | null;
  vaultStatus: string;
  reason: string;
  reviewStatus: 'pending' | 'verified' | 'changes_requested';
  reviewComment: Localized | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  updatedAt: string;
  url: string | null;
}

/**
 * The Housing Management Console's read models.
 *
 * Typed where the console depends on the field and left `unknown` where it does
 * not. The dossier carries the whole fact ledger and the whole rule evaluation,
 * and mirroring those shapes by hand here would produce a copy that drifts from
 * the server with nothing to catch it — so the console narrows them at the point
 * of use instead.
 */
export interface HousingConsoleOverview {
  byStage: Array<{ stage: string; cases: number }>;
  byBand: Array<{ band: string | null; cases: number }>;
  slaBreached: number;
  slaDueSoon: number;
  openFindings: number;
  blockingFindings: number;
  services: Array<{
    serviceSlug: string;
    cases: number;
    slaWorkingDays: number | null;
    breached: number;
  }>;
  integrations: { registered: number; live: number; mock: number; planned: number };
  /** What integration could remove from a citizen's document list, and can today. */
  documentsAvoidable: Array<{ emirate: string; couldWaive: number; canWaiveToday: number }>;
}

export interface HousingConsoleQueueRow {
  caseId: string;
  reference: string;
  serviceSlug: string;
  stage: string;
  status: string;
  priorityScore: number | null;
  priorityBand: string | null;
  priorityAxes: unknown;
  slaDueAt: string | null;
  openedAt: string;
  openFindings: number;
  blockingFindings: number;
  factCount: number;
  sourcesAnswered: number;
}

export interface HousingConsoleFact {
  factKey: string;
  value: unknown;
  sourceId: string;
  sourceName: string;
  observedAt: string;
  expiresAt: string | null;
  stale: boolean;
  confidence: number | null;
  provenance: unknown;
}

export interface HousingConsoleFinding {
  id: string;
  kind: string;
  severity: string;
  origin: string;
  title: { en: string; ar: string | null };
  detail: { en: string | null; ar: string | null };
  termId: string | null;
  articleClause: string | null;
  evidenceFactIds: string[];
  model: string | null;
  confidence: number | null;
  state: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export interface HousingConsoleRuleOutcome {
  key: string;
  passed: boolean;
  /** A fact the rule needed and did not find. Not the same as a failure. */
  indeterminate: boolean;
  factsUsed: string[];
  factsMissing: string[];
  exceptionApplied?: { key: string; articleClause?: string; title: { en: string; ar: string } };
  articleClause?: string;
  ministryTermId?: string;
  reason?: { en: string; ar: string };
  failVerdict?: string;
  severity?: string;
}

export interface HousingConsoleRuleEvaluation {
  rulesetSlug: string;
  version: number;
  verdict: 'eligible' | 'needs_review' | 'ineligible';
  outcomes: HousingConsoleRuleOutcome[];
  blocking: HousingConsoleRuleOutcome[];
  indeterminate: HousingConsoleRuleOutcome[];
  waived: HousingConsoleRuleOutcome[];
  evaluatedAt: string;
}

export interface HousingConsoleSource {
  id: string;
  nameEn: string;
  nameAr: string | null;
  kind: string;
  direction: 'inbound' | 'reference_data';
  emirateScope: string[];
  provides: string[];
  waivesDocumentIds: string[];
  transport: 'live' | 'mock' | 'planned';
  publishedOnServiceCard: boolean;
  notes?: string;
}

export interface HousingConsoleCaseDetail {
  caseId: string;
  reference: string;
  serviceSlug: string;
  stage: string;
  status: string;
  priorityScore: number | null;
  priorityBand: string | null;
  priorityAxes: unknown;
  slaDueAt: string | null;
  openedAt: string;
  stages: Array<{
    stage: string;
    pass: number;
    status: string;
    ownerStaffId: string | null;
    enteredAt: string;
    completedAt: string | null;
    dueAt: string | null;
    notes: unknown;
    aiVerdict: unknown;
  }>;
  facts: HousingConsoleFact[];
  conflicts: Array<{
    factKey: string;
    readings: Array<{ sourceId: string; value: unknown; observedAt: string }>;
  }>;
  ruleEvaluation: HousingConsoleRuleEvaluation | null;
  findings: HousingConsoleFinding[];
  documents: Array<{
    id: string;
    ministryDocumentId: string | null;
    requirementId: string | null;
    status: string;
    waivedBySourceId: string | null;
    extraction: unknown;
    defects: unknown;
  }>;
  integrationBoard: Array<{
    source: HousingConsoleSource;
    lastRun: {
      status: string;
      transport: string;
      latencyMs: number | null;
      factsWritten: number;
      error: string | null;
      requestedAt: string;
    } | null;
    factsProvided: string[];
  }>;
  /** The applicant's declared plot, for the Site tab's map. */
  site: { latitude: number; longitude: number } | null;
  decision: {
    id: string;
    outcome: string;
    assistanceType: string | null;
    amountAed: number | null;
    reasons: unknown;
    issuedAt: string | null;
    makerId: string | null;
    checkerId: string | null;
  } | null;
  timeline: Array<{ kind: string; actor: unknown; detail: unknown; at: string }>;
  documentWaivers: {
    waivable: Array<{ documentId: string; sourceIds: string[] }>;
    liveWaivable: Array<{ documentId: string; sourceIds: string[] }>;
  };
  /**
   * The analysis a reviewer reads before the raw dossier: what stops the case,
   * which facts to fetch and who could supply them, the income reconciled employer
   * by employer, and the conditions grouped the way the Resolution is written.
   *
   * Computed on the server because it is arithmetic about a citizen's entitlement,
   * and a reconciliation done in a React component is one nobody tests.
   */
  insight: {
    blockers: Array<{
      key: string;
      clause: string | null;
      reason: { en: string; ar: string } | null;
    }>;
    missing: Array<{
      factKey: string;
      blocks: string[];
      sources: Array<{
        id: string;
        name: { en: string; ar: string | null };
        transport: string;
      }>;
    }>;
    evidence: { needed: number; present: number; missing: number };
    nextAction:
      | 'fetch_evidence'
      | 'request_from_citizen'
      | 'review_conflicts'
      | 'ready_for_committee'
      | 'record_decision'
      | 'blocked';
    income: {
      declaredTotalAed: number | null;
      corroboratedTotalAed: number | null;
      employments: Array<{
        employer: string | null;
        jobTitle: string | null;
        declaredMonthlyAed: number | null;
        readings: Array<{
          sourceId: string;
          sourceName: string;
          monthlyIncomeAed: number | null;
          observedAt: string;
          stale: boolean;
        }>;
        /** `uncorroborated` is the common case and is not a discrepancy. */
        status: 'agreed' | 'disputed' | 'uncorroborated';
        varianceAed: number | null;
      }>;
      sourcesAnswering: string[];
      sourcesSilent: string[];
      band: 'grant' | 'loan' | 'unknown';
      crossesThreshold: { threshold: number; article: string; label: string } | null;
    };
    articles: Array<{
      article: number | null;
      clause: string;
      title: string | null;
      outcomes: HousingConsoleRuleOutcome[];
      met: number;
      failed: number;
      notEstablished: number;
      waived: number;
    }>;
    /**
     * The case in prose, assembled from the values above and from nothing else.
     *
     * Present on the first render — no model and no second request — so the case
     * can lead with a reading of itself rather than with a table an officer has to
     * interpret. `detail` is empty when there is nothing true to add; a narrative
     * padded to a fixed length is the same lie the empty states were built to stop.
     *
     * Sums are the canonical `AED 15,000`; the dirham sign is substituted when this
     * is rendered, so render it through `Markdown` rather than as bare text.
     */
    assessment: {
      headline: { en: string; ar: string };
      detail: Array<{ en: string; ar: string }>;
    };
  };
}

export interface HousingDesignServices {
  services: Array<{
    slug: string;
    smartServicesId: string;
    serviceCode: string | null;
    contentIds: { en: string; ar: string };
    name: { en: string | null; ar: string | null };
    serviceType: string | null;
    slaWorkingDays: number | null;
    fees: Array<{ kind: string; amountAed: number; label: { en: string | null; ar: string | null } }>;
    partners: string[];
    terms: Array<{ en: string | null; ar: string | null }>;
    suspended?: { observed: boolean; text: string };
    guide: { en: unknown | null; ar: unknown | null };
    formExtracted: boolean;
  }>;
  referenceDataRegister: { why: string; entities: string[] };
  absentFromPortal: Array<{ slug: string; smartServicesId: string; why: string }>;
  observedOnPortalNotInCatalogue: Array<{ name: string; where: string; why: string }>;
  awaitingFormExtraction: Array<{ slug: string; screenshots: number }>;
  capture: { status: string; pending: Array<{ field: string; why: string }>; notes: string[] };
}

export interface HousingDesignRulesets {
  rulesets: Array<{
    slug: string;
    name: { en: string; ar: string };
    serviceSlug: string;
    version: number;
    rules: Array<{
      key: string;
      kind: string;
      ordinal: number;
      authority: string;
      requirement: unknown;
      failVerdict: string;
      severity: string;
      autoDecide: boolean;
      ministryTermId?: string;
      ministryText?: { en: string; ar: string | null };
      articleClause?: string;
      reason: { en: string; ar: string };
    }>;
  }>;
  exceptions: Record<string, unknown[]>;
  /** Published conditions no rule implements yet. Shown, not hidden. */
  uncoveredTerms: Array<{ termId: string; en: string }>;
  articleLimits: Record<string, number>;
}

export interface HousingDesignMatrix {
  capture: { status: string; pending: Array<{ field: string; why: string }>; notes: string[] };
  genders: Array<{ id: string; en: string; ar: string | null }>;
  emirates: Array<{ id: string; en: string; ar: string | null }>;
  maritalStatusesByGender: Record<string, Array<{ id: string; en: string; ar: string | null }>>;
  categoriesByGenderAndMarital: Record<string, Array<{ id: string; en: string; ar: string | null }>>;
  triples: Array<{
    gender: { id: string; en: string };
    maritalStatus: { id: string; en: string };
    category: { id: string; en: string };
  }>;
  documentCatalogue: Array<{ id: string; en: string; ar: string | null; combinations: number }>;
  termCatalogue: Array<{
    id: string;
    en: string;
    ar: string | null;
    combinations: number;
    enTruncated?: boolean;
  }>;
  combinations: Array<{
    key: string;
    documentCount: number;
    termCount: number;
    documentIds?: string[];
  }>;
}

export interface HousingDesignIntegrations {
  sources: HousingConsoleSource[];
  referenceData: HousingConsoleSource[];
  waivers: {
    waivable: Array<{ documentId: string; sourceIds: string[] }>;
    liveWaivable: Array<{ documentId: string; sourceIds: string[] }>;
  };
  /**
   * Stated on the wire, so a green board cannot imply access we do not have.
   * Bilingual because the console renders it verbatim, and an English caveat on
   * an Arabic page is a caveat that does not caveat.
   */
  posture: { en: string; ar: string };
}

// --- Manzali space -----------------------------------------------------------

/**
 * Every board, in journey order.
 *
 * This list and `SPACE_BOARDS` in `platform/src/housing/space/types.ts` are the
 * same list twice, because the platform and the portal deploy separately. The
 * server decides which are unlocked; this decides the order they are drawn in.
 */
export const MANZALI_BOARDS = [
  'status',
  'documents',
  'approval',
  'objection',
  'finance',
  'financing',
  'design',
  'contractor',
  'utilities',
  'decoration',
  'vault',
  'timeline',
] as const;
export type ManzaliBoard = (typeof MANZALI_BOARDS)[number];

export type ManzaliProvenance = 'observed' | 'published' | 'simulated';

/**
 * A value with where it came from.
 *
 * `observed` means a Ministry read returned it for this applicant; `published`
 * means the Ministry's published rules say it of anyone in this combination; and
 * `simulated` means mock mode invented it. The distinction is rendered, not just
 * recorded — a demo that can be mistaken for real data is worse than no demo.
 */
export interface ManzaliSourced<T> {
  value: T;
  provenance: ManzaliProvenance;
  source: string;
  observedAt?: string;
  validUntil?: string;
}

export type ManzaliCustodian =
  | 'citizen'
  | 'ministry_intake'
  | 'study_team'
  | 'committee'
  | 'bank'
  | 'consultant'
  | 'contractor'
  | 'utility'
  | 'complete';

export interface ManzaliEligibilityDecision {
  outcome: 'approved_to_proceed' | 'refused' | 'needs_committee';
  decidedAt: string;
  recommendedBy: string;
  /** Null while this is still a recommendation awaiting a human. */
  confirmedBy: string | null;
  confirmedAt: string | null;
  checks: Array<{
    id: string;
    outcome: 'pass' | 'fail' | 'indeterminate';
    detail: Localized;
    provenance: ManzaliProvenance;
  }>;
  alternativeServiceSlug?: string;
}

/**
 * The application the citizen has not filed yet.
 *
 * Null once filed and null before there is one. `total` counts the questions the
 * engine will actually ask after the journey model has hidden what the Ministry
 * answered, not the rows in a spec — so "four left" is four questions.
 */
export interface ManzaliIntakeProgress {
  applicationId: string;
  /** A record locator. Never a Ministry number. */
  reference: string;
  state: string;
  answered: number;
  total: number;
  nextLabel: Localized | null;
  documentsOutstanding: number;
  submittedAt: string | null;
}

export interface ManzaliDocument {
  requirementId: string;
  name: Localized;
  /** True when this citizen's own answers make it necessary. */
  required: boolean;
  attached: boolean;
  attachedAt: string | null;
  filename: string | null;
  /** Fetched by the Ministry rather than asked for. */
  electronicallyLinked: boolean;
}

export interface ManzaliDocumentsView {
  applicationId: string | null;
  reference: string | null;
  documents: ManzaliDocument[];
  /** How many the rules require. Frequently zero, which is an answer. */
  requiredByRules: number;
  outstanding: number;
  requirementBasis: Localized;
  editable: boolean;
}

export interface ManzaliVaultItem {
  id: string;
  kind: 'decision' | 'document' | 'concept' | 'brief';
  title: Localized;
  /** A signed path. Null when there is nothing to download. */
  url: string | null;
  mime: string | null;
  createdAt: string;
  simulated: boolean;
  issuedBy: Localized;
}

export interface ManzaliConsultant {
  id: string;
  name: string;
  emirate: string | null;
  [key: string]: unknown;
}

/**
 * The financing contract as the citizen's end of it.
 *
 * Loosely typed on the row itself, because `financingContractProjection` is
 * shared with the staff desk and the desk knows more about a contract than a
 * citizen needs to. `status` is the field both surfaces branch on.
 */
export interface ManzaliFinancingContract {
  id: string;
  status: string;
  assistanceType?: string | null;
  projectKind?: string | null;
  qararReference?: string | null;
  [key: string]: unknown;
}

export interface ManzaliSpaceStatus {
  custodian: ManzaliCustodian;
  custodianLabel: Localized;
  stage: Localized;
  stageCode: string | null;
  nextSteps: Array<{ text: Localized; citizenAction: boolean }>;
  /** The unfiled application, while there is one. */
  intake: ManzaliIntakeProgress | null;
  /** The same-day gate. Independent of the substantive decision. */
  eligibility: ManzaliEligibilityDecision | null;
  /** The Ministry's own applicant score, not one of ours. */
  completeness: ManzaliSourced<number> | null;
  priorityIndex: ManzaliSourced<number> | null;
  applicationNo: ManzaliSourced<string> | null;
  filedAt: ManzaliSourced<string> | null;
  checkedAt: string;
  panicSignal: {
    reason: string;
    since: string;
    preempt: Localized;
  } | null;
  boards: ManzaliBoard[];
}

/**
 * A ministry call the space composed and did not send.
 *
 * `endpoint: null` is a finding rather than a gap: it means MOEI's own endpoint
 * list names nothing for this operation. `dispatchable` is false on every write,
 * and is a field rather than an assumption so the day a write contract arrives the
 * difference is data.
 */
export interface ManzaliProjectedCall {
  direction: 'read' | 'write';
  operation: string;
  method: string | null;
  endpoint: string | null;
  body: unknown;
  /** Fields the ministry's portal collects that we cannot fill, each with why. */
  unmappable: Array<{ field: string; why: string }>;
  blockers: Array<{ id: string; detail: string }>;
  dispatchable: boolean;
  outcome: string | null;
  provenance: unknown;
  createdAt: string;
}

export interface ManzaliDrawdown {
  amountFils: number;
  /** Where the money actually is, not the word "pending". */
  state: 'claimed' | 'voucher_raised' | 'with_finance' | 'paid';
  milestonePercent: number | null;
  occurredAt: string;
  provenance: ManzaliProvenance;
  label: Localized | null;
}

/**
 * Every figure is nullable, and a null means "not known" rather than zero.
 *
 * That distinction is the point of the board. The ministry's own payment pane
 * renders `AED NaN` on an empty file; this returns null and the surface says it does
 * not know.
 */
export interface ManzaliFinanceSummary {
  award: ManzaliSourced<number> | null;
  facility: ManzaliSourced<number> | null;
  ownFunds: ManzaliSourced<number> | null;
  disbursed: ManzaliSourced<number> | null;
  remaining: ManzaliSourced<number> | null;
  paid: ManzaliSourced<number> | null;
  completionPercent: ManzaliSourced<number> | null;
  drawdowns: ManzaliDrawdown[];
  anySimulated: boolean;
}

export interface ManzaliEstimateLine {
  itemKey: string;
  label: Localized;
  unit: string;
  quantity: number;
  rateFils: number;
  amountFils: number;
  /** So a figure can be followed to its origin. Required on every line. */
  sourceUrl: string;
  sourceLabel: string;
  validUntil: string;
}

export interface ManzaliDesignEstimate {
  grossFloorAreaSqm: number;
  finishLevel: 'basic' | 'standard' | 'premium';
  lines: ManzaliEstimateLine[];
  totalFils: number;
  /** Items with no rate in the basis. Reported, never guessed. */
  uncosted: string[];
  awardFils: number | null;
  overrunFils: number | null;
  ratesValidUntil: string | null;
  /** True once a rate used has expired. Shown, not silently re-costed. */
  ratesStale: boolean;
}

/**
 * The three studios, in the order they are useful.
 *
 * Plan first, because the exterior of a house whose layout has not been agreed is
 * decoration, and the interior last for the same reason again.
 */
export const MANZALI_STUDIOS = ['blueprint', 'exterior', 'interior'] as const;
export type ManzaliStudio = (typeof MANZALI_STUDIOS)[number];

export interface ManzaliConcept {
  id: string;
  studio: ManzaliStudio;
  /** A signed, expiring path. A citizen's floor plan is not a public object. */
  url: string;
  mime: string;
  provider: string;
  model: string | null;
  /**
   * True when the deterministic fixture answered rather than a model.
   *
   * Rendered, not merely recorded: a fixture presented as a generated design is the
   * same category of lie as a simulated figure presented as a Ministry one.
   */
  simulated: boolean;
  prompt: string;
  createdAt: string;
}

export interface ManzaliMailThread {
  id: string;
  purpose: string;
  /** What the citizen is told. Not necessarily where the mail went. */
  counterpartyLabel: string;
  state: 'drafting' | 'sent' | 'awaiting_reply' | 'replied' | 'resolved' | 'failed';
  subject: string;
}

export interface ManzaliMailMessage {
  direction: 'outbound' | 'inbound';
  subject: string | null;
  bodyText: string | null;
  receivedVia: 'webhook' | 'imap' | 'staff' | null;
  /** What a model made of a reply. Always shown beside the original, never instead. */
  interpretation: unknown;
  sentAt: string | null;
  receivedAt: string | null;
}

export interface ManzaliFurnitureOffer {
  retailer: string;
  title: string;
  productUrl: string;
  priceFils: number | null;
  wasPriceFils: number | null;
  currency: string;
  observedAt: string;
  validUntil: string;
  snapshotKey: string | null;
  /** Signed, expiring path to the product photograph. Null when there is none. */
  imageUrl: string | null;
  /** What the page said about stock. Advisory: retailers are often wrong. */
  availability: 'in_stock' | 'out_of_stock' | 'preorder' | 'check_retailer' | null;
  brand: string | null;
  /**
   * The end date the shop itself stated.
   *
   * Only present when it was sooner than our own claim window. A retailer setting
   * a validity a year out is a template default, not information, and announcing
   * one as a sale end date would be inventing a deadline.
   */
  promoEndsAt: string | null;
  /** True when this came from the sample catalogue rather than a real shop. */
  simulated: boolean;
  shortlisted: boolean;
  /** True once the price should no longer be presented as current. */
  stale: boolean;
}

/** A live harvest: a real browser opening shops, one page at a time. */
export interface ManzaliFurnitureRun {
  id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled' | 'timed_out';
  phase: string;
  /** True when the sample provider answered, so the board can say so. */
  simulated: boolean;
  rooms: string[];
  retailersPlanned: number;
  retailersVisited: number;
  pagesVisited: number;
  offersFound: number;
  startedAt: string | null;
  finishedAt: string | null;
}

export interface ManzaliFurnitureRunEvent {
  id: string;
  kind: 'phase' | 'retailer' | 'offer' | 'skipped' | 'error';
  message: Localized;
  data: Record<string, unknown>;
  occurredAt: string;
}

export interface ManzaliShortlistTotal {
  totalFils: number;
  /** Counted separately rather than as zero, so a total is never quietly short. */
  itemsWithoutPrice: number;
  overBudgetFils: number | null;
  staleCount: number;
}

export interface ManzaliContractor {
  id: string;
  name: Localized;
  capacity: 'available' | 'limited' | 'full';
  availableInMonths: number;
  deliveryScore: number;
  score: number;
  /**
   * True when this came from a seeded list rather than a Ministry register.
   *
   * Rendered. MOEI's contractor reads are all officer-side, so no citizen-facing
   * certified register exists in the 91 endpoints — presenting a seeded list as the
   * register is the one claim this board has no basis for.
   */
  simulated: boolean;
  sourceUrl: string;
  /**
   * `code`, which is what the matcher emits and the API passes through unchanged.
   *
   * This said `id`, so the board keyed its list on a field that was never on the
   * wire: every reason got `key=""` and React warned about duplicate keys the first
   * time the contractor board was ever rendered. The type was describing a shape
   * nothing produced.
   */
  reasons: Array<{ code: string; label: Localized; value?: string | number | boolean }>;
}

export interface ManzaliSiteCapture {
  capturedAt: string;
  source: 'drone' | 'site_visit' | 'citizen' | 'simulated';
  objectKey: string;
  changeSummary: unknown;
}

export interface ManzaliBankBranch {
  bankKey: string;
  bankName: Localized;
  branchName: Localized;
  emirate: string;
  address: Localized | null;
  hours: Localized | null;
  phone: string | null;
  /** Which housing products this branch handles. Empty means unknown. */
  services: string[];
  /** Kilometres, and only when both ends are real coordinates. Never estimated. */
  distanceKm: number | null;
  source: string;
}

export interface ManzaliSpaceView {
  spaceKey: string;
  state: 'invited' | 'joined' | 'active' | 'dormant';
  emirate: string | null;
  product: 'loan' | 'grant' | 'housing_finance' | null;
  preferredChannel: 'webchat' | 'whatsapp' | 'email' | 'app_widget' | 'site_widget';
  layout: {
    chatPercent?: number;
    collapsed?: 'chat' | 'board' | null;
    lastBoard?: ManzaliBoard;
  };
  rehearsal: boolean;
  /** True when any rendered value was simulated. Drives the surface banner. */
  mock: boolean;
  conversationId: string | null;
  caseReference: string | null;
  bundleId: string | null;
  status: ManzaliSpaceStatus;
  profile: Record<string, ManzaliSourced<unknown>>;
  invitedAt: string;
  joinedAt: string | null;
}

export interface HousingBundleSummary {
  id: string;
  application_id: string;
  application_reference: string;
  customer_id: string;
  project_type: 'self_build' | 'completion' | 'extension' | 'renovation';
  phase: HousingPhase;
  preferences: Record<string, unknown>;
  document_validation_gate: {
    status: 'submitted' | 'approved' | 'changes_requested';
    comment?: Localized;
    submittedAt?: string;
    staffId?: string;
    decidedAt?: string;
    simulated?: boolean;
  } | null;
  consultant_gate: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  concierge_conversation_id: string | null;
  concierge_started_at: string | null;
  relationship_status: 'ongoing' | 'paused' | 'closed_by_citizen';
  whatsapp_conversation_id: string | null;
  last_customer_touch_at: string | null;
}

export interface HousingDocumentChecklistItem {
  requirementId: string;
  name: Localized;
  description: Localized;
  acceptedMime: string[];
  humanReviewRequired: boolean;
  documentId: string | null;
  filename: string | null;
  mime: string | null;
  url: string | null;
  vaultStatus: string | null;
  confidence: number | null;
  validation: {
    status?: 'auto_validated' | 'review_required' | 'accepted' | 'changes_requested';
    needsHumanReview?: boolean;
    extractedFields?: string[];
    checks?: Record<string, unknown>;
  } | null;
  attachedAt: string | null;
}

export interface HousingMatch {
  id: string;
  score: number;
  partner: {
    id: string;
    bank?: Localized;
    name?: Localized;
    sourceUrl?: string;
    [key: string]: unknown;
  };
  reasons: { code: string; label: Localized; value?: string | number | boolean }[];
  sourceUrl: string;
  observedAt: string;
  indicative: true;
  simulated: boolean;
}

export interface HousingDesignAsset {
  id: string;
  kind: 'upload' | 'generated';
  studio: 'exterior' | 'interior';
  room: string | null;
  version: number;
  mime: string;
  metadata: Record<string, unknown>;
  purpose: 'official' | 'inspiration';
  intent: HousingVisualIntent | null;
  favorite: boolean;
  savedAt: string | null;
  selectedAt: string | null;
  createdAt: string;
  url: string;
}

export interface HousingInspirationAsset {
  id: string;
  applicationId: string;
  bundleId: string | null;
  intent: HousingVisualIntent;
  studio: 'exterior' | 'interior';
  room: string | null;
  favorite: boolean;
  savedAt: string | null;
  createdAt: string;
  metadata: Record<string, unknown>;
  url: string;
}

export interface HousingFurnitureProduct {
  id: string;
  title: Localized;
  productUrl: string;
  imageUrl: string | null;
  priceFils: number | null;
  dimensions: string | null;
  style: string[];
  room: string;
  availability: 'in_stock' | 'out_of_stock' | 'check_retailer';
  retailer: Localized;
  observedAt: string;
  sourceMode: 'seeded' | 'feed' | 'grounded_search';
}

/**
 * The palettes a citizen can paint their project workspace in.
 *
 * Named for places rather than colours, because the citizen is choosing a
 * feeling and the hex values are the portal's business. Every one resolves to
 * DLS palette steps.
 */
export const HOUSING_PROJECT_THEMES = ['dune', 'oasis', 'pearl', 'majlis', 'coast'] as const;
export type HousingProjectTheme = (typeof HOUSING_PROJECT_THEMES)[number];

export interface HousingBundleDetail {
  bundle: HousingBundleSummary;
  events: Record<string, unknown>[];
  matchSnapshots: {
    id: string;
    kind: 'bank' | 'contractor';
    input: Record<string, unknown>;
    items: HousingMatch[];
    source_metadata: Record<string, unknown>;
    created_at: string;
  }[];
  selections: {
    kind: 'bank' | 'contractor';
    partner_id: string;
    snapshot_id: string;
    selected_at: string;
  }[];
  assets: HousingDesignAsset[];
  designJobs: Record<string, unknown>[];
  furnitureSearches: {
    id: string;
    room: string;
    budget_fils: number;
    items: HousingFurnitureProduct[];
    source_metadata: Record<string, unknown>;
  }[];
  furnitureShortlist: {
    id: string;
    search_id: string;
    product: HousingFurnitureProduct;
    shortlisted_at: string;
  }[];
  consents: {
    id: string;
    scope: string;
    purpose: Localized;
    granted_at: string;
    revoked_at: string | null;
  }[];
  stepEvidence: HousingStepEvidence[];
  documents: HousingDocumentChecklistItem[];
  documentPolicy: {
    status: 'reviewer_controlled';
    officialChecklistClaimed: false;
  };
  continuity: {
    relationshipStatus: 'ongoing' | 'paused' | 'closed_by_citizen';
    primaryChannel: 'whatsapp';
    canReturnAnytime: boolean;
    workspaceUrl: string;
    whatsappResumeUrl: string;
  };
  labels: Record<string, string>;
}

export type HousingIdeaTopic =
  | 'exterior'
  | 'interior'
  | 'furniture'
  | 'finance'
  | 'timeline'
  | 'general';

export interface HousingIdea {
  id: string;
  title: Localized;
  summary: Localized;
  promptSeed: Localized;
  tags: string[];
  palette: string[];
  budgetHintFils: number | null;
}

export type HousingDraftKind =
  | 'contractor_brief'
  | 'consultant_note'
  | 'furniture_brief'
  | 'bank_message';

export interface HousingDraft {
  title: Localized;
  body: Localized;
}

export interface HousingBankMessageDraft {
  en: string;
  ar: string;
  subject: { en: string; ar: string };
}

export interface HousingConsultant {
  id: string;
  legal_name: Localized;
  trade_licence: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  emirates: string[];
  status: string;
  rating: number | null;
}

export interface HousingFinancingContractView {
  id: string;
  status: string;
  assistanceType: 'grant' | 'loan';
  projectKind: string;
  qararReference: string;
  fee: {
    amountAed: number;
    status: 'pending' | 'paid';
    paidAt: string | null;
  };
  openedBy: 'consultant' | 'beneficiary' | null;
  openedAt: string | null;
  amounts: {
    qararAmountAed?: number;
    bankFacilityAed?: number | null;
    ownFundsAed?: number;
    totalProjectAed?: number;
  };
  contract: {
    templateId?: string;
    version?: string;
    documentId?: string;
  };
  signatures: Array<{
    role: 'owner' | 'contractor' | 'consultant';
    signerId: string;
    signedAt: string;
    documentHash: string;
  }>;
  ministryDecision: {
    outcome: 'approved' | 'rejected';
    reasons: Localized[];
    decidedAt: string;
  } | null;
  bankFile: Record<string, unknown> | null;
  engagementId: string | null;
  updatedAt: string;
}

/** Operational toggles for the Manzili housing bundle (admin settings surface). */
export interface HousingSettings {
  bundleEnabled: boolean;
  aiIdeasEnabled: boolean;
  aiDraftsEnabled: boolean;
  designGenerationEnabled: boolean;
  /** 'env' defers to the deployment's HOUSING_IMAGE_PROVIDER. */
  imageProvider: 'env' | 'openrouter' | 'fixture';
  aiRateLimitPerWindow: number;
  designRateLimitPerWindow: number;
  fieldResearchRequired: boolean;
  financingContractEnabled: boolean;
  qararPdfEnabled: boolean;
}

export type HousingSettingKey = keyof HousingSettings;

export interface HousingSettingsState {
  settings: HousingSettings;
  sources: Record<HousingSettingKey, 'override' | 'env'>;
}

export interface HousingAiRateLimitUsage {
  ai: { used: number; limit: number; remaining: number; resetInSec: number };
  design: { used: number; limit: number; remaining: number; resetInSec: number };
}

/** Cross-bundle operational snapshot for the housing ops dashboard. */
export interface HousingOpsOverview {
  bundlesByPhase: { phase: string; count: number }[];
  eventsByKind24h: { event: string; count: number }[];
  ai: {
    ideas24h: number;
    drafts24h: number;
    designJobs24h: number;
    simulatedIdeas24h: number;
    simulatedDrafts24h: number;
    models: { model: string; count: number }[];
  };
  design: {
    queued: number;
    running: number;
    completed: number;
    failed: number;
    inspiration: number;
    applicationScoped: number;
  };
  whatsapp: { boundConversations: number; media24h: number };
}

export type HousingVisitKind = 'social' | 'technical';
export type HousingVisitStatus =
  | 'not_started'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'waived';

export interface HousingFieldVisitView {
  id: string;
  kind: HousingVisitKind;
  status: HousingVisitStatus;
  assignedTo: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  waivedAt: string | null;
  waiveReason: Localized | null;
  summary: Localized | null;
  checklist: Record<string, unknown> | null;
}

export interface FieldResearchQueueItem {
  applicationId: string;
  reference: string;
  customerId: string;
  currentStageId: string;
  priorityScore: number | null;
  emirate: string | null;
  social: HousingFieldVisitView | null;
  technical: HousingFieldVisitView | null;
  readyForCommittee: boolean;
  updatedAt: string;
}

export interface FieldResearchKpis {
  open: number;
  readyForCommittee: number;
  socialPending: number;
  technicalPending: number;
}

/**
 * Review-order explanation. Named here because the same shape appears on the
 * intake preview, the case projection and the field-research hub, and all three
 * used to declare it inline.
 */
export interface HousingPriorityView {
  score: number;
  band: 'low' | 'medium' | 'high' | 'urgent';
  factors: Array<{ id: string; label: Localized; points: number }>;
  summary: Localized;
}

/** The intake priority preview adds the "what is still unclaimed" hint. */
export interface HousingPriorityPreview extends HousingPriorityView {
  hint: Localized | null;
}

/** Pre-apply verdict from POST /v1/housing/eligibility/preview. Persists nothing. */
export interface HousingEligibilityPreview {
  icp: HousingIcpEligibilityView | null;
  assistance: HousingAssistanceAmountView | null;
  priority: HousingPriorityPreview;
  hardFail: boolean;
  icpUnavailableReason: Localized | null;
}

/** Bilingual visit summary drafted from the checklist, with its provenance. */
export interface HousingVisitSummaryDraft {
  draft: { summary: Localized; basis: Localized[] };
  simulated: boolean;
  model: string | null;
}

/** One row of the ministry financing desk. */
export interface HousingFinancingContractDeskRow extends HousingFinancingContractView {
  applicationReference: string;
  customerId: string;
  applicantName: string | null;
  emirate: string | null;
  consultantName: Localized | null;
  waitingDays: number;
}

export interface FieldResearchCaseHub {
  applicationId: string;
  reference: string;
  customerId: string;
  fields: Record<string, unknown>;
  readyForCommittee: boolean;
  housingCase: {
    currentStageId: string;
    stages: Array<{
      id: string;
      label: Localized;
      status: 'pending' | 'in_progress' | 'completed';
      completedAt: string | null;
      notes: Localized | null;
    }>;
    research: {
      callCompleted: boolean;
      visitCompleted: boolean;
      socialStudyNotes: Localized | null;
      deskCall: { status: string; completedAt: string | null; notes: Localized | null };
      socialVisit: HousingFieldVisitView;
      technicalVisit: HousingFieldVisitView;
      updatedAt: string;
    } | null;
    readyForCommittee: boolean;
    committee: {
      outcome: 'approved' | 'rejected';
      assistanceType: 'grant' | 'loan' | null;
      amountAed: number | null;
      reasons: Localized[];
      decidedAt: string;
    } | null;
    priority?: HousingPriorityView | null;
    suggestion?: HousingCommitteeSuggestion | null;
    simulated: boolean;
    updatedAt: string;
  } | null;
}

/** Full operational trace of one housing bundle (events, design jobs, AI
 *  usage, rate-limit window, counts and hash-chained audit entries). */
export interface HousingBundleTrace {
  bundle: HousingBundleSummary;
  events: Record<string, unknown>[];
  designJobs: {
    id: string;
    studio: string;
    room: string | null;
    status: string;
    provider: string;
    model: string;
    simulated: boolean;
    idempotencyKey: string;
    error: string | null;
    createdAt: string;
    assetCount: number;
    purpose: 'official' | 'inspiration';
    intent: HousingVisualIntent | null;
    applicationId: string;
  }[];
  aiUsage: {
    ideas: { total: number; live: number; simulated: number };
    drafts: { total: number; live: number; simulated: number };
    designJobs: {
      total: number;
      queued: number;
      running: number;
      completed: number;
      failed: number;
    };
  };
  rateLimit: HousingAiRateLimitUsage;
  counts: {
    documents: number;
    uploads: number;
    concepts: number;
    evidence: number;
    shortlist: number;
  };
  audit: { entries: Record<string, unknown>[]; chainOk: boolean };
}

export interface ChatEvents {
  onToken?: (text: string) => void;
  onMessage?: (message: { role: string; text: string; widgets: Widget[]; agent?: string }) => void;
  onApplication?: (projection: ApplicationProjection) => void;
  onOrchestration?: (trace: OrchestrationTrace) => void;
  /** The language this turn was answered in; arrives before the first token. */
  onLanguage?: (lang: Lang) => void;
  onHandover?: () => void;
}

export interface TurnResult {
  replies: EngineReply[];
  /**
   * The language the engine answered in, which is the conversation's language
   * from here on. It is not necessarily the one the turn was sent in: the
   * engine switches when the citizen writes in the other language.
   *
   * Null when the platform did not say — an older build, or a path that does
   * not answer. A caller keeps the language it already holds rather than
   * guessing one, because guessing is what puts a citizen in the wrong one.
   */
  lang: Lang | null;
  application: ApplicationProjection | null;
  handover: boolean;
  orchestration: OrchestrationTrace | null;
}

/** A turn that arrived as speech. */
export interface VoiceTurnResult extends TurnResult {
  /**
   * What the platform heard.
   *
   * Shown, not just used. At Gulf-dialect recognition rates a citizen has to be
   * able to catch a misrecognition before it becomes a wrong application, and
   * the only way to catch it is to read it back.
   */
  transcript?: string;
  /** The reply as audio, when this surface can be spoken to. */
  audioUrl?: string;
  /** The stored copy of the citizen's own recording, for a later reload. */
  noteUrl?: string;
  /**
   * Nothing intelligible was heard.
   *
   * Not an error: they spoke and it did not come through, which is a thing to
   * say to them rather than a failure to report. `replies` is empty.
   */
  notHeard?: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

/**
 * Every error code that means "this browser is no longer signed in".
 *
 * Listed once, here, because the alternative is each caller matching a subset.
 * They arrive from three different guards — the citizen credential, the
 * conversation session, and the conversation session's owning credential — and
 * a client that reacted to one and ignored the others would keep a signed-out
 * citizen looking signed in until they happened to press the right button.
 *
 * `chat.session_expired` is the ordinary way a conversation ends and it was the
 * one missing. A session that simply runs out its own clock never reaches the
 * revocation branch in `api/middleware.ts`: the lookup requires
 * `expires_at > now()`, finds nothing, and answers `chat.session_expired`.
 * Absent from this list it was not a session ending at all, so the live-handover
 * loop treated the refusal as a dropped connection and reopened it every ten
 * seconds for as long as the tab stayed open. Measured in production on 19
 * August: 420 of 623 `/live` requests in half an hour were 401s from three
 * abandoned tabs, two of them the same citizen's, one from the previous day.
 *
 * `chat-session-ended.spec.ts` passed throughout, because it refuses with
 * `chat.session_revoked` — a code that was on the list. Both are covered now.
 */
export const SESSION_ENDED_CODES: readonly string[] = [
  'citizen.token_invalid',
  'citizen.session_expired',
  'chat.session_expired',
  'chat.session_revoked',
];

/** Whether an error says the citizen has to sign in again. */
export function isSessionEnded(error: unknown): boolean {
  return error instanceof ApiError && SESSION_ENDED_CODES.includes(error.code);
}

async function parseError(res: Response): Promise<never> {
  let code = 'http.error';
  let message = res.statusText;
  let details: unknown;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
    };
    code = body.error?.code ?? code;
    message = body.error?.message ?? message;
    details = body.error?.details;
  } catch {
    /* keep defaults */
  }
  throw new ApiError(res.status, code, message, details);
}

/** Transient upstream statuses worth retrying — typical during rolling deploys. */
const RETRIABLE_STATUS = new Set([502, 503, 504]);
const MAX_ATTEMPTS = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Exponential backoff with jitter: ~300ms, ~700ms, … */
function backoffMs(attempt: number): number {
  return 300 * 2 ** (attempt - 1) + Math.floor(Math.random() * 150);
}

/**
 * A registered website embed, as the console lists them.
 *
 * Deliberately carries no credential. `hasHandoffSecret` is a boolean rather
 * than the secret because the console needs to show whether the ministry's site
 * can sign a sign-in handoff, and knowing that does not require holding the key.
 */
export interface EmbedKeySummary {
  id: string;
  label: string;
  origins: string[];
  allowed_services: string[];
  has_handoff_secret: boolean;
  created_at: string;
  created_by_name: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

/** The one response that carries the material. Show it; it is not returned twice. */
export interface MintedEmbedKey {
  id: string;
  label: string;
  key: string;
  handoffSecret?: string;
}

/**
 * One traced run — a conversation, a scripted journey, or a worker job.
 *
 * `integrationBackend` is the field that decides whether the run proved
 * anything: a journey that passed end to end against the mock has demonstrated
 * that the mock agrees with itself.
 */
export interface TraceSessionSummary {
  id: string;
  conversation_id: string | null;
  customer_id: string | null;
  channel: string;
  actor_kind: string;
  actor_ref: string | null;
  integration_backend: string | null;
  turn_count: number;
  llm_call_count: number;
  backend_call_count: number;
  error_count: number;
  handover_count: number;
  cost_fils_total: string;
  services_touched: string[];
  application_refs: string[];
  started_at: string;
  last_activity_at: string;
}

export interface TraceTurnSummary {
  id: string;
  trace_id: string;
  seq: number;
  service_slug: string | null;
  application_ref: string | null;
  input_kind: string | null;
  state_before: string | null;
  state_after: string | null;
  reply_lang: string | null;
  handover: boolean;
  llm_call_count: number;
  backend_call_count: number;
  cost_fils: string;
  latency_ms: number | null;
  error: string | null;
  created_at: string;
  guardrail: Record<string, unknown> | null;
  moderation: Record<string, unknown> | null;
  anomaly: Record<string, unknown> | null;
  /** Whether a payload exists, which is not the same as being allowed to see it. */
  has_input: boolean;
  has_plan: boolean;
  has_replies: boolean;
}

/**
 * A payload column as the API returns it.
 *
 * `{ withheld: true }` means a payload exists and was not asked for; `null`
 * means there was none. Distinct on purpose — reading a trace to decide whether
 * a call carried a body is exactly where conflating the two misleads.
 */
export type TraceBody = Record<string, unknown> | unknown[] | null;

export interface TraceTurnDetail {
  turn: Record<string, unknown> & {
    input: TraceBody;
    planner_plan: TraceBody;
    replies: TraceBody;
  };
  llmCalls: (Record<string, unknown> & { prompt: TraceBody; completion: TraceBody })[];
  backendCalls: (Record<string, unknown> & { request: TraceBody; response: TraceBody })[];
  revealed: boolean;
}

export class ServicesAiClient {
  constructor(private baseUrl: string) {}

  /**
   * A server-relative path an `<img>` or `<a>` can actually load.
   *
   * The platform hands back vault paths like
   * `/v1/vault/objects/<key>?exp=…&sig=…`, signed because the consumers — a
   * Twilio fetch, an image tag — cannot send an Authorization header. Relative is
   * right in production, where the portal and the API share an origin.
   *
   * In development they do not, and there is no proxy: a relative path resolves
   * against the portal, which answers every unknown path with its SPA shell. So
   * the Design Studio's concepts were `<img>` tags pointing at a page of HTML —
   * present in the DOM, `complete: true`, and `naturalWidth: 0`. The board looked
   * like it had no drawings rather than like it had broken ones.
   *
   * Absolute URLs are returned untouched, so a presigned S3 link is unaffected.
   */
  assetUrl(path: string): string {
    if (/^(?:[a-z]+:)?\/\//i.test(path) || path.startsWith('data:')) return path;
    return `${this.baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
  }

  /**
   * The citizen's durable identity, held here rather than passed to every call.
   *
   * It replaces the demo persona id that used to be threaded through ~30
   * methods. That was a customer UUID, which meant the credential and the
   * identifier were the same string and anyone holding one could read that
   * person's records. This is a bearer token verified against a stored hash.
   */
  private citizenToken: string | null = null;

  /**
   * Told when the platform says this browser is no longer signed in.
   *
   * One hook rather than a check at every call site. A signed-out citizen who
   * keeps seeing a signed-in shell until they happen to press the one button
   * that checks is the failure this closes — and the codes arrive from three
   * separate guards, so "handle it where it matters" means handling it in
   * dozens of places and forgetting some.
   *
   * The client does not clear its own token here. Deciding what a lapse means
   * for stored state is the application's business: the portal drops to
   * anonymous, the widget also tears down the conversation in its frame.
   */
  private onSessionEnded: ((code: string) => void) | null = null;

  setCitizenToken(token: string | null): void {
    this.citizenToken = token;
  }

  hasCitizenToken(): boolean {
    return Boolean(this.citizenToken);
  }

  /** Register the callback above. Passing null removes it. */
  onCitizenSessionEnded(listener: ((code: string) => void) | null): void {
    this.onSessionEnded = listener;
  }

  /** Citizen credential headers, empty when this browser has no identity yet. */
  private citizenHeaders(): Record<string, string> {
    return this.citizenToken ? { 'x-citizen-token': this.citizenToken } : {};
  }

  /**
   * Turn a refusal into the error to throw, announcing it first if it means the
   * citizen is no longer signed in.
   *
   * Every path out of this client goes through here rather than calling
   * `parseError` directly, and that is the point: the streaming turn, the
   * subscription and the multipart uploads each build their own request, so
   * "announce it in `request`" left the three surfaces a citizen actually spends
   * a conversation in as the three that stayed silent. A refused turn told the
   * portal nothing, so the shell went on looking signed in and the composer went
   * on inviting another go at a conversation the platform had ended.
   */
  private async fail(res: Response): Promise<never> {
    const error: unknown = await parseError(res).catch((cause: unknown) => cause);
    if (error instanceof ApiError && SESSION_ENDED_CODES.includes(error.code)) {
      this.onSessionEnded?.(error.code);
    }
    throw error;
  }

  /**
   * @param carriesAnswer Statuses whose body is the answer rather than an error
   *   envelope. A journey refusal is the case this exists for: the runner
   *   replies 409 with the report explaining that running it would file a real
   *   application, and that report is exactly what the caller needs to show.
   *   Parsing it as an error would keep the status and throw the reason away.
   */
  private async request<T>(
    path: string,
    init?: RequestInit,
    carriesAnswer?: readonly number[],
  ): Promise<T> {
    // Only auto-retry idempotent reads; retrying a POST/PUT could duplicate a
    // side effect. Reads failing mid-deploy is the common "empty page" cause.
    const method = (init?.method ?? 'GET').toUpperCase();
    const canRetry = method === 'GET' || method === 'HEAD';

    let lastError: unknown;
    for (let attempt = 1; attempt <= (canRetry ? MAX_ATTEMPTS : 1); attempt++) {
      try {
        const res = await fetch(`${this.baseUrl}${path}`, {
          ...init,
          headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
        });
        if (canRetry && attempt < MAX_ATTEMPTS && RETRIABLE_STATUS.has(res.status)) {
          await delay(backoffMs(attempt));
          continue;
        }
        // Announced before it is rethrown, so the application drops its
        // signed-in state on the first refusal rather than on whichever later
        // call the caller happened to write a handler for. See `fail`.
        if (!res.ok && !carriesAnswer?.includes(res.status)) await this.fail(res);
        return (await res.json()) as T;
      } catch (err) {
        lastError = err;
        // fetch() rejects with TypeError on network/connection failures — retry
        // those for reads too (dropped connections during a pod roll).
        const networkError = err instanceof TypeError;
        if (canRetry && attempt < MAX_ATTEMPTS && networkError) {
          await delay(backoffMs(attempt));
          continue;
        }
        if (networkError) {
          throw new ApiError(
            0,
            'network.unavailable',
            canRetry
              ? 'Could not reach ServicesAI after several attempts. Check your connection and try again.'
              : 'The connection closed before the action was confirmed. Refresh the page before trying again.',
          );
        }
        throw err;
      }
    }
    throw lastError;
  }

  private async streamJsonEvents<T>(
    path: string,
    headers: Record<string, string>,
    signal: AbortSignal,
    onEvent: (event: { id?: string; event: string; data: T }) => void,
  ): Promise<void> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      headers: { accept: 'text/event-stream', ...headers },
      signal,
    });
    // A subscription is the one caller that reconnects on failure, so a refusal
    // it does not understand is a refusal it asks for again on a backoff that
    // tops out at ten seconds, for as long as the tab is open. `fail` is what
    // lets it learn the difference.
    if (!res.ok) await this.fail(res);
    for await (const event of readSse(res)) {
      if (event.event === 'heartbeat') continue;
      onEvent({
        id: event.id,
        event: event.event,
        data: JSON.parse(event.data) as T,
      });
    }
  }

  // --- public catalog -------------------------------------------------------

  catalog(): Promise<{ services: CatalogEntry[]; categories: { slug: string; name: Localized; icon?: string }[] }> {
    return this.request('/v1/registry/services');
  }

  /**
   * Which services a typed sentence is asking for.
   *
   * Deliberately not routed through `request`. A 204 here means the search
   * assistance is not configured on this deployment, which is a real answer and
   * not an error — `request` would try to parse an empty body and throw. And
   * the caller aborts this on every keystroke, so it must be able to pass a
   * signal and see the rejection rather than a retry.
   *
   * @returns the matches, or null when this deployment cannot answer.
   */
  async searchServices(
    query: string,
    lang: Lang,
    signal?: AbortSignal,
  ): Promise<ServiceSearchMatch[] | null> {
    const res = await fetch(`${this.baseUrl}/v1/registry/search`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ query, lang }),
      signal,
    });
    if (res.status === 204) return null;
    if (!res.ok) throw new ApiError(res.status, 'registry.search_failed', 'search unavailable');
    const body = (await res.json()) as { matches?: ServiceSearchMatch[] };
    return body.matches ?? [];
  }

/** The catalog narrowed to this citizen, plus everything else still browsable. */
  catalogForCustomer(): Promise<CatalogForCustomer> {
    return this.request('/v1/registry/services/for-me', { headers: this.citizenHeaders() });
  }

  service(slug: string): Promise<PublicServiceDetail> {
    return this.request(`/v1/registry/services/${encodeURIComponent(slug)}`);
  }

  /** Published bundles, in the order the ministry chose. */
  bundles(): Promise<{ bundles: PublicBundle[] }> {
    return this.request('/v1/registry/bundles');
  }

  /**
   * One bundle with its member services already resolved.
   *
   * `previewToken` swaps the published spec for the draft, which is how the
   * console renders a live preview through the real page rather than a second
   * implementation of it.
   */
  bundle(slug: string, previewToken?: string): Promise<PublicBundle> {
    const suffix = previewToken ? `?preview=${encodeURIComponent(previewToken)}` : '';
    return this.request(`/v1/registry/bundles/${encodeURIComponent(slug)}${suffix}`);
  }

  /** Absolute URL for a staff-uploaded bundle image. */
  bundleMediaUrl(slug: string, mediaId: string): string {
    return `${this.baseUrl}/v1/registry/bundles/${encodeURIComponent(slug)}/media/${encodeURIComponent(mediaId)}`;
  }

  /**
   * Application references come from a sequence, so the access token from the
   * citizen's tracking link is what authorises the read. Without it the API
   * answers 404, identically to an unknown reference.
   */
  applicationStatus(reference: string, accessToken: string): Promise<ApplicationStatus> {
    return this.request(
      `/v1/applications/${encodeURIComponent(reference)}?t=${encodeURIComponent(accessToken)}`,
    );
  }

  /**
   * Where a Ministry application number has got to, without a tracking link.
   *
   * For the citizen who has the number MOEI gave them and not the link it was
   * sent with. Answered from the Ministry, so it resolves any application MOEI
   * holds — but only the status: the case file, with its documents and fees,
   * still needs the token above.
   *
   * A reference that is not a Ministry number is a 404 and never reaches MOEI.
   * An application MOEI does not hold resolves with `outcome: 'not_found'`,
   * which is a different thing from the request having failed.
   */
  trackMinistryApplication(reference: string): Promise<MinistryTrackedStatus> {
    return this.request(`/v1/applications/ministry/${encodeURIComponent(reference)}`);
  }

  /**
   * Ask the Ministry, then read.
   *
   * The only citizen path that reaches MOEI. Everything else shows the status
   * last recorded by the background sweep, which is right for drawing a page
   * and wrong for the moment a citizen taps refresh. Rate limited per
   * application; a refusal comes back as the stored status rather than an
   * error, so a caller never has to handle "your refresh was too eager" as a
   * failure.
   */
  refreshApplicationStatus(reference: string, accessToken: string): Promise<ApplicationStatus> {
    return this.request(
      `/v1/applications/${encodeURIComponent(reference)}/refresh?t=${encodeURIComponent(accessToken)}`,
      { method: 'POST' },
    );
  }

  /** Every application this citizen has filed, each with its Ministry status. */
  citizenApplications(): Promise<{ applications: ApplicationStatusSummary[] }> {
    return this.request('/v1/chat/applications', { headers: this.citizenHeaders() });
  }

  /**
   * Ask the Ministry about everything this citizen has open.
   *
   * Unforced is safe on every page load: an application checked inside its
   * cadence is not asked about again, so the usual cost is one query and no
   * Ministry traffic at all. Pass `force` only for an explicit refresh.
   */
  refreshCitizenApplications(force = false): Promise<{
    applications: ApplicationStatusSummary[];
    checked: number;
    overBudget: boolean;
    throttled: boolean;
  }> {
    return this.request('/v1/chat/applications/refresh', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ force }),
    });
  }

  journeyAction(token: string): Promise<JourneyActionContext> {
    return this.request(`/v1/journey-actions/${encodeURIComponent(token)}`);
  }

  /**
   * `emiratesId` is honoured by the sandbox provider only, and is how a caller
   * says which citizen they are standing in for. Live UAE Pass reads the number
   * from the verified claims and ignores anything sent here.
   */
  completeJourneyAuthentication(
    token: string,
    method: 'uae_pass' | 'existing_verification',
    emiratesId?: string,
  ): Promise<JourneyActionCompletion> {
    return this.request(
      `/v1/journey-actions/${encodeURIComponent(token)}/authenticate`,
      {
        method: 'POST',
        body: JSON.stringify({
          method,
          ...(emiratesId ? { emiratesId } : {}),
        }),
      },
    );
  }

  completeJourneyPayment(
    token: string,
    method: 'card' | 'apple_pay' | 'bypass',
    providerToken:
      | 'sandbox-card-visa'
      | 'sandbox-apple-pay'
      | 'sandbox-bypass',
  ): Promise<JourneyActionCompletion> {
    return this.request(`/v1/journey-actions/${encodeURIComponent(token)}/pay`, {
      method: 'POST',
      body: JSON.stringify({ method, providerToken }),
    });
  }

  /** Absolute URL of an official document PDF for direct download / new-tab view. */
  applicationDocumentUrl(
    reference: string,
    type: DocumentType,
    accessToken: string,
    download = false,
  ): string {
    const params = new URLSearchParams({ t: accessToken });
    if (download) params.set('download', '1');
    return `${this.baseUrl}/v1/applications/${encodeURIComponent(reference)}/documents/${type}?${params.toString()}`;
  }

  // --- Manzili housing bundle ----------------------------------------------
  //
  // No intake methods. Housing is applied for by conversation, over `/v1/chat`,
  // inside the Manzali space — the guided web form and its six endpoints are
  // gone, along with the second copy of every rule they enforced.

  housingVisuals(
    owner: { applicationId: string } | { bundleId: string },
  ): Promise<{ assets: HousingInspirationAsset[] }> {
    const query = 'applicationId' in owner
      ? `applicationId=${encodeURIComponent(owner.applicationId)}`
      : `bundleId=${encodeURIComponent(owner.bundleId)}`;
    return this.request(`/v1/housing/visuals?${query}`, {
      headers: this.citizenHeaders(),
    });
  }

  generateHousingInspiration(
    input: ({ applicationId: string } | { bundleId: string }) & {
      intent: HousingVisualIntent;
      room?: string;
      styles: string[];
      prompt?: string;
    },
  ): Promise<{
    jobId: string;
    status: 'completed';
    assets: HousingInspirationAsset[];
    simulated: boolean;
  }> {
    return this.request('/v1/housing/visuals/generate', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  setHousingInspirationFavorite(
    assetId: string,
    owner: { applicationId: string } | { bundleId: string },
    favorite: boolean,
  ): Promise<{ ok: true; favorite: boolean }> {
    return this.request(
      `/v1/housing/visuals/${encodeURIComponent(assetId)}/favorite`,
      {
        method: 'PATCH',
        headers: this.citizenHeaders(),
        body: JSON.stringify({ ...owner, favorite }),
      },
    );
  }

  housingBundles(): Promise<{ bundles: HousingBundleSummary[] }> {
    return this.request('/v1/housing/bundles', {
      headers: this.citizenHeaders(),
    });
  }

  // --- Manzali space --------------------------------------------------------

  /**
   * The citizen's housing space, in one read.
   *
   * Rejects with a 404 when this citizen has no space, which is the normal
   * answer rather than an error: a space exists because the Ministry invited
   * someone, and there is no create path.
   */
  manzaliSpace(): Promise<{ space: ManzaliSpaceView }> {
    return this.request('/v1/manzali/space', { headers: this.citizenHeaders() });
  }

  joinManzaliSpace(spaceKey: string): Promise<{ space: ManzaliSpaceView }> {
    return this.request(`/v1/manzali/spaces/${encodeURIComponent(spaceKey)}/join`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  /** Remember where the citizen put the divider. Clamped server-side to 20–80. */
  setManzaliLayout(layout: {
    chatPercent?: number;
    collapsed?: 'chat' | 'board' | null;
    lastBoard?: ManzaliBoard;
  }): Promise<{ layout: ManzaliSpaceView['layout'] }> {
    return this.request('/v1/manzali/space/layout', {
      method: 'PATCH',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(layout),
    });
  }

  setManzaliChannel(channel: ManzaliSpaceView['preferredChannel']): Promise<{
    preferredChannel: string;
  }> {
    return this.request('/v1/manzali/space/channel', {
      method: 'PATCH',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify({ channel }),
    });
  }

  /**
   * The citizen asked where their application is.
   *
   * Recorded distinctly from a background poll, because repeated asks in a day
   * are what the panic signal reads.
   */
  checkManzaliStatus(): Promise<{ status: ManzaliSpaceStatus }> {
    return this.request('/v1/manzali/space/status/check', {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  manzaliEvents(): Promise<{
    events: Array<{ kind: string; actor: unknown; detail: unknown; createdAt: string }>;
  }> {
    return this.request('/v1/manzali/space/events', { headers: this.citizenHeaders() });
  }

  /**
   * What the space would send the Ministry, and what it read to build it.
   *
   * Behind the "show the Ministry calls" button. Nothing here has been sent —
   * every housing write is projected rather than filed, because MOEI supplied no
   * request bodies and no Postman collection for any SZHP service.
   */
  manzaliProjectedCalls(): Promise<{
    calls: ManzaliProjectedCall[];
    reads: Array<{
      key: string;
      outcome: 'answered' | 'overlaid' | 'empty';
      provenance: ManzaliProvenance;
      source: string;
      stale: boolean;
    }>;
    unwired: Array<{ operation: string; endpoint: string; why: string }>;
    blockers: Array<{ id: string; detail: string }>;
  }> {
    return this.request('/v1/manzali/space/projected-calls', {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Branches that handle this citizen's housing product, nearest first.
   *
   * `basis` says how the list was ordered. `emirate` means we could not place the
   * applicant, and the surface must not imply proximity in that case.
   */
  manzaliBankBranches(): Promise<{
    branches: ManzaliBankBranch[];
    basis: 'distance' | 'emirate';
    unplacedReason?: string;
  }> {
    return this.request('/v1/manzali/space/bank-branches', {
      headers: this.citizenHeaders(),
    });
  }

  /** The money in this space. Nulls mean not known, never zero. */
  manzaliFinance(): Promise<{ finance: ManzaliFinanceSummary }> {
    return this.request('/v1/manzali/space/finance', { headers: this.citizenHeaders() });
  }

  /** Cost a design against the published rate basis. Arithmetic, never a model. */
  estimateManzaliDesign(input: {
    grossFloorAreaSqm: number;
    finishLevel?: 'basic' | 'standard' | 'premium';
    bedrooms?: number;
  }): Promise<{ estimate: ManzaliDesignEstimate }> {
    return this.request('/v1/manzali/space/design/estimate', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * Draw the design. Concepts, never approved drawings.
   *
   * Pass `studio` to draw one, which is how the board fills in as each image
   * lands: an image takes around twenty seconds, so three behind one request is a
   * minute of a frozen button. Omit it to draw the whole set in one call, which
   * is what a chat-driven request wants.
   */
  generateManzaliConcepts(
    input: { bedrooms?: number; grossFloorAreaSqm?: number; studio?: ManzaliStudio } = {},
  ): Promise<{
    concepts: ManzaliConcept[];
    estimate: ManzaliDesignEstimate | null;
    studio: ManzaliStudio | null;
  }> {
    return this.request('/v1/manzali/space/design/concepts', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  manzaliConcepts(): Promise<{ concepts: ManzaliConcept[] }> {
    return this.request('/v1/manzali/space/design/concepts', { headers: this.citizenHeaders() });
  }

  manzaliDesignEstimate(): Promise<{ estimate: ManzaliDesignEstimate | null }> {
    return this.request('/v1/manzali/space/design/estimate', {
      headers: this.citizenHeaders(),
    });
  }

  manzaliUtilities(): Promise<{
    thread: ManzaliMailThread | null;
    messages: ManzaliMailMessage[];
  }> {
    return this.request('/v1/manzali/space/utilities', { headers: this.citizenHeaders() });
  }

  /**
   * Send the utility request, having had the draft approved.
   *
   * `approved` is required and the route refuses without it. An agent writing to a
   * federal authority on a citizen's behalf, unreviewed, is not a thing this does.
   */
  sendManzaliUtilities(input: { approved: true; bodyText?: string }): Promise<{
    thread: ManzaliMailThread | null;
    messages: ManzaliMailMessage[];
  }> {
    return this.request('/v1/manzali/space/utilities/send', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  manzaliFurniture(budget?: number): Promise<{
    offers: ManzaliFurnitureOffer[];
    total: ManzaliShortlistTotal;
    searchAvailable: boolean;
    harvestAvailable: boolean;
    /** The most recent harvest, so a board reopened mid-run rejoins it. */
    run: ManzaliFurnitureRun | null;
    /** Signed feed for a run still going. Null once it has finished. */
    streamPath: string | null;
    rooms: Array<{ id: string; label: Localized }>;
  }> {
    const suffix = budget ? `?budget=${encodeURIComponent(String(budget))}` : '';
    return this.request(`/v1/manzali/space/furniture${suffix}`, {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Send a browser to the shops.
   *
   * Answers 202 with a run and a stream path: opening twenty product pages takes
   * minutes, so there is nothing to return synchronously. `joined` is true when a
   * run was already in flight for this space and this call attached to it rather
   * than starting a second one.
   */
  startManzaliFurnitureRun(input: {
    rooms?: string[];
    query?: string;
    budget?: number;
  }): Promise<{
    run: ManzaliFurnitureRun;
    joined: boolean;
    queuePosition: number;
    streamPath: string;
  }> {
    return this.request('/v1/manzali/space/furniture/run', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * The run's feed, as an `EventSource` URL.
   *
   * A URL rather than a subscription because the caller owns the lifetime: a page
   * that unmounts has to close it, and handing back an object whose close
   * somebody must remember is how a tab ends up holding six of them.
   *
   * `streamPath` comes from `startManzaliFurnitureRun` already signed. It carries
   * its own authorisation because `EventSource` cannot send headers.
   */
  manzaliFurnitureStreamUrl(streamPath: string, after = '0'): string {
    const separator = streamPath.includes('?') ? '&' : '?';
    return `${this.baseUrl}${streamPath}${separator}after=${encodeURIComponent(after)}`;
  }

  cancelManzaliFurnitureRun(runId: string): Promise<{ run: ManzaliFurnitureRun | null }> {
    return this.request(
      `/v1/manzali/space/furniture/run/${encodeURIComponent(runId)}/cancel`,
      { method: 'POST', headers: this.citizenHeaders() },
    );
  }

  searchManzaliFurniture(input: { query: string; room?: string; budget?: number }): Promise<{
    offers: ManzaliFurnitureOffer[];
    total: ManzaliShortlistTotal;
    searched: boolean;
  }> {
    return this.request('/v1/manzali/space/furniture/search', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  shortlistManzaliFurniture(input: { productUrl: string; shortlisted: boolean }): Promise<{
    offers: ManzaliFurnitureOffer[];
    total: ManzaliShortlistTotal;
  }> {
    return this.request('/v1/manzali/space/furniture/shortlist', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * The citizen reaches for an objection or a cancellation.
   *
   * Answers 202, not 200: the filing is composed and recorded for the projection panel
   * and deliberately not sent. Both operations are refused for want of a request body,
   * and cancellation also needs an OTP write MOEI has no contract for. `filed` is false
   * on every response.
   */
  startManzaliRecourse(input: {
    kind: 'objection' | 'cancellation';
    reason?: string;
    requestedAmount?: number;
  }): Promise<{
    recorded: boolean;
    filed: false;
    blockers: Array<{ id: string; detail: string }>;
    applicationNo: string | null;
  }> {
    return this.request('/v1/manzali/space/recourse', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  /**
   * Certified contractors, matched against the award where one is known.
   *
   * The two criteria are the citizen's, and omitting them ranks on neutral
   * values rather than on a preference nobody expressed.
   */
  manzaliContractors(
    criteria: { sustainability?: boolean; startMonths?: number } = {},
  ): Promise<{
    contractors: ManzaliContractor[];
    budgetFils: number | null;
    /** True when nothing behind the list came from a Ministry register. */
    registerUnavailable: boolean;
  }> {
    const params = new URLSearchParams();
    if (criteria.sustainability !== undefined) {
      params.set('sustainability', String(criteria.sustainability));
    }
    if (criteria.startMonths !== undefined) params.set('startMonths', String(criteria.startMonths));
    const query = params.toString();
    return this.request(`/v1/manzali/space/contractors${query ? `?${query}` : ''}`, {
      headers: this.citizenHeaders(),
    });
  }

  /** Ministry-supplied site imagery. An ingest seam, not a progress product. */
  manzaliSiteCaptures(): Promise<{ captures: ManzaliSiteCapture[] }> {
    return this.request('/v1/manzali/space/site-captures', { headers: this.citizenHeaders() });
  }

  /**
   * What the case requires, which is frequently nothing.
   *
   * `requiredByRules: 0` with a stated `requirementBasis` is a finished step, not
   * an empty one — the Ministry reads most of what it needs from its own records.
   */
  manzaliDocuments(): Promise<ManzaliDocumentsView> {
    return this.request('/v1/manzali/space/documents', { headers: this.citizenHeaders() });
  }

  manzaliAttachDocument(requirementId: string, file: File): Promise<ManzaliDocumentsView> {
    const body = new FormData();
    body.append('requirementId', requirementId);
    body.append('file', file);
    // No `content-type`: the browser has to set it, because it is the only thing
    // that knows the multipart boundary it just generated.
    return this.request('/v1/manzali/space/documents', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body,
    });
  }

  /** Everything the citizen keeps: decisions, documents, concepts. */
  manzaliVault(): Promise<{ items: ManzaliVaultItem[]; anySimulated: boolean }> {
    return this.request('/v1/manzali/space/vault', { headers: this.citizenHeaders() });
  }

  manzaliFinancingContract(): Promise<{
    contract: ManzaliFinancingContract | null;
    consultants: ManzaliConsultant[];
  }> {
    return this.request('/v1/manzali/space/financing-contract', {
      headers: this.citizenHeaders(),
    });
  }

  manzaliFinancingAction(
    action: 'engage' | 'pay-fee' | 'select' | 'sign',
    body: Record<string, unknown> = {},
  ): Promise<{ contract: ManzaliFinancingContract | null }> {
    return this.request(`/v1/manzali/space/financing-contract/${action}`, {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  /**
   * Bind the space's one permanent conversation.
   *
   * Set once. A space created by a campaign has no conversation until somebody
   * speaks, and a second thread would split history the citizen is told is
   * continuous.
   */
  attachManzaliConversation(conversationId: string): Promise<{ conversationId: string | null }> {
    return this.request('/v1/manzali/space/conversation', {
      method: 'POST',
      headers: { ...this.citizenHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify({ conversationId }),
    });
  }

  /** Open a demonstration space. 404 unless demonstration mode is on. */
  seedManzaliDemo(): Promise<{ space: ManzaliSpaceView }> {
    return this.request('/v1/manzali/space/demo', {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  resetManzaliDemo(): Promise<{ space: ManzaliSpaceView | null }> {
    return this.request('/v1/manzali/space/demo/reset', {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  housingBundle(bundleId: string): Promise<HousingBundleDetail> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}`, {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Put a sample-account bundle back to phase one so the journey can be shown
   * again. 404 for every other bundle — a real citizen's progress is not
   * something a presenter can erase.
   */
  resetHousingDemoBundle(
    bundleId: string,
  ): Promise<{ ok: true; phase: 'document_validation' }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/demo/reset`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  async uploadHousingDocument(
    bundleId: string,
    requirementId: string,
    file: File,
  ): Promise<{
    documentId: string;
    requirementId: string;
    status: 'auto_validated' | 'review_required';
    confidence: number | null;
  }> {
    const form = new FormData();
    form.append('requirementId', requirementId);
    form.append('file', file);
    const response = await fetch(
      `${this.baseUrl}/v1/housing/bundles/${encodeURIComponent(bundleId)}/documents`,
      { method: 'POST', headers: this.citizenHeaders(), body: form },
    );
    if (!response.ok) await this.fail(response);
    return (await response.json()) as {
      documentId: string;
      requirementId: string;
      status: 'auto_validated' | 'review_required';
      confidence: number | null;
    };
  }

  /**
   * `approved` rather than `submitted` means the bundle belongs to a sample
   * account and opened its own gate, so the caller is already on `phase`.
   */
  submitHousingDocuments(
    bundleId: string,
  ): Promise<{
    ok: true;
    status: 'submitted' | 'approved';
    phase?: HousingPhase;
    demoReleased?: boolean;
  }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/documents/submit`,
      { method: 'POST', headers: this.citizenHeaders() },
    );
  }

  async uploadHousingStepEvidence(
    bundleId: string,
    phase: HousingSkippablePhase,
    reason: string,
    file: File,
  ): Promise<{
    evidenceId: string;
    documentId: string;
    phase: HousingSkippablePhase;
    reviewStatus: 'pending';
    advanced: boolean;
    nextPhase: HousingPhase | null;
  }> {
    const form = new FormData();
    form.append('reason', reason);
    form.append('file', file);
    const response = await fetch(
      `${this.baseUrl}/v1/housing/bundles/${encodeURIComponent(bundleId)}/steps/${encodeURIComponent(phase)}/evidence`,
      { method: 'POST', headers: this.citizenHeaders(), body: form },
    );
    if (!response.ok) await this.fail(response);
    return (await response.json()) as {
      evidenceId: string;
      documentId: string;
      phase: HousingSkippablePhase;
      reviewStatus: 'pending';
      advanced: boolean;
      nextPhase: HousingPhase | null;
    };
  }

  housingMatches(
    bundleId: string,
    kind: 'bank' | 'contractor',
    input: Record<string, unknown>,
  ): Promise<{ snapshotId: string; items: HousingMatch[]; label: string }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/matches/${kind}`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  selectHousingPartner(
    bundleId: string,
    input: { kind: 'bank' | 'contractor'; partnerId: string; snapshotId: string },
  ): Promise<{ ok: true }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/selections`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  /**
   * Rename a project, or repaint it.
   *
   * Presentation only — nothing here reaches a ministry record. Both fields
   * are optional and independently clearable: sending an empty name puts the
   * ministry's own wording back.
   */
  setHousingProjectIdentity(
    bundleId: string,
    input: { name?: string | null; theme?: HousingProjectTheme | null },
  ): Promise<{ ok: true; name: string | null; theme: HousingProjectTheme | null }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/identity`, {
      method: 'PATCH',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  setHousingImageConsent(
    bundleId: string,
    granted: boolean,
  ): Promise<{ ok: true; consentId: string | null; granted: boolean }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/consents`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ scope: 'ai.home_image.process', granted }),
    });
  }

  async uploadHousingImage(
    bundleId: string,
    input: { studio: 'exterior' | 'interior'; room?: string; file: File },
  ): Promise<{ assetId: string }> {
    const form = new FormData();
    form.append('file', input.file);
    form.append('studio', input.studio);
    if (input.room) form.append('room', input.room);
    const response = await fetch(
      `${this.baseUrl}/v1/housing/bundles/${encodeURIComponent(bundleId)}/design/uploads`,
      { method: 'POST', headers: this.citizenHeaders(), body: form },
    );
    if (!response.ok) await this.fail(response);
    return (await response.json()) as { assetId: string };
  }

  generateHousingDesign(
    bundleId: string,
    input: {
      sourceAssetId: string;
      studio: 'exterior' | 'interior';
      room?: string;
      prompt: string;
      preferences: Record<string, unknown>;
    },
  ): Promise<{ jobId: string; assetIds: string[]; simulated: boolean }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/design/generate`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  /**
   * Queue design generation (202) instead of blocking on it — poll
   * `housingBundle(bundleId).designJobs` for the job status. An
   * idempotent repeat of a completed request resolves with its asset ids.
   */
  startHousingDesignJob(
    bundleId: string,
    input: {
      sourceAssetId: string;
      studio: 'exterior' | 'interior';
      room?: string;
      prompt: string;
      preferences: Record<string, unknown>;
    },
  ): Promise<{ jobId: string; status: 'queued' | 'completed'; assetIds?: string[] }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/design/generate/async`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input),
      },
    );
  }

  selectHousingDesign(bundleId: string, assetId: string): Promise<{ ok: true }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/design/${encodeURIComponent(assetId)}/select`,
      { method: 'POST', headers: this.citizenHeaders() },
    );
  }

  submitHousingConsultantReview(bundleId: string): Promise<{ ok: true; phase: string }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/consultant/submit`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  searchHousingFurniture(
    bundleId: string,
    input: { room: string; query: string; budgetFils: number; style: string[] },
  ): Promise<{ searchId: string; products: HousingFurnitureProduct[]; simulated: boolean }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/furniture/search`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  shortlistHousingFurniture(
    bundleId: string,
    searchId: string,
    productId: string,
  ): Promise<{ ok: true }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/furniture/shortlist`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ searchId, productId }),
    });
  }

  removeHousingFurnitureShortlist(
    bundleId: string,
    productId: string,
  ): Promise<{ ok: true; removed: boolean }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/furniture/shortlist/${encodeURIComponent(productId)}`,
      {
        method: 'DELETE',
        headers: this.citizenHeaders(),
      },
    );
  }

  exportHousingBrief(bundleId: string): Promise<Record<string, unknown>> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/export`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  housingBriefPdf(
    bundleId: string,
    lang: Lang,
  ): Promise<{ filename: string; url: string }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/brief`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ lang }),
    });
  }

  async getHousingQararPdf(
    bundleId: string,
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await fetch(
      `${this.baseUrl}/v1/housing/bundles/${encodeURIComponent(bundleId)}/qarar`,
      { headers: this.citizenHeaders() },
    );
    if (!response.ok) await this.fail(response);
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition');
    const filename =
      disposition?.match(/filename="([^"]+)"/)?.[1] ?? 'qarar.pdf';
    return { blob, filename };
  }

  getFinancingContract(
    bundleId: string,
  ): Promise<{ financingContract: HousingFinancingContractView | null }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract`,
      { headers: this.citizenHeaders() },
    );
  }

  listHousingConsultants(): Promise<{ consultants: HousingConsultant[] }> {
    return this.request('/v1/housing/consultants', {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Engage a consultant. `source: 'brought'` registers a consultant who is not on
   * the programme registry yet, which the backend has always supported but no
   * client method could reach.
   */
  engageFinancingConsultant(
    bundleId: string,
    input:
      | { consultantId: string; source?: 'suggested' | 'chosen' }
      | {
          source: 'brought';
          legalNameEn: string;
          legalNameAr: string;
          tradeLicence: string;
          contactEmail?: string | null;
        },
  ): Promise<{ financingContract: HousingFinancingContractView }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/engage`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input),
      },
    );
  }

  payFinancingContractFee(
    bundleId: string,
  ): Promise<{ financingContract: HousingFinancingContractView }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/pay-fee`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
      },
    );
  }

  updateFinancingContractAmounts(
    bundleId: string,
    input: { ownFundsAed: number; bankFacilityAed?: number | null },
  ): Promise<{ financingContract: HousingFinancingContractView }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/amounts`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input),
      },
    );
  }

  selectFinancingContractTemplate(
    bundleId: string,
    input: { templateId: string },
  ): Promise<{ financingContract: HousingFinancingContractView }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/select`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input),
      },
    );
  }

  signFinancingContract(
    bundleId: string,
    input?: { role?: 'owner' | 'contractor' | 'consultant'; signerId?: string },
  ): Promise<{ financingContract: HousingFinancingContractView }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/sign`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input ?? {}),
      },
    );
  }

  draftFinancingBankMessage(
    bundleId: string,
    input?: { bankPartnerId?: string; contractorName?: string; lang?: Lang },
  ): Promise<{ draft: HousingBankMessageDraft }> {
    return this.request(
      `/v1/housing/bundles/${encodeURIComponent(bundleId)}/financing-contract/bank-message`,
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify(input ?? {}),
      },
    );
  }

  startHousingConcierge(
    bundleId: string,
    lang: Lang,
  ): Promise<{
    conversationId: string;
    startedAt: string;
    reused: boolean;
    suggestedServices: string[];
  }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/concierge`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ lang }),
    });
  }

  generateHousingIdeas(
    bundleId: string,
    input: { topic: HousingIdeaTopic; prompt: string; room?: string; lang?: Lang },
  ): Promise<{ ideas: HousingIdea[]; simulated: boolean; model: string | null }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/ai/ideas`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  draftHousingText(
    bundleId: string,
    input: { kind: HousingDraftKind; prompt?: string; lang?: Lang },
  ): Promise<{ draft: HousingDraft; simulated: boolean; model: string | null }> {
    return this.request(`/v1/housing/bundles/${encodeURIComponent(bundleId)}/ai/draft`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  // --- chat -----------------------------------------------------------------

  /** Who this browser is acting as, for the portal's chrome. */
  citizen(): Promise<CitizenIdentity> {
    return this.request('/v1/chat/citizen/me', { headers: this.citizenHeaders() });
  }

  /**
   * What the platform holds about this citizen, and who told it.
   *
   * Separate from `citizen()` on purpose. That one is chrome — a name and a tier, asked
   * for on every page load — and this carries a date of birth and a passport expiry, so
   * it belongs to the one screen a citizen opens deliberately.
   */
  heldIdentity(): Promise<HeldIdentity> {
    return this.request('/v1/chat/citizen/profile', { headers: this.citizenHeaders() });
  }

  /** This citizen's durable conversation history (across every service). */
  listConversations(): Promise<{ conversations: ConversationSummary[] }> {
    return this.request('/v1/chat/conversations', { headers: this.citizenHeaders() });
  }

  /**
   * Name a conversation, or hand it back to automatic naming.
   *
   * An empty title is the reset: the platform stops treating the thread as
   * named and resumes deriving one from the application.
   */
  renameConversation(
    conversationId: string,
    title: string | null,
  ): Promise<{ title: string | null; titleCustom: boolean }> {
    return this.request(`/v1/chat/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'PATCH',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ title }),
    });
  }

  /** Personalized dashboard overview (greeting, signals, recommendations). */
  dashboard(): Promise<DashboardOverview> {
    return this.request('/v1/chat/dashboard', { headers: this.citizenHeaders() });
  }

  /**
   * The pleasure boats the Ministry holds for this citizen.
   *
   * Paged and searchable because a fleet owner exists: the conversational
   * channels cap a picker at ten options, and this is the surface where the
   * rest can be reached.
   */
  boats(params: { query?: string; limit?: number; offset?: number } = {}): Promise<CitizenBoatPage> {
    const search = new URLSearchParams();
    if (params.query) search.set('query', params.query);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    const suffix = search.toString();
    return this.request(`/v1/chat/boats${suffix ? `?${suffix}` : ''}`, {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Everything the citizen holds on the water, with what can be done to each.
   *
   * Separate from `boats()`, which is shaped for the conversational picker and
   * stays that way. This carries the licences, the actions, the in-flight work
   * and the citizen's own notes — the dashboard's question rather than the
   * journey's.
   */
  marineAssets(
    params: { query?: string; limit?: number; offset?: number } = {},
  ): Promise<MarineAssetsPage> {
    const search = new URLSearchParams();
    if (params.query) search.set('query', params.query);
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    const suffix = search.toString();
    return this.request(`/v1/chat/marine-assets${suffix ? `?${suffix}` : ''}`, {
      headers: this.citizenHeaders(),
    });
  }

  // -------------------------------------------------------------------------
  // Simulator
  // -------------------------------------------------------------------------
  //
  // Reading a run needs no credential and starting one needs the citizen's,
  // because a run files as them. That asymmetry is the whole access model: the
  // record is open so a finding is worth writing down, and the act is bounded
  // by what the person doing it could already do themselves.

  simulatorCatalogue(): Promise<SimulatorCatalogue> {
    return this.request('/v1/simulator/services');
  }

  /** Whether this citizen could get anywhere with this service, asked first. */
  simulatorPreflight(slug: string): Promise<SimulatorPreflight> {
    return this.request(`/v1/simulator/services/${encodeURIComponent(slug)}/preflight`, {
      headers: this.citizenHeaders(),
    });
  }

  /**
   * Start a run. Answers 202 with an id; the conversation arrives on the stream.
   *
   * A refusal comes back as 409 carrying its reason rather than as a thrown
   * error — "you own no boat the Ministry knows about" is an answer the page
   * shows, not a failure it hides. Same shape as the journey runner's refusal.
   */
  startSimulatorRun(input: SimulatorStartInput): Promise<SimulatorStartResponse> {
    return this.request(
      '/v1/simulator/runs',
      { method: 'POST', headers: this.citizenHeaders(), body: JSON.stringify(input) },
      [409],
    );
  }

  simulatorRuns(
    params: {
      service?: string;
      status?: SimulatorRunStatus;
      commented?: boolean;
      findings?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ runs: SimulatorRun[] }> {
    const search = new URLSearchParams();
    if (params.service) search.set('service', params.service);
    if (params.status) search.set('status', params.status);
    if (params.commented) search.set('commented', 'true');
    if (params.findings) search.set('findings', 'true');
    if (params.limit !== undefined) search.set('limit', String(params.limit));
    if (params.offset !== undefined) search.set('offset', String(params.offset));
    const suffix = search.toString();
    return this.request(`/v1/simulator/runs${suffix ? `?${suffix}` : ''}`);
  }

  simulatorRun(runId: string): Promise<SimulatorRunDetail> {
    return this.request(`/v1/simulator/runs/${encodeURIComponent(runId)}`);
  }

  /** Ask the Ministry where the filing got to, then let the citizen finish. */
  continueSimulatorRun(runId: string): Promise<{ runId: string; leg: number }> {
    return this.request(`/v1/simulator/runs/${encodeURIComponent(runId)}/continue`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  cancelSimulatorRun(runId: string): Promise<{ run: SimulatorRun }> {
    return this.request(`/v1/simulator/runs/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
    });
  }

  simulatorComments(runId: string): Promise<{ comments: SimulatorComment[] }> {
    return this.request(`/v1/simulator/runs/${encodeURIComponent(runId)}/comments`);
  }

  addSimulatorComment(
    runId: string,
    input: { authorName: string; body: string; turnId?: string | null },
  ): Promise<{ comment: SimulatorComment }> {
    return this.request(`/v1/simulator/runs/${encodeURIComponent(runId)}/comments`, {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  editSimulatorComment(
    runId: string,
    commentId: string,
    input: { authorName: string; body: string; expectedRevision: number },
  ): Promise<{ comment: SimulatorComment }> {
    return this.request(
      `/v1/simulator/runs/${encodeURIComponent(runId)}/comments/${encodeURIComponent(commentId)}`,
      { method: 'PUT', headers: this.citizenHeaders(), body: JSON.stringify(input) },
    );
  }

  removeSimulatorComment(runId: string, commentId: string): Promise<{ ok: true }> {
    return this.request(
      `/v1/simulator/runs/${encodeURIComponent(runId)}/comments/${encodeURIComponent(commentId)}`,
      { method: 'DELETE', headers: this.citizenHeaders() },
    );
  }

  /**
   * The live feed of a run, as an `EventSource` URL.
   *
   * A URL rather than a subscription because the caller owns the lifetime: a
   * page that unmounts must close it, and handing back an object whose close
   * somebody has to remember is how a tab ends up holding six of them.
   */
  simulatorStreamUrl(runId: string, after = 0): string {
    return `${this.baseUrl}/v1/simulator/runs/${encodeURIComponent(runId)}/stream?after=${after}`;
  }

  /** One asset in full, including the Ministry's whole record for it. */
  marineAsset(kind: MarineAssetKind, identifier: string): Promise<MarineAssetDetail> {
    return this.request(
      `/v1/chat/marine-assets/${kind}/${encodeURIComponent(identifier)}`,
      { headers: this.citizenHeaders() },
    );
  }

  /**
   * Ask the Ministry which of these services each boat qualifies for.
   *
   * Its own call because it costs a ministry round trip per boat per service
   * and the list above does not: the cards paint first and the badges resolve
   * behind them, so a slow ministry delays a detail rather than the page.
   */
  confirmMarineActions(
    identifiers: string[],
    tier: ActionTier = 'primary',
  ): Promise<{ results: Array<{ identifier: string; answers: Record<string, boolean> }> }> {
    return this.request('/v1/chat/marine-assets/eligibility', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify({ identifiers, tier }),
    });
  }

  /**
   * Save what the citizen wrote about their asset.
   *
   * A partial edit: an absent key is left alone and an explicit `null` clears
   * the field, so removing a note and editing a berth are the same request
   * shape.
   */
  saveAssetAnnotations(
    kind: MarineAssetKind,
    identifier: string,
    patch: Partial<Pick<AssetAnnotations, 'nickname' | 'berth' | 'note'>>,
  ): Promise<AssetAnnotations> {
    return this.request(
      `/v1/chat/marine-assets/${kind}/${encodeURIComponent(identifier)}/annotations`,
      {
        method: 'PATCH',
        headers: this.citizenHeaders(),
        body: JSON.stringify(patch),
      },
    );
  }

  async uploadAssetPhoto(
    kind: MarineAssetKind,
    identifier: string,
    file: File,
  ): Promise<AssetAnnotations> {
    const form = new FormData();
    form.append('file', file);
    const response = await fetch(
      `${this.baseUrl}/v1/chat/marine-assets/${kind}/${encodeURIComponent(identifier)}/photo`,
      { method: 'PUT', headers: this.citizenHeaders(), body: form },
    );
    if (!response.ok) await this.fail(response);
    return (await response.json()) as AssetAnnotations;
  }

  deleteAssetPhoto(kind: MarineAssetKind, identifier: string): Promise<AssetAnnotations> {
    return this.request(
      `/v1/chat/marine-assets/${kind}/${encodeURIComponent(identifier)}/photo`,
      { method: 'DELETE', headers: this.citizenHeaders() },
    );
  }

  /** Mint a fresh session token bound to an existing conversation. */
  reopenConversation(
    conversationId: string,
  ): Promise<{ sessionId: string; sessionToken: string; lang: Lang }> {
    return this.request(`/v1/chat/conversations/${conversationId}/session`, {
      method: 'POST',
      headers: this.citizenHeaders(),
    });
  }

  /**
   * End this citizen's session ("not you? start fresh").
   *
   * The citizen's records are untouched; what is withdrawn is the authority to
   * act as them. The platform ends the session on every surface this person
   * holds — the portal, the widget and the mobile application each carry their
   * own credential in the same browser — and revokes the conversations those
   * sessions opened, so a bearer token sitting in a widget's iframe stops
   * working rather than carrying on.
   *
   * `endUaePassSession` in the answer is the platform saying whether there is
   * also a UAE Pass session in this browser to close. Acting on it matters: UAE
   * Pass keeps its own session, and leaving it open signs the next person on the
   * device straight back in as the last.
   */
  async signOutCitizen(
    reason: 'user' | 'page_closed' | 'host_signed_out' = 'user',
  ): Promise<{ endUaePassSession: boolean }> {
    const answer = await this.request<{ ok: boolean; endUaePassSession?: boolean }>(
      '/v1/chat/citizen/sign-out',
      {
        method: 'POST',
        headers: this.citizenHeaders(),
        body: JSON.stringify({ reason }),
      },
    ).catch(() => null);
    this.setCitizenToken(null);
    return { endUaePassSession: answer?.endUaePassSession === true };
  }

  /**
   * Tell the platform the page is going away, from `pagehide`.
   *
   * `sendBeacon` rather than `fetch`, because a request started in an unloading
   * document is cancelled and this one has to survive it. Best-effort by nature,
   * which is why it is not the mechanism that makes `endOnPageClose` true: with
   * that setting the browser holds its authentication in `sessionStorage` and a
   * closed tab cannot present it again whatever happens to this call. What the
   * beacon buys is the server knowing promptly, so the session shows as ended
   * rather than idling out half an hour later.
   */
  signOutCitizenOnUnload(): void {
    if (!this.citizenToken || typeof navigator === 'undefined' || !navigator.sendBeacon) return;
    // A Blob rather than a string: `sendBeacon` sends text/plain otherwise, and
    // the token has to travel in the body because a beacon carries no headers.
    const payload = new Blob(
      [JSON.stringify({ reason: 'page_closed', citizenToken: this.citizenToken })],
      { type: 'application/json' },
    );
    navigator.sendBeacon(`${this.baseUrl}/v1/chat/citizen/sign-out`, payload);
  }

  /**
   * Demo records this portal can sign in as while UAE Pass credentials are
   * pending. Empty when the platform has the feature switched off, which is
   * what the caller renders against — there is no separate flag to read.
   */
  async sampleAccounts(): Promise<{ accounts: SampleAccount[] }> {
    return this.request('/v1/chat/citizen/samples');
  }

  /** Adopt a sample account and keep its token for subsequent calls. */
  async signInAsSampleAccount(key: string): Promise<{ citizenToken: string; expiresAt: string }> {
    const issued = await this.request<{ citizenToken: string; expiresAt: string }>(
      `/v1/chat/citizen/samples/${encodeURIComponent(key)}/sign-in`,
      { method: 'POST' },
    );
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  /**
   * How signing in works on this deployment, read before rendering the page.
   *
   * `uae_pass` redirects to UAE Pass; `sandbox` collects an Emirates ID because a
   * mock stands in; `unavailable` means neither is permitted here and the page
   * should say so rather than offer a button that refuses.
   */
  uaePassSignInMethod(): Promise<{ method: 'uae_pass' | 'sandbox' | 'unavailable' }> {
    return this.request('/v1/auth/uaepass/sign-in');
  }

  /**
   * Begin a UAE Pass sign-in that no request asked for.
   *
   * Sends the browser's existing identity, so a half-finished application
   * started anonymously belongs to the citizen who signs in rather than being
   * left behind. Returns the address to navigate to; the platform will not
   * redirect on the caller's behalf, because only the caller knows whether the
   * UAE Pass application is installed on this device.
   *
   * `503 uaepass.not_configured` when live UAE Pass is not enabled — the caller
   * offers `signInWithSandboxUaePass` instead, where that is permitted.
   */
  startUaePassSignIn(input: {
    lang?: Lang;
    /** A path on the portal to land on afterwards. */
    next?: string | null;
    /** `app` asks for the on-device flow; only after detecting the UAE Pass app. */
    flow?: 'app';
    /**
     * Where the handoff code should be returned.
     *
     * Defaults to the portal. `app` sends it through the mobile application's
     * own URI scheme instead, and is a separate question from `flow`: a phone
     * without UAE Pass installed still needs the browser flow and still needs
     * to come home to the app.
     */
    surface?: 'app';
    /**
     * The RFC 7636 S256 challenge binding the handoff code to this client.
     *
     * Required for `surface: 'app'` and rejected with `400` without it — a code
     * returned over a URI scheme any application may claim has to be bound to
     * the one that asked for it.
     */
    codeChallenge?: string;
  }): Promise<{ authorizeUrl: string }> {
    return this.request('/v1/auth/uaepass/sign-in/start', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  /**
   * Exchange the one-time code UAE Pass came back with for this client's
   * durable identity, and adopt it. Single-use: a second attempt is a 404.
   *
   * `codeVerifier` is required exactly when the attempt was opened with a
   * challenge. A mismatch answers the same 404 as an unknown code and leaves
   * the code unspent, so a failed theft cannot also deny the citizen their
   * sign-in.
   */
  async completeUaePassSignIn(
    code: string,
    codeVerifier?: string,
  ): Promise<{ citizenToken: string; expiresAt: string; next: string | null }> {
    const issued = await this.request<{
      citizenToken: string;
      expiresAt: string;
      next: string | null;
    }>('/v1/auth/uaepass/sign-in/session', {
      method: 'POST',
      body: JSON.stringify(codeVerifier ? { code, codeVerifier } : { code }),
    });
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  /**
   * Sign in where a mock stands in for UAE Pass.
   *
   * `409` once live UAE Pass is configured, and `409` in production unless a
   * named ministry pilot has opted in — the same refusal the journey card's
   * sandbox completion answers with, from the same function.
   */
  async signInWithSandboxUaePass(input: {
    lang?: Lang;
    /** Who the mock is standing in for; the ministry's records are keyed on it. */
    emiratesId?: string;
    next?: string | null;
  }): Promise<{ citizenToken: string; expiresAt: string; next: string | null }> {
    const issued = await this.request<{
      citizenToken: string;
      expiresAt: string;
      next: string | null;
    }>('/v1/auth/uaepass/sign-in/sandbox', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  // -- The embedded widget --------------------------------------------------
  //
  // A widget key rides on every one of these. It is not a citizen credential and
  // proves nothing about who is asking — it says which registered embed the
  // request came from, and the platform pairs it with the `Origin` header the
  // browser sets to decide whether this frame may hold an identity at all.

  private embedKey: string | null = null;

  /** Present a widget key on subsequent embed and channel calls. */
  setEmbedKey(key: string | null): void {
    this.embedKey = key && key.trim() !== '' ? key.trim() : null;
  }

  private embedHeaders(): Record<string, string> {
    return this.embedKey ? { 'x-embed-key': this.embedKey } : {};
  }

  /**
   * What this embed may do, before the citizen has done anything.
   *
   * Asked first, so the frame can configure itself rather than discovering its
   * limits one refusal at a time: `trusted: false` means conversation-only and
   * the frame should not offer sign-in or attachments at all.
   */
  embedConfig(): Promise<{
    trusted: boolean;
    signIn: EmbedSignInProvider | 'sandbox' | 'unavailable';
    hostHandoff: boolean;
    allowedServices: string[];
    languages: Lang[];
  }> {
    return this.request('/v1/embed/config', { headers: this.embedHeaders() });
  }

  /**
   * Rung one, for a host page that lives inside eServices.
   *
   * The alternative to `signInFromHostHandoff` for a ministry site that already
   * holds an eServices session id and has no reason to build a signing endpoint
   * for one. The platform confirms the id against the ministry's own API before
   * it issues anything, so an invented one buys nothing.
   */
  async signInFromEservicesSession(input: {
    sessionId: string;
    lang?: Lang;
  }): Promise<{ citizenToken: string; expiresAt: string }> {
    const issued = await this.request<{ citizenToken: string; expiresAt: string }>(
      '/v1/embed/identity/eservices',
      {
        method: 'POST',
        headers: { ...this.embedHeaders(), ...this.citizenHeaders() },
        body: JSON.stringify(input),
      },
    );
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  /**
   * Exchange the one-time code an eServices popup came back with.
   *
   * Separate from `completeUaePassSignIn` because the two providers mint their
   * codes on different endpoints, and identical in every other respect: single
   * use, and a second attempt is a 404.
   */
  async completeMoeiSsoSignIn(
    code: string,
  ): Promise<{ citizenToken: string; expiresAt: string; next: string | null }> {
    const issued = await this.request<{
      citizenToken: string;
      expiresAt: string;
      next: string | null;
    }>('/v1/auth/moei-sso/sign-in/session', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  /**
   * Rung one: spend an assertion the host page made about who the citizen is.
   *
   * The host site is already authenticated, so this is the only rung with no
   * window and nothing for the citizen to do. `409` when no handoff secret has
   * been agreed with that site, which is a cue to try the silent rung instead.
   */
  async signInFromHostHandoff(input: {
    token: string;
    lang?: Lang;
    emiratesId?: string;
  }): Promise<{ citizenToken: string; expiresAt: string }> {
    const issued = await this.request<{ citizenToken: string; expiresAt: string }>(
      '/v1/embed/identity/host',
      {
        method: 'POST',
        headers: { ...this.embedHeaders(), ...this.citizenHeaders() },
        body: JSON.stringify(input),
      },
    );
    this.setCitizenToken(issued.citizenToken);
    return issued;
  }

  /**
   * Open a sign-in attempt for a popup to run.
   *
   * `provider` defaults to `uae_pass`. Read it off `embedConfig().signIn`
   * rather than choosing: which provider a deployment offers is a property of
   * how it is configured, and a frame that asks for one that is not enabled
   * gets a 503 it could have avoided.
   *
   * `silent: true` is UAE Pass only. It adds `prompt=none`, which answers from
   * an existing session or refuses without showing anything; the platform
   * ignores it for eServices and says so in its answer, because that provider's
   * popup is first-party to the ministry's own site and returns a signed-in
   * citizen without prompting anyway.
   *
   * Run the returned URL in a **popup**, never a hidden iframe — third-party
   * cookie partitioning means an iframe sees neither provider's session cookie,
   * so a signed-in citizen is reported as signed out.
   */
  startEmbedSignIn(input: {
    lang?: Lang;
    silent?: boolean;
    provider?: EmbedSignInProvider;
  }): Promise<{
    authorizeUrl: string;
    provider: EmbedSignInProvider;
    silent: boolean;
  }> {
    return this.request('/v1/embed/auth/start', {
      method: 'POST',
      headers: { ...this.embedHeaders(), ...this.citizenHeaders() },
      body: JSON.stringify(input),
    });
  }

  /**
   * Where to send a citizen so UAE Pass itself forgets them, for a shared
   * device. Revoking our own token is not the same thing: the UAE Pass session
   * in this browser would still sign the next person straight back in.
   */
  uaePassLogoutUrl(lang: Lang): string {
    return `${this.baseUrl}/v1/auth/uaepass/logout?lang=${lang}`;
  }

createChatSession(input: {
    lang?: Lang;
    service?: string;
    simulation?: boolean;
    handoffToken?: string;
    /**
     * The asset this conversation is about, when a card already knows.
     *
     * Verified server-side against the citizen's own boats and licences before
     * it settles anything, so naming somebody else's official number achieves
     * nothing. A pre-selection the Ministry cannot confirm is simply ignored
     * and the journey asks, as it does today.
     */
    record?: { kind: MarineAssetKind; id: string };
  }): Promise<{
    sessionId: string;
    conversationId: string;
    sessionToken: string;
    /**
     * Present only when this call created a new citizen. A returning browser
     * keeps the token it already holds rather than collecting one per visit.
     */
    citizenToken: string | null;
    lang: Lang;
    disclosure: string;
    replies: EngineReply[];
    application: ApplicationProjection | null;
    orchestration: OrchestrationTrace | null;
    handover: boolean;
    handoff?: boolean;
    /** The surface a handoff came from, so the page can offer the way back. */
    handoffSource?: 'whatsapp' | 'webchat' | 'app_widget' | 'site_widget' | null;
    /** A plain WhatsApp thread link — no token, nothing for the citizen to send. */
    returnUrl?: string | null;
  }> {
    // Sends the existing identity so a returning browser continues as the same
    // citizen instead of being given a fresh record on every visit.
    return this.request('/v1/chat/sessions', {
      method: 'POST',
      headers: this.citizenHeaders(),
      body: JSON.stringify(input),
    });
  }

  /** Mint a cross-channel continue token for the active application. */
  mintHandoff(
    session: { sessionId: string; sessionToken: string },
    target: 'whatsapp' | 'web',
  ): Promise<{ token: string; url: string; waMessage: string; expiresAt: string; target: string }> {
    return this.request(`/v1/chat/${session.sessionId}/handoff`, {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: JSON.stringify({ target }),
    });
  }

  sendOtp(
    session: { sessionId: string; sessionToken: string },
    phone: string,
    lang?: Lang,
  ): Promise<{ ok: true; expiresInSeconds: number; debugCode?: string }> {
    return this.request('/v1/identity/otp/send', {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: JSON.stringify({ sessionId: session.sessionId, phone, lang }),
    });
  }

  verifyOtp(
    session: { sessionId: string; sessionToken: string },
    phone: string,
    code: string,
    lang?: Lang,
  ): Promise<{
    ok: true;
    customerId: string;
    verificationTier: string;
    mergedAnonymous: boolean;
  }> {
    return this.request('/v1/identity/otp/verify', {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: JSON.stringify({ sessionId: session.sessionId, phone, code, lang }),
    });
  }

  /**
   * One name, written in the other script.
   *
   * For a card asking a name in both — a boat is registered in English and in
   * Arabic — so leaving one box fills the other rather than asking somebody to
   * spell their own boat's name again in a script they may not type.
   *
   * Null whenever nothing could be produced, including when the request itself
   * failed. The engine writes the name on the turn after the card is submitted,
   * so this is only ever an attempt to be earlier, and a card is not worth
   * interrupting for a convenience that did not land.
   */
  async transliterate(
    session: { sessionId: string; sessionToken: string },
    input: { name: string; target: 'en' | 'ar' },
  ): Promise<string | null> {
    try {
      const res = await fetch(`${this.baseUrl}/v1/chat/${session.sessionId}/transliterate`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${session.sessionToken}`,
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { name?: string | null };
      return body.name ?? null;
    } catch {
      return null;
    }
  }

  async sendMessage(
    session: { sessionId: string; sessionToken: string },
    input: {
      text?: string;
      widget?: { fieldId?: string; value?: string; action?: string };
      /** Answers from a `field_group` card: field id → raw value. */
      fields?: Record<string, string>;
    },
    events?: ChatEvents,
  ): Promise<TurnResult> {
    const res = await fetch(`${this.baseUrl}/v1/chat/${session.sessionId}/messages`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${session.sessionToken}`,
        accept: events ? 'text/event-stream' : 'application/json',
      },
      body: JSON.stringify(input),
    });
    if (!res.ok) await this.fail(res);

    if (!events || !res.headers.get('content-type')?.includes('text/event-stream')) {
      const body = (await res.json()) as Omit<TurnResult, 'lang'> & { lang?: Lang };
      return { ...body, lang: body.lang ?? null };
    }

    const replies: EngineReply[] = [];
    let application: ApplicationProjection | null = null;
    let handover = false;
    let orchestration: OrchestrationTrace | null = null;
    let lang: Lang | null = null;

    for await (const event of readSse(res)) {
      if (event.event === 'token' && events.onToken) {
        events.onToken((JSON.parse(event.data) as { text: string }).text);
      } else if (event.event === 'message') {
        const message = JSON.parse(event.data) as { role: string; text: string; widgets: Widget[]; agent?: string };
        replies.push({ text: message.text, widgets: message.widgets, agent: message.agent });
        events.onMessage?.(message);
      } else if (event.event === 'orchestration') {
        orchestration = JSON.parse(event.data) as OrchestrationTrace;
        events.onOrchestration?.(orchestration);
      } else if (event.event === 'language') {
        const declared = (JSON.parse(event.data) as { lang: Lang }).lang;
        if (declared === 'en' || declared === 'ar') {
          lang = declared;
          events.onLanguage?.(declared);
        }
      } else if (event.event === 'application') {
        application = JSON.parse(event.data) as ApplicationProjection;
        events.onApplication?.(application);
      } else if (event.event === 'handover') {
        handover = true;
        events.onHandover?.();
      }
    }
    return { replies, lang, application, handover, orchestration };
  }

  /**
   * Send a recording as one turn.
   *
   * The same route as `sendMessage`, carrying audio instead of JSON — a citizen
   * who speaks is sending a message, not using a different feature, and the
   * platform runs it through the same guardrails and the same engine call.
   *
   * Never streamed. The spoken reply only exists once the whole turn does, so
   * there is nothing to type out ahead of it, and a typewriter running under
   * audio that is already talking is two versions of the same sentence.
   */
  async sendVoice(
    session: { sessionId: string; sessionToken: string },
    recording: {
      blob: Blob;
      durationMs?: number;
      /**
       * A transcript the client already holds, from a live captioning stream.
       * Lets the platform skip transcribing the same audio a second time.
       */
      transcriptHint?: string;
    },
  ): Promise<VoiceTurnResult> {
    const form = new FormData();
    form.append('audio', recording.blob, 'voice-message.wav');
    if (recording.durationMs != null) {
      form.append('durationMs', String(Math.max(0, Math.round(recording.durationMs))));
    }
    if (recording.transcriptHint?.trim()) {
      form.append('transcriptHint', recording.transcriptHint.trim());
    }

    const res = await fetch(`${this.baseUrl}/v1/chat/${session.sessionId}/messages`, {
      method: 'POST',
      // No content-type: the browser sets it with the multipart boundary, and
      // setting it by hand produces a body the server cannot parse.
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: form,
    });
    if (!res.ok) await this.fail(res);
    const body = (await res.json()) as Omit<VoiceTurnResult, 'lang'> & { lang?: Lang };
    return { ...body, lang: body.lang ?? null };
  }

  async uploadDocument(
    session: { sessionId: string; sessionToken: string },
    requirementId: string,
    file: File,
  ): Promise<{ replies: EngineReply[]; application: ApplicationProjection | null }> {
    const form = new FormData();
    form.append('file', file);
    form.append('requirementId', requirementId);
    const res = await fetch(`${this.baseUrl}/v1/chat/${session.sessionId}/documents`, {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: form,
    });
    if (!res.ok) await this.fail(res);
    return (await res.json()) as { replies: EngineReply[]; application: ApplicationProjection | null };
  }

  /**
   * A short-lived way to look at a document the ministry already holds.
   *
   * For the `preview` on a `vault_reuse` boolean: the citizen is being asked to
   * reuse a file they sent months ago, and a filename is not something a person
   * recognises. Resolved on demand rather than carried on the widget because
   * the URL expires and the widget is persisted with the transcript.
   *
   * Null when the document is no longer there, which a caller should show as
   * "we cannot open this" rather than as a failure of the offer — the reuse
   * itself may still be perfectly good.
   */
  async previewDocument(
    session: { sessionId: string; sessionToken: string },
    documentId: string,
  ): Promise<DocumentView | null> {
    try {
      return await this.request<DocumentView>(
        `/v1/chat/${session.sessionId}/documents/${encodeURIComponent(documentId)}/preview`,
        { headers: { authorization: `Bearer ${session.sessionToken}` } },
      );
    } catch {
      return null;
    }
  }

  transcript(session: { sessionId: string; sessionToken: string }): Promise<{
    messages: {
      id: string;
      direction: string;
      role: string;
      text: string | null;
      /** Answered on a card rather than typed; the card itself shows the value. */
      viaCard?: boolean;
      widgets: Widget[];
      agent?: string;
      staffName?: string;
      staffAvatar?: string;
      media?: {
        filename: string;
        mime: string;
        url: string | null;
        documentId: string | null;
        kind?: string;
      }[];
      /** Spoken or typed. A voice message keeps its marker across a reload. */
      modality?: 'text' | 'voice';
      at: string;
    }[];
    application: ApplicationProjection | null;
    handover: {
      state: 'ai' | 'pending_human' | 'human';
      status: string | null;
      agentName: string | null;
      agentAvatar: string | null;
      queuePosition: number | null;
      estimatedWaitSec: number | null;
      routingQueueName: string | null;
      connection: 'live' | 'degraded';
    };
  }> {
    return this.request(`/v1/chat/${session.sessionId}/messages`, {
      headers: { authorization: `Bearer ${session.sessionToken}` },
    });
  }

  liveHandover(
    session: { sessionId: string; sessionToken: string },
    signal: AbortSignal,
    onEvent: (event: CitizenHandoverEvent) => void,
    after?: string,
  ): Promise<void> {
    const suffix = after ? `?after=${encodeURIComponent(after)}` : '';
    return this.streamJsonEvents<Record<string, unknown>>(
      `/v1/chat/${session.sessionId}/live${suffix}`,
      { authorization: `Bearer ${session.sessionToken}` },
      signal,
      ({ id, event, data }) =>
        onEvent({
          id,
          kind: event,
          ...(data as Omit<CitizenHandoverEvent, 'id' | 'kind'>),
        }),
    );
  }

  setHandoverTyping(
    session: { sessionId: string; sessionToken: string },
    typing: boolean,
  ): Promise<{ ok: true }> {
    return this.request(`/v1/chat/${session.sessionId}/live/typing`, {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: JSON.stringify({ typing }),
    });
  }

  markHandoverRead(
    session: { sessionId: string; sessionToken: string },
    messageId?: string,
  ): Promise<{ ok: true }> {
    return this.request(`/v1/chat/${session.sessionId}/live/read`, {
      method: 'POST',
      headers: { authorization: `Bearer ${session.sessionToken}` },
      body: JSON.stringify({ messageId }),
    });
  }

  async uploadHandoverAttachment(
    session: { sessionId: string; sessionToken: string },
    file: File,
    caption?: string,
  ): Promise<{ ok: true; messageId: string }> {
    const form = new FormData();
    form.append('file', file);
    if (caption) form.append('caption', caption);
    const response = await fetch(
      `${this.baseUrl}/v1/chat/${session.sessionId}/handover-attachments`,
      {
        method: 'POST',
        headers: { authorization: `Bearer ${session.sessionToken}` },
        body: form,
      },
    );
    if (!response.ok) await this.fail(response);
    return (await response.json()) as { ok: true; messageId: string };
  }

  /** Public "meet the agents" roster (no auth). */
  agents(): Promise<{ agents: AgentSummary[] }> {
    return this.request('/v1/registry/agents');
  }

  /** Public per-agent conversational chat. */
  agentChat(
    slug: string,
    input: {
      messages: { role: 'user' | 'assistant'; content: string }[];
      lang: Lang;
      customer?: { reference?: string; phone?: string };
    },
  ): Promise<{ reply: string; model: string; grounded: boolean; customer: CustomerAppContext[] }> {
    return this.request(`/v1/agents/${slug}/chat`, { method: 'POST', body: JSON.stringify(input) });
  }

  /**
   * ElevenLabs TTS of an agent reply, using the same two fixed voices as
   * WhatsApp (male/female by the agent's gender). Returns the mp3 as a Blob, or
   * null when TTS is unconfigured (HTTP 204) so callers can fall back to the
   * browser Web Speech API.
   */
  async agentSpeak(slug: string, input: { text: string; lang: Lang }): Promise<Blob | null> {
    const res = await fetch(`${this.baseUrl}/v1/agents/${slug}/speak`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (res.status === 204) return null;
    if (!res.ok) await this.fail(res);
    const blob = await res.blob();
    return blob.size > 0 ? blob : null;
  }

  /** Privacy-scoped lookup of the citizen's own application(s). */
  customerLookup(input: { reference?: string; phone?: string; lang?: Lang }): Promise<{ applications: CustomerAppContext[] }> {
    return this.request('/v1/agents/lookup', { method: 'POST', body: JSON.stringify(input) });
  }

  // --- admin (staff token) ---------------------------------------------------

  admin(staffToken: string) {
    const headers: Record<string, string> = staffToken.startsWith('Bearer ')
      ? { authorization: staffToken }
      : { 'x-staff-token': staffToken };
    const complaintsPath =
      '/v1/admin/agents/complaints-manager/complaints';
    const mysteryShopRunsPath =
      '/v1/admin/agents/mystery-shopper/browser-runs';
    return {
      me: () => this.request<{ id: string; displayName: string; roles: string[] }>('/v1/admin/me', { headers }),
      queues: (params?: { stage?: string; status?: string }) => {
        const qs = new URLSearchParams(Object.entries(params ?? {}).filter(([, v]) => v) as [string, string][]);
        return this.request<{ items: Record<string, unknown>[] }>(`/v1/admin/queues?${qs}`, { headers });
      },
      caseFile: (reference: string) =>
        this.request<Record<string, unknown>>(`/v1/admin/cases/${encodeURIComponent(reference)}`, { headers }),
      sectionState: (assignmentId: string, sectionId: string, status: string, comment?: string) =>
        this.request(`/v1/admin/assignments/${assignmentId}/sections/${sectionId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ status, comment }),
        }),
      correct: (reference: string, fieldId: string, value: unknown, reason: string) =>
        this.request(`/v1/admin/cases/${encodeURIComponent(reference)}/correct`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ fieldId, value, reason }),
        }),
      returnToCitizen: (reference: string, assignmentId: string, comment: Localized) =>
        this.request(`/v1/admin/cases/${encodeURIComponent(reference)}/return`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ assignmentId, comment }),
        }),
      recommend: (reference: string, assignmentId: string, outcome: string, reasons: Localized[]) =>
        this.request<{ approvalId: string }>(`/v1/admin/cases/${encodeURIComponent(reference)}/recommend`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ assignmentId, outcome, reasons }),
        }),
      approvals: () => this.request<{ items: Record<string, unknown>[] }>('/v1/admin/approvals', { headers }),
      decideApproval: (id: string, decision: 'approve' | 'reject', note?: string) =>
        this.request(`/v1/admin/approvals/${id}/decide`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ decision, note }),
        }),
      // --- Housing Management Console ------------------------------------
      //
      // The pre-approval review surface. Deliberately untyped in their return
      // shapes beyond the fields the console reads: the dossier is wide and
      // still moving, and a hand-maintained mirror of it here would drift from
      // the server without anything catching it. The console reads it through
      // its own view models instead.
      housingConsoleOverview: () =>
        this.request<HousingConsoleOverview>('/v1/admin/housing/console/overview', { headers }),
      housingConsoleQueue: (filters: {
        stage?: string;
        status?: string;
        band?: string;
        service?: string;
        limit?: number;
      } = {}) => {
        const search = new URLSearchParams();
        for (const [key, value] of Object.entries(filters)) {
          if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
        }
        const suffix = search.size > 0 ? `?${search.toString()}` : '';
        return this.request<{ cases: HousingConsoleQueueRow[] }>(
          `/v1/admin/housing/console/queue${suffix}`,
          { headers },
        );
      },
      housingConsoleCase: (reference: string) =>
        this.request<HousingConsoleCaseDetail>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}`,
          { headers },
        ),
      housingConsoleCaseRules: (reference: string) =>
        this.request<{ evaluation: unknown; uncoveredTerms: Array<{ termId: string; en: string }> }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/rules`,
          { headers },
        ),
      completeHousingConsoleStage: (
        reference: string,
        stage: string,
        notes?: { en: string; ar: string } | null,
      ) =>
        this.request<{ ok: true; caseId: string; nextStage: string | null }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/stages/${encodeURIComponent(stage)}/complete`,
          { method: 'POST', headers, body: JSON.stringify({ notes: notes ?? null }) },
        ),
      returnHousingConsoleStage: (reference: string, toStage: string, reason: string) =>
        this.request<{ ok: true; caseId: string }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/stages/return`,
          { method: 'POST', headers, body: JSON.stringify({ toStage, reason }) },
        ),
      runHousingConsoleIntegrations: (reference: string) =>
        this.request<{
          attempted: number;
          answered: number;
          factsWritten: number;
          runs: Array<{ sourceId: string; status: string; latencyMs: number; error?: string }>;
          unanswered: string[];
        }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/integrations/run`,
          { method: 'POST', headers },
        ),
      // 503 is a real answer here, not a failure: it means no model is
      // configured, and the console says so rather than showing an empty
      // findings list that reads as a clean case.
      analyseHousingConsoleCase: (reference: string) =>
        this.request<{
          headline: { en: string; ar: string | null };
          findingsRecorded: number;
          missingFacts: string[];
          model: string;
        }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/ai/analyse`,
          { method: 'POST', headers },
          [503],
        ),
      readHousingConsoleConflicts: (reference: string) =>
        this.request<{ conflicts: unknown[]; model: string | null }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/ai/conflicts`,
          { method: 'POST', headers },
        ),
      draftHousingConsoleMemo: (reference: string, kind: string, officerNotes?: string) =>
        this.request<{
          subject: { en: string; ar: string | null };
          body: { en: string; ar: string | null };
          citations: string[];
          draftedBy: 'model' | 'deterministic';
          model: string;
        }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/ai/memo`,
          { method: 'POST', headers, body: JSON.stringify({ kind, officerNotes }) },
        ),
      decideHousingConsoleFinding: (
        findingId: string,
        decision: 'accept' | 'dismiss',
        note?: string,
      ) =>
        this.request<{ ok: true }>(
          `/v1/admin/housing/console/findings/${encodeURIComponent(findingId)}/${decision}`,
          { method: 'POST', headers, body: JSON.stringify({ note }) },
        ),
      issueHousingConsoleDecision: (
        reference: string,
        input: {
          outcome: 'approved' | 'rejected' | 'returned_for_information';
          assistanceType?: string | null;
          amountAed?: number | null;
          purpose?: string | null;
          reasons: Array<{ en: string; ar: string | null; articleClause?: string }>;
        },
      ) =>
        this.request<{ ok: true; caseId: string; decisionId: string }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/decision`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      waiveHousingConsoleRule: (
        reference: string,
        input: {
          ruleKeys: string[];
          title: { en: string; ar: string | null };
          justification: string;
          articleClause?: string | null;
          expiresAt: string;
        },
      ) =>
        this.request<{ ok: true; caseId: string; exceptionId: string }>(
          `/v1/admin/housing/console/cases/${encodeURIComponent(reference)}/waivers`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      housingDesignServices: () =>
        this.request<HousingDesignServices>('/v1/admin/housing/console/design/services', { headers }),
      housingDesignRulesets: () =>
        this.request<HousingDesignRulesets>('/v1/admin/housing/console/design/rulesets', { headers }),
      housingDesignMatrix: () =>
        this.request<HousingDesignMatrix>('/v1/admin/housing/console/design/matrix', { headers }),
      housingDesignForm: (sid: string) =>
        this.request<unknown>(
          `/v1/admin/housing/console/design/forms/${encodeURIComponent(sid)}`,
          { headers },
          [404],
        ),
      housingDesignIntegrations: (emirate?: string) =>
        this.request<HousingDesignIntegrations>(
          `/v1/admin/housing/console/design/integrations${emirate ? `?emirate=${encodeURIComponent(emirate)}` : ''}`,
          { headers },
        ),
      replayHousingRuleset: (slug: string, draft: unknown, references: string[]) =>
        this.request<{
          replayed: number;
          changed: number;
          results: Array<{
            reference: string;
            verdictChanged: boolean;
            before: string;
            after: string;
            nowFailing: string[];
            nowPassing: string[];
            nowIndeterminate: string[];
          }>;
        }>(`/v1/admin/housing/console/design/rulesets/${encodeURIComponent(slug)}/replay`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ draft, references }),
        }),
      housingBundles: () =>
        this.request<{ bundles: HousingBundleSummary[] }>('/v1/admin/housing/bundles', { headers }),
      housingBundle: (bundleId: string) =>
        this.request<HousingBundleDetail>(
          `/v1/admin/housing/bundles/${encodeURIComponent(bundleId)}`,
          { headers },
        ),
      simulateHousingDecision: (
        reference: string,
        outcome: 'approved' | 'rejected',
        reasons?: Localized[],
      ) =>
        this.request<{ ok: true; outcome: string; simulated: true; bundleId: string | null }>(
          `/v1/admin/housing/applications/${encodeURIComponent(reference)}/simulate-decision`,
          { method: 'POST', headers, body: JSON.stringify({ outcome, reasons }) },
        ),
      completeHousingPipelineStage: (
        reference: string,
        stageId: 'application_assessment' | 'review_research',
        notes?: Localized,
        research?: { callCompleted?: boolean; visitCompleted?: boolean; socialStudyNotes?: Localized },
      ) =>
        this.request<{ ok: true; housingCase: ApplicationProjection['housingCase'] }>(
          `/v1/admin/housing/applications/${encodeURIComponent(reference)}/pipeline/complete-stage`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ stageId, notes, research }),
          },
        ),
      recordHousingCommitteeDecision: (
        reference: string,
        body: {
          outcome: 'approved' | 'rejected';
          assistanceType?: 'grant' | 'loan' | null;
          amountAed?: number | null;
          reasons?: Localized[];
        },
      ) =>
        this.request<{ ok: true; housingCase: ApplicationProjection['housingCase'] }>(
          `/v1/admin/housing/applications/${encodeURIComponent(reference)}/pipeline/committee`,
          { method: 'POST', headers, body: JSON.stringify(body) },
        ),
      issueHousingPipelineDecision: (reference: string) =>
        this.request<{
          ok: true;
          bundleId: string | null;
          outcome: string | null;
          housingCase: ApplicationProjection['housingCase'];
        }>(
          `/v1/admin/housing/applications/${encodeURIComponent(reference)}/pipeline/issue`,
          { method: 'POST', headers },
        ),
      decideHousingConsultantGate: (
        bundleId: string,
        outcome: 'approved' | 'changes_requested',
        comment: Localized,
      ) =>
        this.request<{ ok: true; outcome: string }>(
          `/v1/admin/housing/bundles/${encodeURIComponent(bundleId)}/consultant-decision`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ outcome, comment, simulated: true }),
          },
        ),
      decideHousingDocumentGate: (
        bundleId: string,
        outcome: 'approved' | 'changes_requested',
        comment: Localized,
      ) =>
        this.request<{ ok: true; outcome: string }>(
          `/v1/admin/housing/bundles/${encodeURIComponent(bundleId)}/document-decision`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ outcome, comment, simulated: true }),
          },
        ),
      decideHousingStepEvidence: (
        bundleId: string,
        phase: HousingSkippablePhase,
        outcome: 'verified' | 'changes_requested',
        comment: Localized,
      ) =>
        this.request<{ ok: true; outcome: string }>(
          `/v1/admin/housing/bundles/${encodeURIComponent(bundleId)}/step-evidence/${encodeURIComponent(phase)}/decision`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ outcome, comment }),
          },
        ),
      housingSettings: () =>
        this.request<HousingSettingsState>('/v1/admin/housing/settings', { headers }),
      updateHousingSettings: (patch: Partial<HousingSettings>) =>
        this.request<HousingSettingsState>('/v1/admin/housing/settings', {
          method: 'PUT',
          headers,
          body: JSON.stringify(patch),
        }),
      housingOps: () =>
        this.request<HousingOpsOverview>('/v1/admin/housing/ops', { headers }),
      housingBundleTrace: (bundleId: string) =>
        this.request<HousingBundleTrace>(
          `/v1/admin/housing/bundles/${encodeURIComponent(bundleId)}/trace`,
          { headers },
        ),
      fieldResearchQueue: (kind: HousingVisitKind | 'all' = 'all') =>
        this.request<{ queue: FieldResearchQueueItem[]; kpis: FieldResearchKpis }>(
          `/v1/admin/housing/field-research?kind=${encodeURIComponent(kind)}`,
          { headers },
        ),
      fieldResearchCase: (reference: string) =>
        this.request<FieldResearchCaseHub>(
          `/v1/admin/housing/field-research/cases/${encodeURIComponent(reference)}`,
          { headers },
        ),
      patchFieldResearchVisit: (
        reference: string,
        kind: HousingVisitKind,
        body: {
          status?: HousingVisitStatus;
          assignedTo?: string;
          scheduledAt?: string | null;
          summary?: Localized;
          waiveReason?: Localized;
          checklist?: Record<string, unknown>;
        },
      ) =>
        this.request<{ housingCase: FieldResearchCaseHub['housingCase'] }>(
          `/v1/admin/housing/field-research/cases/${encodeURIComponent(reference)}/visits/${encodeURIComponent(kind)}`,
          { method: 'PATCH', headers, body: JSON.stringify(body) },
        ),
      fieldResearchInvestigationPack: async (reference: string) => {
        const response = await fetch(
          `${this.baseUrl}/v1/admin/housing/field-research/cases/${encodeURIComponent(reference)}/investigation-pack`,
          { headers },
        );
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      housingQararPdf: async (reference: string) => {
        const response = await fetch(
          `${this.baseUrl}/v1/admin/housing/applications/${encodeURIComponent(reference)}/qarar`,
          { headers },
        );
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      /** Draft the bilingual visit summary. The officer must edit before saving. */
      draftFieldVisitSummary: (
        reference: string,
        kind: HousingVisitKind,
        body: { checklist?: Record<string, unknown> } = {},
      ) =>
        this.request<HousingVisitSummaryDraft>(
          `/v1/admin/housing/field-research/cases/${encodeURIComponent(reference)}/visits/${encodeURIComponent(kind)}/summary-draft`,
          { method: 'POST', headers, body: JSON.stringify(body) },
        ),
      financingContracts: (status: 'pending_decision' | 'all' = 'pending_decision') =>
        this.request<{ contracts: HousingFinancingContractDeskRow[] }>(
          `/v1/admin/housing/financing-contracts?status=${encodeURIComponent(status)}`,
          { headers },
        ),
      decideFinancingContract: (
        contractId: string,
        body: { outcome: 'approved' | 'rejected'; reasons?: Localized[] },
      ) =>
        this.request<{ financingContract: HousingFinancingContractView }>(
          `/v1/admin/housing/financing-contracts/${encodeURIComponent(contractId)}/decide`,
          { method: 'POST', headers, body: JSON.stringify(body) },
        ),
      submitFinancingContract: (contractId: string) =>
        this.request<{ financingContract: HousingFinancingContractView }>(
          `/v1/admin/housing/financing-contracts/${encodeURIComponent(contractId)}/submit`,
          { method: 'POST', headers },
        ),
      services: () => this.request<{ services: Record<string, unknown>[] }>('/v1/admin/registry/services', { headers }),
      serviceDetail: (slug: string) =>
        this.request<Record<string, unknown>>(`/v1/admin/registry/services/${slug}`, { headers }),
      serviceGraph: (slug: string, source: 'draft' | 'live' = 'draft') =>
        this.request<ServiceGraphProjection>(
          `/v1/admin/registry/services/${encodeURIComponent(slug)}/graph?source=${source}`,
          { headers },
        ),
      serviceDiff: (slug: string) =>
        this.request<{ slug: string; base: string; compare: string; entries: SpecDiffEntry[] }>(
          `/v1/admin/registry/services/${encodeURIComponent(slug)}/diff`,
          { headers },
        ),
      serviceVersionDiff: (slug: string, version: number) =>
        this.request<{ slug: string; baseVersion: number | null; compareVersion: number; entries: SpecDiffEntry[] }>(
          `/v1/admin/registry/services/${encodeURIComponent(slug)}/versions/${version}/diff`,
          { headers },
        ),
      serviceIntegrationHealth: (slug: string) =>
        this.request<ServiceIntegrationHealth>(
          `/v1/admin/registry/services/${encodeURIComponent(slug)}/integration-health`,
          { headers },
        ),
      draftServiceFromSource: (slug: string, url: string, lang: Lang) =>
        this.request<{
          spec: unknown;
          source: string;
          model: string | null;
          confidence: 'high' | 'review_required';
          notes: string[];
        }>(`/v1/admin/registry/services/${encodeURIComponent(slug)}/draft-from-source`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ url, lang }),
        }),
      saveDraft: (slug: string, spec: unknown) =>
        this.request<{ ok: boolean; issues: { path: string; message: string }[] }>(
          `/v1/admin/registry/services/${slug}/draft`,
          { method: 'PUT', headers, body: JSON.stringify({ spec }) },
        ),
      validateSpec: (slug: string, spec: unknown) =>
        this.request<{ ok: boolean; issues: { path: string; message: string }[] }>(
          `/v1/admin/registry/services/${slug}/validate`,
          { method: 'POST', headers, body: JSON.stringify({ spec }) },
        ),
      submitForPublish: (slug: string, changelog: string) =>
        this.request<{ approvalId: string }>(`/v1/admin/registry/services/${slug}/submit`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ changelog }),
        }),
      requestServiceLifecycle: (slug: string, action: 'pause' | 'resume' | 'retire', reason: string) =>
        this.request<{ approvalId: string }>(
          `/v1/admin/registry/services/${encodeURIComponent(slug)}/lifecycle`,
          { method: 'POST', headers, body: JSON.stringify({ action, reason }) },
        ),

      // --- service bundles ---------------------------------------------------

      bundles: () => this.request<{ bundles: BundleSummary[] }>('/v1/admin/registry/bundles', { headers }),
      bundle: (slug: string) =>
        this.request<AdminBundleDetail>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}`,
          { headers },
        ),
      saveBundleDraft: (slug: string, spec: BundleSpec) =>
        this.request<{ ok: boolean; issues: { path: string; message: string }[] }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/draft`,
          { method: 'PUT', headers, body: JSON.stringify({ spec }) },
        ),
      setBundleOrder: (slug: string, sortOrder: number) =>
        this.request<{ ok: boolean }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/order`,
          { method: 'POST', headers, body: JSON.stringify({ sortOrder }) },
        ),
      submitBundleForPublish: (slug: string, changelog: string) =>
        this.request<{ approvalId: string }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/submit`,
          { method: 'POST', headers, body: JSON.stringify({ changelog }) },
        ),
      retireBundle: (slug: string) =>
        this.request<{ ok: boolean }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/retire`,
          { method: 'POST', headers },
        ),
      /** A link the preview iframe can load; the staff token cannot ride in an iframe src. */
      bundlePreviewToken: (slug: string) =>
        this.request<{ token: string; expiresAt: string }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/preview-token`,
          { method: 'POST', headers },
        ),
      uploadBundleMedia: async (
        slug: string,
        file: File,
        options: { kind: string; alt: Localized; width?: number; height?: number },
      ) => {
        const form = new FormData();
        form.append('file', file);
        form.append('kind', options.kind);
        form.append('altEn', options.alt.en);
        form.append('altAr', options.alt.ar);
        if (options.width) form.append('width', String(options.width));
        if (options.height) form.append('height', String(options.height));
        // Deliberately not this.request: setting content-type by hand strips the
        // multipart boundary the browser generates, and the server sees no file.
        const response = await fetch(
          `${this.baseUrl}/v1/admin/registry/bundles/${encodeURIComponent(slug)}/media`,
          { method: 'POST', headers, body: form },
        );
        if (!response.ok) await this.fail(response);
        return (await response.json()) as BundleMedia;
      },
      deleteBundleMedia: (slug: string, mediaId: string) =>
        this.request<{ ok: boolean }>(
          `/v1/admin/registry/bundles/${encodeURIComponent(slug)}/media/${encodeURIComponent(mediaId)}`,
          { method: 'DELETE', headers },
        ),
      dashboard: () => this.request<Record<string, unknown>>('/v1/admin/dashboards/summary', { headers }),
      metricsJson: () => this.request<MetricsExport>('/v1/admin/metrics/export', { headers }),
      metricsCsv: async () => {
        const response = await fetch(`${this.baseUrl}/v1/admin/metrics/export?format=csv`, { headers });
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      executive: (path: string, query?: Record<string, string | number | boolean | undefined>) => {
        const params = new URLSearchParams();
        if (query) {
          for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== '') params.set(k, String(v));
          }
        }
        const qs = params.toString();
        return this.request<Record<string, unknown>>(
          `/v1/admin/executive/${path.replace(/^\//, '')}${qs ? `?${qs}` : ''}`,
          { headers },
        );
      },
      executiveAsk: (question: string) =>
        this.request<Record<string, unknown>>('/v1/admin/executive/ask', {
          method: 'POST',
          headers,
          body: JSON.stringify({ question }),
        }),
      executiveExplain: (body: Record<string, unknown>) =>
        this.request<{ insight: Record<string, unknown> }>('/v1/admin/executive/explain', {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        }),
      executiveRecords: (query?: Record<string, string | number | boolean | undefined>) => {
        const params = new URLSearchParams();
        if (query) {
          for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== '') params.set(k, String(v));
          }
        }
        const qs = params.toString();
        return this.request<{
          items: Record<string, unknown>[];
          total: number;
          liveCount?: number;
          page: number;
          pageSize: number;
        }>(`/v1/admin/executive/records${qs ? `?${qs}` : ''}`, { headers });
      },
      executiveRecord: (id: string) =>
        this.request<Record<string, unknown>>(`/v1/admin/executive/records/${encodeURIComponent(id)}`, { headers }),
      executiveTrend: (metric: string) =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/trend?metric=${encodeURIComponent(metric)}`,
          { headers },
        ),
      executiveFormula: (metric: string) =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/formula?metric=${encodeURIComponent(metric)}`,
          { headers },
        ),
      executiveCrmSplit: (path: string, dimension = 'auto') =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/crm/split?path=${encodeURIComponent(path)}&dimension=${encodeURIComponent(dimension)}`,
          { headers },
        ),
      executiveBreakdown: (path: string) =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/breakdown?path=${encodeURIComponent(path)}`,
          { headers },
        ),
      executiveHierarchy: (path: string, dims: string) =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/breakdown/hierarchy?path=${encodeURIComponent(path)}&dims=${encodeURIComponent(dims)}`,
          { headers },
        ),
      executiveSearch: (q: string) =>
        this.request<{
          query: string;
          totalHits: number;
          groups: {
            type: string;
            label: Record<string, string>;
            hits: Record<string, unknown>[];
          }[];
        }>(`/v1/admin/executive/search?q=${encodeURIComponent(q)}`, { headers }),
      executiveRevealAudit: (body: { scope?: string; filters?: Record<string, unknown> }) =>
        this.request<{ ok: boolean; audited: boolean }>('/v1/admin/executive/records/reveal-audit', {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        }),
      executiveRecordsCsv: async (query?: Record<string, string | number | boolean | undefined>) => {
        const params = new URLSearchParams({ format: 'csv' });
        if (query) {
          for (const [k, v] of Object.entries(query)) {
            if (v !== undefined && v !== '') params.set(k, String(v));
          }
        }
        const response = await fetch(`${this.baseUrl}/v1/admin/executive/records?${params.toString()}`, { headers });
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      executiveCitizen: (hash: string) =>
        this.request<Record<string, unknown>>(
          `/v1/admin/executive/citizen/${encodeURIComponent(hash)}`,
          { headers },
        ),
      executiveExport: async (page: string) => {
        const response = await fetch(
          `${this.baseUrl}/v1/admin/executive/export?page=${encodeURIComponent(page)}&format=csv`,
          { headers },
        );
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      executiveAgent: () =>
        this.request<{
          identity: Record<string, unknown>;
          stats: Record<string, number>;
          watch: Record<string, unknown>[];
          flags: Record<string, unknown>[];
        }>('/v1/admin/executive/agent', { headers }),
      executiveDimensions: () =>
        this.request<Record<string, { id: string; name: Record<string, string>; count?: number; sector?: string; slug?: string }[]>>(
          '/v1/admin/executive/dimensions',
          { headers },
        ),
      executivePrefs: () =>
        this.request<{
          prefs: Record<string, unknown>;
          savedViews: { id: string; name: string; params: Record<string, string> }[];
          persisted: boolean;
        }>('/v1/admin/executive/prefs', { headers }),
      executiveSavePrefs: (prefs: Record<string, unknown>) =>
        this.request<{ ok: boolean; prefs: Record<string, unknown>; persisted: boolean }>(
          '/v1/admin/executive/prefs',
          { method: 'PUT', headers, body: JSON.stringify({ prefs }) },
        ),
      executiveSaveView: (view: { name: string; params: Record<string, string> }) =>
        this.request<{
          ok: boolean;
          id: string | null;
          views: { id: string; name: string; params: Record<string, string> }[];
          persisted: boolean;
        }>('/v1/admin/executive/saved-views', {
          method: 'POST',
          headers,
          body: JSON.stringify(view),
        }),
      executiveDeleteView: (id: string) =>
        this.request<{
          ok: boolean;
          views: { id: string; name: string; params: Record<string, string> }[];
          persisted: boolean;
        }>(`/v1/admin/executive/saved-views/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers,
        }),
      platformReadiness: () =>
        this.request<PlatformReadiness>('/v1/admin/platform/readiness', { headers }),
      journeys: (service?: string) =>
        this.request<JourneyCatalogueResponse>(
          `/v1/admin/journeys${service ? `?service=${encodeURIComponent(service)}` : ''}`,
          { headers },
        ),
      /**
       * A refusal comes back as 409 with a report, not as a thrown error: "this
       * would file a real application" is an answer the console shows, not a
       * failure it hides.
       */
      runJourney: (
        journeyId: string,
        input: { transports?: string[]; lang?: 'en' | 'ar'; acknowledgeWrites?: boolean } = {},
      ) =>
        this.request<JourneyRunResponse>(
          `/v1/admin/journeys/${encodeURIComponent(journeyId)}/run`,
          { method: 'POST', headers, body: JSON.stringify(input) },
          [409],
        ),
      /**
       * The developer documentation portal.
       *
       * Every response has already been through `redactForPortal` on the
       * server, so these types describe the published surface rather than the
       * inventory behind it — there is no field here that names how a call
       * reaches the ministry, and adding one would fail the coverage gate.
       */
      devPortalSurface: () =>
        this.request<DeveloperSurfaceResponse>('/v1/admin/devportal/surface', { headers }),
      devPortalService: (slug: string) =>
        this.request<DevPortalServiceResponse>(
          `/v1/admin/devportal/services/${encodeURIComponent(slug)}`,
          { headers },
        ),
      devPortalCapability: (id: string) =>
        this.request<DevPortalCapabilityResponse>(
          `/v1/admin/devportal/capabilities/${encodeURIComponent(id)}`,
          { headers },
        ),
      devPortalOperation: (id: string) =>
        this.request<DevPortalOperationResponse>(
          `/v1/admin/devportal/operations/${encodeURIComponent(id)}`,
          { headers },
        ),
      devPortalMcp: (role?: string) =>
        this.request<DevPortalMcpResponse>(
          `/v1/admin/devportal/mcp${role ? `?role=${encodeURIComponent(role)}` : ''}`,
          { headers },
        ),
      /**
       * A refusal is an answer, not an error: "writes are switched off here" and
       * "type the capability id to confirm" are both things the portal shows.
       * 502 carries the traced failure in the published vocabulary.
       */
      devPortalTryCapability: (input: {
        capability: string;
        input?: Record<string, unknown>;
        state?: string;
        confirm?: string;
      }) =>
        this.request<DevPortalTraceResponse>(
          '/v1/admin/devportal/try/capability',
          { method: 'POST', headers, body: JSON.stringify(input) },
          [400, 403, 404, 409, 502],
        ),
      auditVerify: () => this.request<{ ok: boolean; rows: number }>('/v1/admin/audit/verify', { headers }),
      auditRecent: () => this.request<{ items: Record<string, unknown>[] }>('/v1/admin/audit/recent', { headers }),
      auditAccess: (q?: string) =>
        this.request<{ items: Record<string, unknown>[] }>(
          `/v1/admin/audit/access${q ? `?q=${encodeURIComponent(q)}` : ''}`,
          { headers },
        ),
      traceSessions: (params?: {
        channel?: string;
        actorKind?: string;
        actor?: string;
        service?: string;
        since?: string;
        failed?: boolean;
        limit?: number;
        offset?: number;
      }) => {
        const qs = new URLSearchParams(
          Object.entries(params ?? {})
            .filter(([, value]) => value !== undefined && value !== '' && value !== false)
            .map(([key, value]) => [key, String(value)]),
        );
        return this.request<{
          items: TraceSessionSummary[];
          limit: number;
          offset: number;
        }>(`/v1/admin/traces/sessions?${qs}`, { headers });
      },
      traceSession: (id: string) =>
        this.request<{ session: TraceSessionSummary; turns: TraceTurnSummary[] }>(
          `/v1/admin/traces/sessions/${encodeURIComponent(id)}`,
          { headers },
        ),
      /**
       * One turn in full.
       *
       * `reveal` decrypts the prompts, payloads and citizen text, and is
       * recorded in `access_log` on the way through — so it is a separate
       * argument rather than the default.
       */
      traceTurn: (id: string, reveal = false) =>
        this.request<TraceTurnDetail>(
          `/v1/admin/traces/turns/${encodeURIComponent(id)}${reveal ? '?reveal=1' : ''}`,
          { headers },
        ),
      traceRequests: (params: { session?: string; trace?: string; reveal?: boolean }) => {
        const qs = new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== false)
            .map(([key, value]) => [key, String(value)]),
        );
        return this.request<{ items: Record<string, unknown>[] }>(
          `/v1/admin/traces/requests?${qs}`,
          { headers },
        );
      },
      /**
       * The export as NDJSON text.
       *
       * Not routed through `request`, which parses JSON — a newline-delimited
       * stream is not one document, and buffering it into one would defeat the
       * reason it streams.
       */
      traceExport: async (params: { session?: string; since?: string; reveal?: boolean }) => {
        const qs = new URLSearchParams(
          Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== false)
            .map(([key, value]) => [key, String(value)]),
        );
        const res = await fetch(`${this.baseUrl}/v1/admin/traces/export?${qs}`, { headers });
        if (!res.ok) await this.fail(res);
        return res.text();
      },
      govSyncOutbox: (state?: string) =>
        this.request<{ items: Record<string, unknown>[] }>(
          `/v1/admin/govsync/outbox${state ? `?state=${encodeURIComponent(state)}` : ''}`,
          { headers },
        ),
      govSyncDeadLetters: () =>
        this.request<{ items: Record<string, unknown>[] }>('/v1/admin/govsync/deadletters', { headers }),
      replayGovSync: (id: string) =>
        this.request(`/v1/admin/govsync/outbox/${encodeURIComponent(id)}/replay`, { method: 'POST', headers }),
      completeGovSync: (id: string, govReference: string) =>
        this.request(`/v1/admin/govsync/outbox/${encodeURIComponent(id)}/complete`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ govReference }),
        }),
      documents: (status?: string) =>
        this.request<{ items: Record<string, unknown>[] }>(
          `/v1/admin/documents${status ? `?status=${encodeURIComponent(status)}` : ''}`,
          { headers },
        ),
      verifyDocument: (id: string, decision: 'verified' | 'rejected', reason: string) =>
        this.request(`/v1/admin/documents/${encodeURIComponent(id)}/verify`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ decision, reason }),
        }),
      payments: (status?: string) =>
        this.request<{ items: Record<string, unknown>[] }>(
          `/v1/admin/payments${status ? `?status=${encodeURIComponent(status)}` : ''}`,
          { headers },
        ),
      requestRefund: (id: string, reason: string) =>
        this.request<{ approvalId: string }>(`/v1/admin/payments/${encodeURIComponent(id)}/refund`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ reason }),
        }),
      notificationTemplates: () =>
        this.request<{ items: Record<string, unknown>[] }>('/v1/admin/notification-templates', { headers }),
      requestTemplateChange: (id: string, input: { status?: string; body?: Localized; note: string }) =>
        this.request<{ approvalId: string }>(
          `/v1/admin/notification-templates/${encodeURIComponent(id)}/change`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      integrationsCatalog: () =>
        this.request<{
          adapters: Record<string, unknown>[];
          services: Record<string, unknown>[];
        }>('/v1/admin/integrations/catalog', { headers }),
      platformUsers: () =>
        this.request<{ items: Record<string, unknown>[] }>('/v1/admin/platform/users', { headers }),
      platformFeatures: () =>
        this.request<{ featureFlags: Record<string, boolean>; branding: Record<string, unknown> }>('/v1/admin/platform/features', { headers }),
      platformSettings: () =>
        this.request<Record<string, unknown>>('/v1/admin/platform/settings', { headers }),
      savePlatformSettings: (input: Record<string, unknown>) =>
        this.request<Record<string, unknown>>('/v1/admin/platform/settings', {
          method: 'PUT',
          headers,
          body: JSON.stringify(input),
        }),
      embedKeys: () =>
        this.request<{ keys: EmbedKeySummary[] }>('/v1/admin/platform/embed-keys', { headers }),
      /**
       * Mint a website embed key.
       *
       * The `key` and `handoffSecret` in the response exist nowhere else — there
       * is no endpoint that returns them again — so a caller that does not show
       * them to the operator has lost them.
       */
      mintEmbedKey: (input: {
        label: string;
        origins?: string[];
        allowedServices?: string[];
        withHandoffSecret?: boolean;
      }) =>
        this.request<MintedEmbedKey>('/v1/admin/platform/embed-keys', {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      revokeEmbedKey: (id: string) =>
        this.request<{ revoked: boolean }>(
          `/v1/admin/platform/embed-keys/${encodeURIComponent(id)}`,
          { method: 'DELETE', headers },
        ),
      breakGlass: (input: { action: 'pause_service' | 'suppress_notification_queue'; reason: string; slug?: string }) =>
        this.request<{ approvalId: string; detail: Record<string, unknown> }>('/v1/admin/break-glass', {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      // Customer 360 (CRM)
      customers: (q?: string) => {
        const qs = q ? `?q=${encodeURIComponent(q)}` : '';
        return this.request<{
          items: CustomerDirectoryItem[];
          summary: CustomerCrmSummary;
        }>(`/v1/admin/customers${qs}`, { headers });
      },
      customer: (id: string) =>
        this.request<Customer360Record>(`/v1/admin/customers/${encodeURIComponent(id)}`, { headers }),
      createCustomerNote: (
        id: string,
        input: {
          body: string;
          kind?: CustomerCrmNote['kind'];
          tags?: string[];
          pinned?: boolean;
        },
      ) =>
        this.request<{ id: string }>(`/v1/admin/customers/${encodeURIComponent(id)}/notes`, {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      pinCustomerNote: (id: string, noteId: string, pinned: boolean) =>
        this.request<{ ok: true }>(
          `/v1/admin/customers/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}/pin`,
          { method: 'POST', headers, body: JSON.stringify({ pinned }) },
        ),
      createCustomerTask: (
        id: string,
        input: {
          title: string;
          description?: string;
          priority?: CustomerCrmTask['priority'];
          assignedTo?: string;
          dueAt?: string;
        },
      ) =>
        this.request<{ id: string }>(`/v1/admin/customers/${encodeURIComponent(id)}/tasks`, {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      updateCustomerTaskStatus: (
        id: string,
        taskId: string,
        status: CustomerCrmTask['status'],
      ) =>
        this.request<{ ok: true }>(
          `/v1/admin/customers/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/status`,
          { method: 'POST', headers, body: JSON.stringify({ status }) },
        ),
      updateCustomerCrm: (id: string, input: Partial<CustomerCrmProfile>) =>
        this.request<{ ok: true }>(`/v1/admin/customers/${encodeURIComponent(id)}/crm`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(input),
        }),
      // Proactive renewals
      proactiveDue: (withinDays = 45) =>
        this.request<{ due: Record<string, unknown>[] }>(`/v1/admin/proactive/due?withinDays=${withinDays}`, { headers }),
      proactiveScan: (withinDays = 45) =>
        this.request<{ offered: number; offers: Record<string, unknown>[] }>('/v1/admin/proactive/scan', {
          method: 'POST',
          headers,
          body: JSON.stringify({ withinDays }),
        }),
      // Ministry CRM (Dynamics 365 / Dataverse) — read-only; data routes need
      // the per-staff Microsoft SSO session (crm.sso_required → sign-in gate).
      crmStatus: () =>
        this.request<CrmStatus>('/v1/integrations/dataverse/status', { headers }),
      crmSsoStart: () =>
        this.request<{ authorizeUrl: string }>('/v1/integrations/dataverse/sso/start', {
          method: 'POST',
          headers,
        }),
      crmSsoDisconnect: () =>
        this.request<{ ok: true }>('/v1/integrations/dataverse/sso/disconnect', {
          method: 'POST',
          headers,
        }),
      crmSharedStart: () =>
        this.request<{ authorizeUrl: string }>('/v1/integrations/dataverse/shared/start', {
          method: 'POST',
          headers,
        }),
      crmSharedDisconnect: () =>
        this.request<{ ok: true }>('/v1/integrations/dataverse/shared/disconnect', {
          method: 'POST',
          headers,
        }),
      crmEntities: () =>
        this.request<{ entities: CrmEntitySummary[] }>('/v1/integrations/dataverse/entities', {
          headers,
        }),
      crmStats: () => this.request<CrmStats>('/v1/integrations/dataverse/stats', { headers }),
      crmRecords: (
        entity: string,
        opts: { search?: string; top?: number; activeOnly?: boolean } = {},
      ) => {
        const params = new URLSearchParams({ entity });
        if (opts.search) params.set('search', opts.search);
        if (opts.top) params.set('top', String(opts.top));
        if (opts.activeOnly) params.set('active', 'true');
        return this.request<{
          ok: boolean;
          entity: string;
          set: string;
          count: number;
          records: CrmRecordDto[];
        }>(`/v1/integrations/dataverse/records?${params.toString()}`, { headers });
      },
      crmRecord: (set: string, id: string) =>
        this.request<{ ok: boolean; set: string; record: Record<string, unknown> }>(
          `/v1/integrations/dataverse/records/${encodeURIComponent(set)}/${encodeURIComponent(id)}`,
          { headers },
        ),
      crmRecordComments: (set: string, id: string) =>
        this.request<{ ok: boolean; set: string; id: string; comments: CrmRecordComment[] }>(
          `/v1/integrations/dataverse/records/${encodeURIComponent(set)}/${encodeURIComponent(id)}/comments`,
          { headers },
        ),
      crmRecordContext: (set: string, id: string) =>
        this.request<CrmCaseContext>(
          `/v1/integrations/dataverse/records/${encodeURIComponent(set)}/${encodeURIComponent(id)}/context`,
          { headers },
        ),
      crmLinks: () =>
        this.request<{ links: CrmLink[] }>('/v1/integrations/dataverse/links', { headers }),
      crmSaveLink: (input: {
        serviceSlug: string;
        crmEntity: string;
        odataFilter?: string | null;
        labelEn?: string | null;
        labelAr?: string | null;
      }) =>
        this.request<{ ok: true; id: string }>('/v1/integrations/dataverse/links', {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      crmDeleteLink: (id: string) =>
        this.request<{ ok: true }>(
          `/v1/integrations/dataverse/links/${encodeURIComponent(id)}`,
          { method: 'DELETE', headers },
        ),
      crmCustomer: (customerId: string) =>
        this.request<CrmCustomerPanel>(
          `/v1/integrations/dataverse/customers/${encodeURIComponent(customerId)}`,
          { headers },
        ),
      crmLinkCustomer: (customerId: string, contactId: string) =>
        this.request<{ ok: true; contactId: string; fullname: string | null }>(
          `/v1/integrations/dataverse/customers/${encodeURIComponent(customerId)}/link`,
          { method: 'POST', headers, body: JSON.stringify({ contactId }) },
        ),
      crmUnlinkCustomer: (customerId: string) =>
        this.request<{ ok: true }>(
          `/v1/integrations/dataverse/customers/${encodeURIComponent(customerId)}/link`,
          { method: 'DELETE', headers },
        ),
      crmMirrorStatus: () =>
        this.request<CrmMirrorStatus>('/v1/integrations/dataverse/mirror/status', { headers }),
      crmMirrorSync: () =>
        this.request<{ ok: true; mirrored: number }>('/v1/integrations/dataverse/mirror/sync', {
          method: 'POST',
          headers,
        }),
      crmMirrorTranslate: () =>
        this.request<{ ok: true; translatedNow: number } & CrmTranslationStats>(
          '/v1/integrations/dataverse/mirror/translate',
          { method: 'POST', headers },
        ),
      crmMirrorCases: (opts: { q?: string; entity?: string; top?: number } = {}) => {
        const params = new URLSearchParams();
        if (opts.q) params.set('q', opts.q);
        if (opts.entity) params.set('entity', opts.entity);
        if (opts.top) params.set('top', String(opts.top));
        const suffix = params.toString() ? `?${params.toString()}` : '';
        return this.request<{ ok: boolean; cases: CrmSimilarCase[] }>(
          `/v1/integrations/dataverse/mirror/cases${suffix}`,
          { headers },
        );
      },
      /** Suggestions for an identified case are stored server-side and returned
       *  with `cached: true` on later visits; pass `refresh` to regenerate. */
      crmSuggestResolution: (
        text: string,
        entity?: string,
        excludeRecordId?: string,
        context?: { caseType?: string | null; categories?: string[] },
        refresh?: boolean,
      ) =>
        this.request<{
          ok: boolean;
          cached?: boolean;
          generatedAt?: string;
          suggestion: CrmResolutionSuggestion;
          similar: CrmSimilarCase[];
        }>('/v1/integrations/dataverse/case-intel/suggest', {
          method: 'POST',
          headers,
          body: JSON.stringify({
            text,
            ...(entity ? { entity } : {}),
            ...(excludeRecordId ? { excludeRecordId } : {}),
            ...(context ? { context } : {}),
            ...(refresh ? { refresh } : {}),
          }),
        }),
      cxoneSettings: () =>
        this.request<CxonePublicSettings>('/v1/admin/integrations/cxone', { headers }),
      saveCxoneSettings: (input: Record<string, unknown>) =>
        this.request<{ settings: CxonePublicSettings; generatedByocSecret?: string }>(
          '/v1/admin/integrations/cxone',
          { method: 'PUT', headers, body: JSON.stringify(input) },
        ),
      checkCxoneHealth: () =>
        this.request<CxoneHealth>('/v1/admin/integrations/cxone/health', {
          method: 'POST',
          headers,
        }),
      connectCxone: () =>
        this.request<{
          health: CxoneHealth;
          settings: CxonePublicSettings;
          requiresByocAdminSetup?: boolean;
        }>('/v1/admin/integrations/cxone/connect', { method: 'POST', headers }),
      cxoneAgentConfig: () =>
        this.request<CxoneAgentConfig>('/v1/admin/integrations/cxone/agent-config', {
          headers,
        }),
      cxoneSkills: () =>
        this.request<{
          skills: Array<{ id: string; name: string; mediaType?: string; isActive: boolean }>;
          routingQueues: Array<{ id: string; name: string; skillId: string }>;
        }>('/v1/admin/integrations/cxone/skills', { headers }),
      cxoneTelemetry: () =>
        this.request<{
          queues: Array<{
            skillId: string;
            skillName?: string;
            contactsQueued: number;
            longestWaitSeconds?: number;
            agentsAvailable?: number;
          }>;
          unavailable?: boolean;
          error?: string;
        }>('/v1/admin/integrations/cxone/telemetry', { headers }),
      cxoneLinkAgent: (agentId: string) =>
        this.request<{ linked: true; agent: Record<string, unknown> }>(
          '/v1/admin/integrations/cxone/agent-link',
          { method: 'POST', headers, body: JSON.stringify({ agentId }) },
        ),
      cxoneStartAgentSession: (sdkSessionId?: string) =>
        this.request<CxoneAgentSessionLease>(
          '/v1/admin/integrations/cxone/agent-session',
          { method: 'POST', headers, body: JSON.stringify({ sdkSessionId }) },
        ),
      cxoneHeartbeatAgentSession: (
        sessionId: string,
        leaseToken: string,
        degraded = false,
      ) =>
        this.request<{ ok: true; expiresAt: string }>(
          `/v1/admin/integrations/cxone/agent-session/${encodeURIComponent(sessionId)}/heartbeat`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ leaseToken, degraded }),
          },
        ),
      cxoneEndAgentSession: (sessionId: string, leaseToken: string) =>
        this.request<{ ok: true }>(
          `/v1/admin/integrations/cxone/agent-session/${encodeURIComponent(sessionId)}`,
          {
            method: 'DELETE',
            headers,
            body: JSON.stringify({ leaseToken }),
          },
        ),
      cxoneAgentEvent: (event: CxoneAgentEventInput, leaseToken: string) =>
        this.request<{ applied: boolean; conversationId?: string }>(
          '/v1/admin/integrations/cxone/agent-events',
          {
            method: 'POST',
            headers: { ...headers, 'x-cxone-session-lease': leaseToken },
            body: JSON.stringify(event),
          },
        ),
      cxonePointsOfContact: () =>
        this.request<{ items: Record<string, unknown>[] }>(
          '/v1/admin/integrations/cxone/points-of-contact',
          { headers },
        ),
      cxoneDeadLetters: () =>
        this.request<{ items: Record<string, unknown>[] }>(
          '/v1/admin/integrations/cxone/deadletters',
          { headers },
        ),
      cxoneRequeueDeadLetter: (id: string) =>
        this.request<{ ok: true }>(
          `/v1/admin/integrations/cxone/deadletters/${encodeURIComponent(id)}/requeue`,
          { method: 'POST', headers },
        ),
      handoverEvents: (
        signal: AbortSignal,
        onEvent: (event: HandoverEvent) => void,
        conversationId?: string,
        after?: string,
      ) => {
        const path = conversationId
          ? `/v1/admin/handover/${encodeURIComponent(conversationId)}/events`
          : '/v1/admin/handover/events';
        const suffix = after ? `?after=${encodeURIComponent(after)}` : '';
        return this.streamJsonEvents<HandoverEvent>(
          `${path}${suffix}`,
          headers,
          signal,
          ({ data }) => onEvent(data),
        );
      },
      handoverPresence: (status: 'available' | 'busy' | 'away' | 'offline', maxActiveChats?: number) =>
        this.request('/v1/admin/handover/presence', {
          method: 'POST',
          headers,
          body: JSON.stringify({ status, maxActiveChats }),
        }),
      handoverList: () =>
        this.request<{ provider: HandoverProvider; items: Record<string, unknown>[] }>(
          '/v1/admin/handover',
          { headers },
        ),
      /**
       * Draft replies for the agent, delivered one at a time.
       *
       * Two are generated concurrently and `onSuggestion` fires for each as it
       * lands, so the faster one is on screen while the other is still being
       * written. Resolves once both have been attempted; an empty stream means
       * the model was unavailable or both drafts failed vetting, which is a
       * normal outcome and not an error.
       */
      handoverSuggestions: (
        conversationId: string,
        signal: AbortSignal,
        onSuggestion: (suggestion: HandoverSuggestion) => void,
        lastMessageId?: string | null,
      ) => {
        const suffix = lastMessageId
          ? `?lastMessageId=${encodeURIComponent(lastMessageId)}`
          : '';
        return this.streamJsonEvents<HandoverSuggestion>(
          `/v1/admin/handover/${encodeURIComponent(conversationId)}/suggestions${suffix}`,
          headers,
          signal,
          ({ event, data }) => {
            if (event === 'suggestion') onSuggestion(data);
          },
        );
      },
      handoverApplication: (conversationId: string) =>
        this.request<{ application: HandoverApplicationView | null }>(
          `/v1/admin/handover/${encodeURIComponent(conversationId)}/application`,
          { headers },
        ),
      /** Answers one field for the citizen; returns the whole sidebar again,
       *  because a conditional requirement may have opened or closed. */
      handoverFillField: (conversationId: string, fieldId: string, value: unknown) =>
        this.request<{ application: HandoverApplicationView }>(
          `/v1/admin/handover/${encodeURIComponent(conversationId)}/application/fields/${encodeURIComponent(fieldId)}`,
          { method: 'PATCH', headers, body: JSON.stringify({ value }) },
        ),
      handoverDetail: (conversationId: string) =>
        this.request<Record<string, unknown>>(`/v1/admin/handover/${conversationId}`, { headers }),
      handoverClaim: (conversationId: string) =>
        this.request(`/v1/admin/handover/${conversationId}/claim`, { method: 'POST', headers }),
      handoverRelease: (conversationId: string) =>
        this.request(`/v1/admin/handover/${conversationId}/release`, { method: 'POST', headers }),
      handoverTransfer: (conversationId: string, input: { targetStaffId?: string; skillId?: string }) =>
        this.request(`/v1/admin/handover/${conversationId}/transfer`, {
          method: 'POST',
          headers,
          body: JSON.stringify(input),
        }),
      handoverTyping: (conversationId: string, typing: boolean) =>
        this.request(`/v1/admin/handover/${conversationId}/typing`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ typing }),
        }),
      handoverRead: (conversationId: string, messageId?: string) =>
        this.request(`/v1/admin/handover/${conversationId}/read`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ messageId }),
        }),
      handoverReply: (conversationId: string, text: string) =>
        this.request(`/v1/admin/handover/${conversationId}/reply`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ text }),
        }),
      handoverAttachment: async (conversationId: string, file: File, caption?: string) => {
        const form = new FormData();
        form.append('file', file);
        if (caption) form.append('caption', caption);
        const response = await fetch(
          `${this.baseUrl}/v1/admin/handover/${encodeURIComponent(conversationId)}/attachments`,
          { method: 'POST', headers, body: form },
        );
        if (!response.ok) await this.fail(response);
        return (await response.json()) as { ok: true; messageId: string };
      },
      handoverReturnToAi: (conversationId: string, disposition = 'resolved') =>
        this.request(`/v1/admin/handover/${conversationId}/return-to-ai`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ disposition }),
        }),
      conversationTranscript: (conversationId: string) =>
        this.request<Record<string, unknown>>(`/v1/admin/conversations/${conversationId}/transcript`, { headers }),
      // Agents ops
      complaints: (filters: ComplaintListFilters = {}) => {
        const search = new URLSearchParams();
        if (filters.status) search.set('status', filters.status);
        if (filters.severity) search.set('severity', filters.severity);
        if (filters.category) search.set('category', filters.category);
        if (filters.kind) search.set('kind', filters.kind);
        if (filters.source) search.set('source', filters.source);
        if (filters.assignedTo) search.set('assignedTo', filters.assignedTo);
        if (filters.sla) search.set('sla', filters.sla);
        if (filters.limit !== undefined) search.set('limit', String(filters.limit));
        if (filters.offset !== undefined) search.set('offset', String(filters.offset));
        const suffix = search.size > 0 ? `?${search.toString()}` : '';
        return this.request<ComplaintListResponse>(`${complaintsPath}${suffix}`, { headers });
      },
      complaint: (complaintId: string) =>
        this.request<ComplaintDetailResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}`,
          { headers },
        ),
      assignComplaint: (
        complaintId: string,
        assigneeId: string | null,
        expectedRevision: number,
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/assign`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ assigneeId, expectedRevision }),
          },
        ),
      updateComplaintResponse: (
        complaintId: string,
        response: Localized,
        expectedRevision: number,
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/response`,
          {
            method: 'PUT',
            headers,
            body: JSON.stringify({ response, expectedRevision }),
          },
        ),
      approveComplaintResponse: (
        complaintId: string,
        expectedRevision: number,
        response?: Localized,
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/approve-response`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedRevision, response }),
          },
        ),
      sendComplaintResponse: (complaintId: string, expectedRevision: number) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/send`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedRevision }),
          },
        ),
      escalateComplaint: (
        complaintId: string,
        expectedRevision: number,
        reason = 'staff_request',
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/escalate`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedRevision, reason }),
          },
        ),
      closeComplaint: (
        complaintId: string,
        expectedRevision: number,
        reason = 'staff_request',
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/close`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedRevision, reason }),
          },
        ),
      reopenComplaint: (
        complaintId: string,
        expectedRevision: number,
        reason = 'staff_request',
      ) =>
        this.request<ComplaintMutationResponse>(
          `${complaintsPath}/${encodeURIComponent(complaintId)}/reopen`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedRevision, reason }),
          },
        ),
      enqueueMysteryShopRuns: (input: { targetId?: string; scenarioIds?: string[] } = {}) =>
        this.request<{ jobs: { job: MysteryShopJob; created: boolean }[] }>(
          mysteryShopRunsPath,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      mysteryShopRuns: (filters?: {
        status?: MysteryShopJobStatus;
        targetId?: string;
        before?: string;
        limit?: number;
      }) => {
        const search = new URLSearchParams();
        if (filters?.status) search.set('status', filters.status);
        if (filters?.targetId) search.set('targetId', filters.targetId);
        if (filters?.before) search.set('before', filters.before);
        if (filters?.limit !== undefined) search.set('limit', String(filters.limit));
        const suffix = search.size > 0 ? `?${search.toString()}` : '';
        return this.request<{ jobs: MysteryShopJob[] }>(
          `${mysteryShopRunsPath}${suffix}`,
          { headers },
        );
      },
      mysteryShopRun: (jobId: string) =>
        this.request<MysteryShopDetail>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}`,
          { headers },
        ),
      mysteryShopRunEvents: (
        jobId: string,
        signal: AbortSignal,
        onEvent: (event: MysteryShopStreamEvent) => void,
        after?: string,
      ) => {
        const suffix = after ? `?after=${encodeURIComponent(after)}` : '';
        return this.streamJsonEvents<MysteryShopEvent | MysteryShopRunStatusEvent>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/events${suffix}`,
          headers,
          signal,
          ({ id, event, data }) => onEvent({
            id,
            event: event as MysteryShopStreamEvent['event'],
            data,
          }),
        );
      },
      pauseMysteryShopRun: (jobId: string, expectedUpdatedAt?: string) =>
        this.request<{ job: MysteryShopJob }>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/pause`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedUpdatedAt }),
          },
        ),
      resumeMysteryShopRun: (jobId: string, expectedUpdatedAt?: string) =>
        this.request<{ job: MysteryShopJob }>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/resume`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedUpdatedAt }),
          },
        ),
      cancelMysteryShopRun: (jobId: string, expectedUpdatedAt?: string) =>
        this.request<{ job: MysteryShopJob }>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/cancel`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ expectedUpdatedAt }),
          },
        ),
      mysteryShopLiveView: (jobId: string) =>
        this.request<MysteryShopLiveView>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/live-view?interactive=true`,
          { headers },
        ),
      mysteryShopReplay: (jobId: string) =>
        this.request<MysteryShopReplay>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/replay`,
          { headers },
        ),
      mysteryShopReplayPlaylist: async (jobId: string, pageId: string) => {
        const response = await fetch(
          `${this.baseUrl}${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/replay/${encodeURIComponent(pageId)}.m3u8`,
          { headers },
        );
        if (!response.ok) await this.fail(response);
        return response.text();
      },
      mysteryShopSessionLogs: (jobId: string) =>
        this.request<{ logs: MysteryShopSessionLog[] }>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/logs`,
          { headers },
        ),
      emailMysteryShopReport: (jobId: string, lang: 'en' | 'ar') =>
        this.request<{ sent: MysteryShopEmailResult }>(
          `${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/email-report`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({ lang }),
          },
        ),
      mysteryShopArtifact: async (jobId: string, artifactId: string) => {
        const response = await fetch(
          `${this.baseUrl}${mysteryShopRunsPath}/${encodeURIComponent(jobId)}/artifacts/${encodeURIComponent(artifactId)}`,
          { headers },
        );
        if (!response.ok) await this.fail(response);
        return response.blob();
      },
      agents: () => this.request<{ agents: AgentSummary[] }>('/v1/admin/agents', { headers }),
      agent: (slug: string) =>
        this.request<{
          agent: AgentSummary;
          runs: Record<string, unknown>[];
          report: { summary?: Localized; metric?: AgentMetric; data?: Record<string, unknown> } | null;
          complaints?: Record<string, unknown>[];
        }>(`/v1/admin/agents/${slug}`, { headers }),
      agentWorkspace: (slug: string) =>
        this.request<AgentWorkspace>(`/v1/admin/agents/${encodeURIComponent(slug)}/workspace`, { headers }),
      agentConfigVersions: (slug: string) =>
        this.request<{ items: AgentConfigVersion[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/versions`,
          { headers },
        ),
      createAgentDraft: (slug: string, sourceVersionId?: string) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts`,
          { method: 'POST', headers, body: JSON.stringify({ sourceVersionId }) },
        ),
      updateAgentDraft: (
        slug: string,
        versionId: string,
        input: {
          config: AgentConfigSnapshot;
          draftContext?: AgentDraftContext;
          expectedLockVersion: number;
          changeNote?: string;
        },
      ) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}`,
          { method: 'PUT', headers, body: JSON.stringify(input) },
        ),
      deleteAgentDraft: (slug: string, versionId: string) =>
        this.request<{ ok: true }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}`,
          { method: 'DELETE', headers },
        ),
      validateAgentConfig: (slug: string, config: AgentConfigSnapshot) =>
        this.request<{ valid: boolean; issues: { path: string; message: string }[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/validate`,
          { method: 'POST', headers, body: JSON.stringify({ config }) },
        ),
      simulateAgentConfig: (slug: string, config: AgentConfigSnapshot, lang: Lang) =>
        this.request<{
          valid: true;
          promptPreview: string;
          schedulePreviews: { id?: string; name: string; occurrences: string[] }[];
          integrationEffects: {
            adapterKey: string;
            enabled: boolean;
            capabilities: string[];
            secretRefPresent: boolean;
          }[];
          authorityBoundary: string;
        }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/simulate`,
          { method: 'POST', headers, body: JSON.stringify({ config, lang }) },
        ),
      previewAgentSchedule: (slug: string, cron: string, timezone: string, count = 5) =>
        this.request<{ occurrences: string[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/schedules/preview`,
          { method: 'POST', headers, body: JSON.stringify({ cron, timezone, count }) },
        ),
      submitAgentDraft: (slug: string, versionId: string) =>
        this.request<{ approvalId: string }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}/submit`,
          { method: 'POST', headers },
        ),
      rollbackAgentConfig: (slug: string, versionId: string) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/versions/${encodeURIComponent(versionId)}/rollback`,
          { method: 'POST', headers },
        ),
      agentConfigDiff: (slug: string, from: string, to: string) =>
        this.request<{ from: string; to: string; changes: { section: string; before: unknown; after: unknown }[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/diff?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
          { headers },
        ),
      agentNotes: (slug: string, archived = false) =>
        this.request<{ items: AgentNote[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/notes?archived=${String(archived)}`,
          { headers },
        ),
      createAgentNote: (slug: string, input: AgentNoteInput) =>
        this.request<{ note: AgentNote }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/notes`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      updateAgentNote: (
        slug: string,
        noteId: string,
        input: AgentNoteInput & { expectedRevision: number },
      ) =>
        this.request<{ note: AgentNote }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/notes/${encodeURIComponent(noteId)}`,
          { method: 'PUT', headers, body: JSON.stringify(input) },
        ),
      archiveAgentNote: (slug: string, noteId: string) =>
        this.request<{ ok: true }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/notes/${encodeURIComponent(noteId)}`,
          { method: 'DELETE', headers },
        ),
      agentReportingCatalog: (slug: string) =>
        this.request<{
          sources: {
            key: AgentReportDataBinding['source'];
            label: string;
            description: string;
            fields: string[];
          }[];
          aggregations: AgentReportDataBinding['aggregation'][];
          formats: string[];
        }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/reporting/catalog`,
          { headers },
        ),
      assuranceCommandCenter: (slug: string, lang: Lang) =>
        this.request<{ commandCenter: AssuranceCommandCenter }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/command-center?lang=${encodeURIComponent(lang)}`,
          { headers },
        ),
      agentAudit: (
        slug: string,
        params?: {
          limit?: number;
          beforeId?: number;
          category?: 'all' | 'write' | 'read' | 'chat' | 'run' | 'other';
          actionPrefix?: string;
          actorKind?: string;
          from?: string;
          to?: string;
        },
      ) => {
        const search = new URLSearchParams();
        if (params?.limit != null) search.set('limit', String(params.limit));
        if (params?.beforeId != null) search.set('beforeId', String(params.beforeId));
        if (params?.category) search.set('category', params.category);
        if (params?.actionPrefix) search.set('actionPrefix', params.actionPrefix);
        if (params?.actorKind) search.set('actorKind', params.actorKind);
        if (params?.from) search.set('from', params.from);
        if (params?.to) search.set('to', params.to);
        const query = search.size > 0 ? `?${search.toString()}` : '';
        return this.request<{
          items: {
            id: number;
            ts: string;
            actor: Record<string, unknown>;
            action: string;
            subject: Record<string, unknown>;
            detail: Record<string, unknown>;
            this_hash: string;
            category: 'write' | 'read' | 'chat' | 'run' | 'other';
          }[];
          nextBeforeId: number | null;
        }>(`/v1/admin/agents/${encodeURIComponent(slug)}/audit${query}`, { headers });
      },
      previewAgentReport: (
        slug: string,
        input: {
          definition: AgentReportDefinition;
          dataMode: 'sample' | 'live_safe';
          lang: Lang;
        },
      ) =>
        this.request<{ preview: AgentReportPreview }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/reports/preview`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      createAgentReportTestIntent: (
        slug: string,
        input: {
          definition: AgentReportDefinition;
          dataMode: 'sample' | 'live_safe';
          lang: Lang;
          recipient: string;
        },
      ) =>
        this.request<{ intent: AgentReportTestIntent }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/reports/test-send/intents`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      confirmAgentReportTestSend: (slug: string, token: string) =>
        this.request<{ sent: AgentReportTestSendResult }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/reports/test-send/confirm`,
          { method: 'POST', headers, body: JSON.stringify({ token }) },
        ),
      agentResourceDrafts: (
        slug: string,
        filters?: {
          archived?: boolean;
          kind?: AgentResourceDraft['kind'];
        },
      ) => {
        const search = new URLSearchParams();
        if (filters?.archived) search.set('archived', 'true');
        if (filters?.kind) search.set('kind', filters.kind);
        const query = search.size > 0 ? `?${search.toString()}` : '';
        return this.request<{ items: AgentResourceDraft[] }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts${query}`,
          { headers },
        );
      },
      agentResourceDraft: (slug: string, resourceDraftId: string) =>
        this.request<{ resourceDraft: AgentResourceDraft }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts/${encodeURIComponent(resourceDraftId)}`,
          { headers },
        ),
      createAgentResourceDraft: (slug: string, input: AgentResourceDraftInput) =>
        this.request<{ resourceDraft: AgentResourceDraft }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      updateAgentResourceDraft: (
        slug: string,
        resourceDraftId: string,
        input: AgentResourceDraftUpdateInput,
      ) =>
        this.request<{ resourceDraft: AgentResourceDraft }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts/${encodeURIComponent(resourceDraftId)}`,
          { method: 'PUT', headers, body: JSON.stringify(input) },
        ),
      archiveAgentResourceDraft: (
        slug: string,
        resourceDraftId: string,
        expectedRevision: number,
      ) =>
        this.request<{ resourceDraft: AgentResourceDraft }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts/${encodeURIComponent(resourceDraftId)}?revision=${expectedRevision}`,
          { method: 'DELETE', headers },
        ),
      verifyAgentResourceDraft: (
        slug: string,
        resourceDraftId: string,
        input: { expectedRevision: number; notes?: string },
      ) =>
        this.request<{ resourceDraft: AgentResourceDraft }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts/${encodeURIComponent(resourceDraftId)}/verify`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      promoteAgentResourceDraft: (
        slug: string,
        resourceDraftId: string,
        input: AgentResourceDraftPromoteInput,
      ) =>
        this.request<{ resourceDraft: AgentResourceDraft; version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/resource-drafts/${encodeURIComponent(resourceDraftId)}/promote`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      agentIntegrationCatalog: (slug: string) =>
        this.request<{
          items: {
            key: string;
            label: string;
            description: string;
            risk: 'low' | 'standard' | 'elevated' | 'critical';
            capabilities: string[];
            allowedAgentSlugs: string[] | '*';
            allowedConfigKeys: string[];
            secretRefOptional: boolean;
          }[];
        }>(`/v1/admin/agents/${encodeURIComponent(slug)}/integrations/catalog`, { headers }),
      testAgentIntegration: (slug: string, versionId: string, bindingId: string) =>
        this.request<{
          status: 'healthy' | 'degraded' | 'failed';
          checkedAt: string;
          detail: Record<string, unknown>;
        }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/versions/${encodeURIComponent(versionId)}/integrations/${encodeURIComponent(bindingId)}/test`,
          { method: 'POST', headers },
        ),
      createAgentResource: (
        slug: string,
        versionId: string,
        kind: 'integrations' | 'schedules',
        value: AgentIntegrationBindingConfig | AgentScheduleConfig,
        expectedLockVersion: number,
      ) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}/${kind}`,
          { method: 'POST', headers, body: JSON.stringify({ value, expectedLockVersion }) },
        ),
      updateAgentResource: (
        slug: string,
        versionId: string,
        kind: 'integrations' | 'schedules',
        resourceId: string,
        value: AgentIntegrationBindingConfig | AgentScheduleConfig,
        expectedLockVersion: number,
      ) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}/${kind}/${encodeURIComponent(resourceId)}`,
          { method: 'PUT', headers, body: JSON.stringify({ value, expectedLockVersion }) },
        ),
      deleteAgentResource: (
        slug: string,
        versionId: string,
        kind: 'integrations' | 'schedules',
        resourceId: string,
        expectedLockVersion: number,
      ) =>
        this.request<{ version: AgentConfigVersion }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config/drafts/${encodeURIComponent(versionId)}/${kind}/${encodeURIComponent(resourceId)}?lockVersion=${expectedLockVersion}`,
          { method: 'DELETE', headers },
        ),
      createAgentAssistantSession: (
        slug: string,
        targetOrDraftVersionId: string | AgentAssistantTarget | AgentAssistantSessionInput,
        scope?: AgentAssistantScope,
      ) =>
        this.request<{ session: AgentAssistantSession }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config-assistant/sessions`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(
              typeof targetOrDraftVersionId === 'string'
                ? { draftVersionId: targetOrDraftVersionId, ...(scope ? { scope } : {}) }
                : 'target' in targetOrDraftVersionId
                  ? {
                      target: targetOrDraftVersionId.target,
                      scope: targetOrDraftVersionId.scope ?? { paths: [], noteRefs: [] },
                    }
                  : {
                      target: targetOrDraftVersionId,
                      scope: scope ?? { paths: [], noteRefs: [] },
                    },
            ),
          },
        ),
      agentAssistantSession: (slug: string, sessionId: string) =>
        this.request<AgentAssistantSessionDetail>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config-assistant/sessions/${encodeURIComponent(sessionId)}`,
          { headers },
        ),
      proposeAgentConfiguration: (
        slug: string,
        sessionId: string,
        input: { message: string; noteIds: string[]; lang: Lang },
      ) =>
        this.request<{ proposal: AgentAssistantProposal }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config-assistant/sessions/${encodeURIComponent(sessionId)}/messages`,
          { method: 'POST', headers, body: JSON.stringify(input) },
        ),
      applyAgentConfigurationProposal: (
        slug: string,
        sessionId: string,
        proposalId: string,
        expected:
          | number
          | { expectedLockVersion: number; expectedRevision?: never }
          | { expectedRevision: number; expectedLockVersion?: never },
      ) =>
        this.request<
          | { version: AgentConfigVersion }
          | { resourceDraft: AgentResourceDraft }
        >(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config-assistant/sessions/${encodeURIComponent(sessionId)}/proposals/${encodeURIComponent(proposalId)}/apply`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify(
              typeof expected === 'number' ? { expectedLockVersion: expected } : expected,
            ),
          },
        ),
      rejectAgentConfigurationProposal: (slug: string, sessionId: string, proposalId: string) =>
        this.request<{ ok: true }>(
          `/v1/admin/agents/${encodeURIComponent(slug)}/config-assistant/sessions/${encodeURIComponent(sessionId)}/proposals/${encodeURIComponent(proposalId)}/reject`,
          { method: 'POST', headers },
        ),
      runAgent: (slug: string, params?: Record<string, unknown>) =>
        this.request<{ runId: string; status: string; error?: string }>(`/v1/admin/agents/${slug}/run`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ params: params ?? {} }),
        }),
    };
  }
}

// --- SSE reader --------------------------------------------------------------

async function* readSse(res: Response): AsyncGenerator<{ id?: string; event: string; data: string }> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const chunk = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = 'message';
      let id: string | undefined;
      const dataLines: string[] = [];
      for (const line of chunk.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('id:')) id = line.slice(3).trim();
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length > 0) yield { id, event, data: dataLines.join('\n') };
    }
  }
}

/**
 * The shared application-status store.
 *
 * Kept in its own module so it can be read and tested without the seven
 * thousand lines of HTTP surface above it, and re-exported here because the
 * package publishes one entry point.
 */
export {
  createApplicationStatusStore,
  FILING_POLL,
  type ApplicationStatusSnapshot,
  type ApplicationStatusStore,
  type PollPolicy,
} from './applicationStatus.js';
